import { RecentSearchClient } from '@zillow/recent-search-fetcher';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { Component, useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import intersection from 'lodash/intersection';
import styled, { css, createGlobalStyle } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { IconButton, BUTTON_SIZES, IconLocationArrowOutline, IconLocationOutline, IconLocationArrow, IconLocation, Adornment, token, IconButtonMixin, Combobox, Form, ListboxOptionLabel, Gleam, ComboboxOption, CALLBACK_FREQUENCY, VisuallyHidden, INPUT_SIZES, IconFavoriteHome, IconSavedSearchOutline, IconSavedSearch, IconClockOutline, IconClock, IconSearchOutline, AdornedInput, Input, Highlighter, IconSearch, IconCloseCircle, ComboboxInput, ComboboxEmptyState, Paragraph, Tag, IconChevronLeft } from '@zillow/constellation';
import { yieldCallback, useYieldCallback } from '@zillow/yield-callback';
import findIndex from 'lodash/findIndex';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import AutocompleteManager, { FetchTypes } from '@zillow/autocomplete-manager';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/taggedTemplateLiteralLoose';
import gql from 'graphql-tag';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { isInitialized, event, datalayer, track } from '@zillow/universal-analytics';
import _assertThisInitialized from '@babel/runtime/helpers/assertThisInitialized';
import _inheritsLoose from '@babel/runtime/helpers/inheritsLoose';
import _typeof from '@babel/runtime/helpers/typeof';
import flow from 'lodash/flow';
import identity from 'lodash/identity';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';

/* Search/Autocomplete */
// min input length before autocomplete results are fetched
var MIN_AUTOCOMPLETE_CHARS = 3; // default number of results to show in autocomplete dropdown

var DEFAULT_MAX_SAVED_SEARCHES = 2;
var DEFAULT_MAX_CLAIMED_HOMES = 1;
var DEFAULT_MAX_RECENT_SEARCHES = 3;
var DEFAULT_MAX_REGION_ADDRESS = 10;
var DEFAULT_MAX_SAVED_HOMES = 1;
var DEFAULT_MAX_POST_TYPE_SEMANTIC = 10;
var DEFAULT_MAX_PRE_TYPE_SEMANTIC = 3;
/* FetchResults reasons */

var INIT_LOCAL_RESULTS = 'init-local-results';
var INPUT_FOCUSED = 'input-focused';
var FREEFORM = 'freeform';
var CLEAR_INPUT = 'clear input'; // current location radius to make map bounds from user position

var CURRENT_LOCATION_MAPBOUNDS_RADIUS = 0.005;
var SEARCH_BOX_HEIGHT = 42; // The minimum viewport width to display search box input
// when collapsed.

var DISPLAY_INPUT_MIN_WIDTH = 428; // Results container
var AUTOCOMPLETE_REQUEST_DELAY = 400;
var AUTOCOMPLETE_MIN_CHAR_LIMIT = 1;
var SCREEN_READER_MESSAGE_TIMEOUT = 500;
var MAX_REGION_SELECTION_LENGTH = 5;
/**
 * @deprecated use SuggestionCategory instead
 */

var RESULT_CATEGORIES = {
  CLAIMED_HOME: 'CLAIMED_HOME',
  CURRENT_LOCATION: 'CURRENT_LOCATION',
  SAVED_SEARCH: 'SAVED_SEARCH',
  SAVED_HOME: 'SAVED_HOME',
  RECENT_SEARCH: 'RECENT_SEARCH',
  REGION: 'REGION',
  SEMANTIC: 'SEMANTIC',
  ADDRESS: 'ADDRESS',
  RAW_SEARCH: 'RAW_SEARCH'
};
var PLACEHOLDER_TEXT_EMPTY = 'City, Neighborhood, ZIP, Address';
var PLACEHOLDER_TEXT_COLLAPSED = 'Add another location';
var PLACEHOLDER_TEXT_EXPANDED = 'City, Neighborhood, ZIP';

var SAVED_HOMES_URL = '/savedhomes/';

var encodeSeoURLString = function encodeSeoURLString(str) {
  return encodeURIComponent(String(str).replace(/-/g, '.dash.').replace(/[;\\/ ]/g, '-') // replace ';', '\', '/', ' ' with '-'
  .replace(/%/g, '')).replace(/%2C/g, ',').replace(/%23/g, '.num.');
};

var encodeSeoURLStringBldg = function encodeSeoURLStringBldg(str) {
  var addressBreakdown = str.split(/,/g);
  addressBreakdown = addressBreakdown.map(function (addr) {
    return addr.trim().replace(/[;\\/ ]/g, '-');
  });
  var encodedStr = addressBreakdown.join('-');
  return encodedStr;
};
/**
 * Converts an object into stringified query parameters key=value&keyB=valueB
 * @param {Object} queryParamObject
 * @returns {String}
 */


var paramsObjectToString = function paramsObjectToString(queryParamObject) {
  return Object.keys(queryParamObject).map(function (key) {
    return key + "=" + queryParamObject[key];
  }).join('&');
};

var getShouldUseBDPUrl = function getShouldUseBDPUrl(searchTermData) {
  var addressType = searchTermData === null || searchTermData === void 0 ? void 0 : searchTermData.addressType;
  var lat = searchTermData === null || searchTermData === void 0 ? void 0 : searchTermData.lat;
  var lng = searchTermData === null || searchTermData === void 0 ? void 0 : searchTermData.lng;
  return addressType && addressType === 'community_address' && lat && lng;
};

var getStaticMapBounds = function getStaticMapBounds(latitude, longitude) {
  return {
    north: latitude + CURRENT_LOCATION_MAPBOUNDS_RADIUS,
    south: latitude - CURRENT_LOCATION_MAPBOUNDS_RADIUS,
    east: longitude + CURRENT_LOCATION_MAPBOUNDS_RADIUS,
    west: longitude - CURRENT_LOCATION_MAPBOUNDS_RADIUS
  };
};
/**
 * Generates search URL based on
 * search input, location, search type, and query params.
 * @param {Object} searchTerm
 *   @param {String} searchTerm.category one of
 *     ADDRESS|CLAIMED_HOME|CURRENT_LOCATION|RAW_SEARCH|RECENT_SEARCH|REGION|SAVED_SEARCH
 *   @param {Object} searchTerm.coord
 *   @param {String} searchTerm.displayName
 *   @param {String} searchTerm.id
 *   @param {String} searchTerm.subCategory
 * @param {Object} location { latitude, longitude }
 * @param {String} searchType
 * @param {Object} extraQueryParams map of additional params
 */


var buildURL = function buildURL(searchTerm, location, searchType, extraQueryParams) {
  if (location === void 0) {
    location = {};
  }

  if (extraQueryParams === void 0) {
    extraQueryParams = {};
  }

  var category = searchTerm.category,
      displayName = searchTerm.displayName;
  var _location = location,
      latitude = _location.latitude,
      longitude = _location.longitude;
  var shouldUseBDPUrl = getShouldUseBDPUrl(searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.data);
  var queryParams = Object.assign({}, extraQueryParams);
  var generatedUrl = '/homes/';

  if (shouldUseBDPUrl) {
    generatedUrl = '/b/';
  }

  if (searchType) {
    generatedUrl += searchType + "/";
  }

  if (typeof displayName === 'string' && category !== 'CURRENT_LOCATION') {
    if (shouldUseBDPUrl) {
      var _searchTerm$data, _searchTerm$data2;

      generatedUrl += encodeSeoURLStringBldg(displayName) + "/";
      generatedUrl += (searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data = searchTerm.data) === null || _searchTerm$data === void 0 ? void 0 : _searchTerm$data.lat) + "," + (searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data2 = searchTerm.data) === null || _searchTerm$data2 === void 0 ? void 0 : _searchTerm$data2.lng) + "_ll/";
    } else {
      generatedUrl += encodeSeoURLString(displayName) + "_rb/";
    }
  }

  if (category === 'CURRENT_LOCATION') {
    if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined') {
      // adding CURRENT_LOCATION_MAPBOUNDS_RADIUS prevents sending the map bounds with no size
      queryParams.userPosition = longitude + "," + latitude;

      var _getStaticMapBounds = getStaticMapBounds(latitude, longitude),
          north = _getStaticMapBounds.north,
          south = _getStaticMapBounds.south,
          east = _getStaticMapBounds.east,
          west = _getStaticMapBounds.west;

      queryParams.userPositionBounds = north + "," + east + "," + south + "," + west;
    } else {
      return {
        error: 'No location provided for CURRENT_LOCATION search'
      };
    }

    queryParams.currentLocationSearch = true;
  }

  var queryParamsString = paramsObjectToString(queryParams);

  if (queryParamsString) {
    generatedUrl += "?" + queryParamsString;
  }

  return generatedUrl;
};

var buildURLForSemanticSearch = function buildURLForSemanticSearch(searchTerm, location) {
  var _filters$isForRent;

  var _searchTerm$displayNa = searchTerm.displayName,
      usersSearchTerm = _searchTerm$displayNa === void 0 ? '' : _searchTerm$displayNa,
      _searchTerm$regions = searchTerm.regions,
      regionSelection = _searchTerm$regions === void 0 ? [] : _searchTerm$regions,
      _searchTerm$filters = searchTerm.filters,
      filters = _searchTerm$filters === void 0 ? {} : _searchTerm$filters,
      _searchTerm$queryReso = searchTerm.queryResolutionStatus,
      queryResolutionStatus = _searchTerm$queryReso === void 0 ? '' : _searchTerm$queryReso;
  var filterState = Object.assign({}, filters, {
    sortSelection: {
      value: ((_filters$isForRent = filters.isForRent) === null || _filters$isForRent === void 0 ? void 0 : _filters$isForRent.value) ? 'priorityscore' : 'globalrelevanceex'
    }
  });
  var nlsQueryStatus = queryResolutionStatus !== 'FULL_RESOLUTION' ? queryResolutionStatus : null;
  var searchQueryState = {
    filterState: filterState,
    regionSelection: regionSelection,
    usersSearchTerm: usersSearchTerm
  };

  if (regionSelection.length === 0) {
    delete searchQueryState.regionSelection;
  }

  var url = "/homes/?searchQueryState=" + encodeURIComponent(JSON.stringify(searchQueryState));

  if (nlsQueryStatus) {
    url += "&nlsQueryStatus=" + nlsQueryStatus;
  }

  if ((location === null || location === void 0 ? void 0 : location.latitude) && (location === null || location === void 0 ? void 0 : location.longitude)) {
    var latitude = location.latitude,
        longitude = location.longitude;

    var _ref = location.mapBounds || getStaticMapBounds(latitude, longitude),
        north = _ref.north,
        south = _ref.south,
        east = _ref.east,
        west = _ref.west;

    url += "&userPosition=" + longitude + "," + latitude;
    url += "&userPositionBounds=" + north + "," + east + "," + south + "," + west;
    url += '&currentLocationSearch=true';
  }

  return url;
};

var recentSearchCategories = ['RAW_SEARCH', 'REGION', 'ADDRESS', 'RECENT_SEARCH'];
var recentSearchCategoriesForMultiRegion = ['REGION', 'ADDRESS', 'RECENT_SEARCH'];

var isSearchTermEligibleForRecentSearch = function isSearchTermEligibleForRecentSearch(searchTerm, isMultiRegionEnabled, isNaturalLanguageSearchEnabled) {
  return (isMultiRegionEnabled || isNaturalLanguageSearchEnabled ? recentSearchCategoriesForMultiRegion : recentSearchCategories).includes(searchTerm.category);
};
/**
 * URL redirect based on search term and/or location
 * @param {Object} searchTerm - submitted search result
 * @param {Object} location - { latitude, longitude, accuracy }
 * @param {String} searchType - search type
 * @param {Object} extraQueryParams - query params to append to url
 */


function SRPNavigationSubmitHandler(searchTerm, location, searchType, extraQueryParams, isMultiRegionEnabled, isNaturalLanguageSearchEnabled) {
  if (isMultiRegionEnabled === void 0) {
    isMultiRegionEnabled = false;
  }

  if (isNaturalLanguageSearchEnabled === void 0) {
    isNaturalLanguageSearchEnabled = false;
  }

  if (!searchTerm.category) {
    throw new Error('Search submit handler invoked without category property');
  }

  var newURL;

  switch (searchTerm.category) {
    case RESULT_CATEGORIES.CLAIMED_HOME:
    case RESULT_CATEGORIES.SAVED_SEARCH:
      newURL = searchTerm.url;
      break;

    case RESULT_CATEGORIES.SAVED_HOME:
      newURL = SAVED_HOMES_URL;
      break;

    case RESULT_CATEGORIES.SEMANTIC:
      newURL = buildURLForSemanticSearch(searchTerm, location);
      break;

    default:
      newURL = buildURL(searchTerm, location, searchType, extraQueryParams);
  }

  if (newURL && newURL.error) {
    console.error("Search handler failed. Reason: " + newURL.error);
  } else if (typeof newURL === 'string' && newURL.length === 0) {
    console.error('Search handler did not produce a URL');
  } else if (!window || !window.location) {
    console.error('Failed to navigate to new search page. window.location undefined');
  } else {
    if (isSearchTermEligibleForRecentSearch(searchTerm, isMultiRegionEnabled, isNaturalLanguageSearchEnabled)) {
      RecentSearchClient.addToRecentSearches(searchTerm);
    }

    window.location.href = newURL;
  }
}

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */

var getLabelSuggestionIndex = function getLabelSuggestionIndex(_ref) {
  var chosenSuggestion = _ref.chosenSuggestion,
      chosenIndex = _ref.chosenIndex;
  return chosenSuggestion.category + " | " + chosenIndex;
};

var getLabelSuggestionIndexValue = function getLabelSuggestionIndexValue(_ref2) {
  var _chosenSuggestion$dis;

  var chosenSuggestion = _ref2.chosenSuggestion,
      chosenIndex = _ref2.chosenIndex,
      searchBoxValue = _ref2.searchBoxValue;
  return chosenSuggestion.category + " | " + chosenIndex + " | " + (searchBoxValue !== null && searchBoxValue !== void 0 ? searchBoxValue : '') + " | " + ((_chosenSuggestion$dis = chosenSuggestion.displayName) !== null && _chosenSuggestion$dis !== void 0 ? _chosenSuggestion$dis : '');
};
/**
 * Category-specific tracking label generation functions.
 * getSearchBoxClickLabel takes a config object with the following four attributes:
 * @param {Object} chosenSuggestion - the suggestion object that was chosen by the user
 * @param {Array} allSuggestions - the array of suggestions that were being shown to the user at time of selection
 * @param {Number} chosenIndex - the 1-based index of chosenSuggestion in allSuggestions
 * @param {String} searchBoxValue - the text that was typed into the search box at the time an option was chosen
 */


var trackingConfigsByCategory = {
  SAVED_SEARCH: {
    getSearchBoxClickLabel: function getSearchBoxClickLabel(_ref3) {
      var _chosenSuggestion$not;

      var chosenSuggestion = _ref3.chosenSuggestion,
          chosenIndex = _ref3.chosenIndex;
      return chosenSuggestion.category + " | " + chosenIndex + " | " + ((_chosenSuggestion$not = chosenSuggestion.notificationCount) !== null && _chosenSuggestion$not !== void 0 ? _chosenSuggestion$not : '');
    }
  },
  RECENT_SEARCH: {
    getSearchBoxClickLabel: function getSearchBoxClickLabel(_ref4) {
      var chosenSuggestion = _ref4.chosenSuggestion,
          chosenIndex = _ref4.chosenIndex,
          _ref4$allSuggestions = _ref4.allSuggestions,
          allSuggestions = _ref4$allSuggestions === void 0 ? [] : _ref4$allSuggestions;

      var isSuggestionRecentSearch = function isSuggestionRecentSearch(_ref5) {
        var category = _ref5.category;
        return category === 'RECENT_SEARCH';
      };

      var totalRecentSearches = allSuggestions.filter(isSuggestionRecentSearch).length;
      var firstRecentSearchIndex = findIndex(allSuggestions, isSuggestionRecentSearch);
      var chosenRecentSearchIndex = chosenIndex - firstRecentSearchIndex;
      return chosenSuggestion.category + " | " + chosenIndex + " | " + chosenRecentSearchIndex + " | " + totalRecentSearches;
    }
  },
  CLAIMED_HOME: {
    getSearchBoxClickLabel: getLabelSuggestionIndex
  },
  SAVED_HOME: {
    getSearchBoxClickLabel: getLabelSuggestionIndex
  },
  CURRENT_LOCATION: {
    getSearchBoxClickLabel: getLabelSuggestionIndex
  },
  REGION: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  SEMANTIC: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  ADDRESS: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  RAW_SEARCH: {
    getSearchBoxClickLabel: function getSearchBoxClickLabel(_ref6) {
      var _chosenSuggestion$dis2;

      var chosenSuggestion = _ref6.chosenSuggestion,
          searchBoxValue = _ref6.searchBoxValue;
      return chosenSuggestion.category + " | " + (searchBoxValue !== null && searchBoxValue !== void 0 ? searchBoxValue : '') + " | " + ((_chosenSuggestion$dis2 = chosenSuggestion.displayName) !== null && _chosenSuggestion$dis2 !== void 0 ? _chosenSuggestion$dis2 : '');
    }
  }
};

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment SemanticResultFields on SearchAssistanceSemanticResult {\n        nearMe\n        regionIds\n        regionTypes\n        regionDisplayIds\n        queryResolutionStatus\n        viewLatitudeDelta\n        filters {\n            basementStatusType\n            baths {\n                min\n                max\n            }\n            beds {\n                min\n                max\n            }\n            excludeTypes\n            hoaFeesPerMonth {\n                min\n                max\n            }\n            homeType\n            keywords\n            listingStatusType\n            livingAreaSqft {\n                min\n                max\n            }\n            lotSizeSqft {\n                min\n                max\n            }\n            parkingSpots {\n                min\n                max\n            }\n            price {\n                min\n                max\n            }\n            searchRentalFilters {\n                monthlyPayment {\n                    min\n                    max\n                }\n                petsAllowed\n                rentalAvailabilityDate {\n                    min\n                    max\n                }\n            }\n            searchSaleFilters {\n                daysOnZillow {\n                    min\n                    max\n                }\n            }\n            showOnlyType\n            view\n            yearBuilt {\n                min\n                max\n            }\n        }\n    }\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment RentalCommunityResultFields on SearchAssistanceRentalCommunityResult {\n        location {\n            latitude\n            longitude\n        }\n    }\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject$3() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment RegionResultFields on SearchAssistanceRegionResult {\n        regionId\n        subType\n    }\n"]);

  _templateObject$3 = function _templateObject() {
    return data;
  };

  return data;
}
var REGION_RESULT_FIELDS = gql(_templateObject$3());
var RENTAL_COMMUNITY_RESULT_FIELDS = gql(_templateObject2());
var SEMANTIC_RESULT_FIELDS = gql(_templateObject3());

function _templateObject$2() {
  var data = _taggedTemplateLiteralLoose(["\n    query getAutocompleteResults(\n        $query: String!\n        $queryOptions: SearchAssistanceQueryOptions\n        $resultType: [SearchAssistanceResultType]\n    ) {\n        searchAssistanceResult: zgsAutocompleteRequest(\n            query: $query\n            queryOptions: $queryOptions\n            resultType: $resultType\n        ) {\n            requestId\n            results {\n                __typename\n                id\n                ...RegionResultFields\n                ...SemanticResultFields\n                ...RentalCommunityResultFields\n            }\n        }\n    }\n    ", "\n    ", "\n    ", "\n"]);

  _templateObject$2 = function _templateObject() {
    return data;
  };

  return data;
}
var getAutocompleteResults = gql(_templateObject$2(), REGION_RESULT_FIELDS, SEMANTIC_RESULT_FIELDS, RENTAL_COMMUNITY_RESULT_FIELDS);

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */
var SuggestionCategory;

(function (SuggestionCategory) {
  SuggestionCategory["address"] = "ADDRESS";
  SuggestionCategory["claimedHome"] = "CLAIMED_HOME";
  SuggestionCategory["currentLocation"] = "CURRENT_LOCATION";
  SuggestionCategory["rawSearch"] = "RAW_SEARCH";
  SuggestionCategory["recentSearch"] = "RECENT_SEARCH";
  SuggestionCategory["region"] = "REGION";
  SuggestionCategory["savedHome"] = "SAVED_HOME";
  SuggestionCategory["savedSearch"] = "SAVED_SEARCH";
  SuggestionCategory["semantic"] = "SEMANTIC";
})(SuggestionCategory || (SuggestionCategory = {}));

var isRecentSearch = function isRecentSearch(suggestion) {
  return suggestion.category === SuggestionCategory.recentSearch;
};
var isRegionSuggestion = function isRegionSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.region;
};
var isRecentRegionSuggestion = function isRecentRegionSuggestion(suggestion) {
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.region;
};
var isCurrentLocationSuggestion = function isCurrentLocationSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.currentLocation;
};
var isSemanticSuggestion = function isSemanticSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.semantic;
};
var isRecentSemanticSuggestion = function isRecentSemanticSuggestion(suggestion) {
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.semantic;
};
var isAddressSuggestion = function isAddressSuggestion(suggestion) {
  var _suggestion$data;

  return suggestion.category === SuggestionCategory.address && ((_suggestion$data = suggestion.data) === null || _suggestion$data === void 0 ? void 0 : _suggestion$data.addressType) !== 'community_address';
};
var isRecentAddressSuggestion = function isRecentAddressSuggestion(suggestion) {
  var _suggestion$data2;

  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.address && ((_suggestion$data2 = suggestion.data) === null || _suggestion$data2 === void 0 ? void 0 : _suggestion$data2.addressType) !== 'community_address';
};
var isRentalCommunitySuggestion = function isRentalCommunitySuggestion(suggestion) {
  var _suggestion$data3;

  return suggestion.category === SuggestionCategory.address && ((_suggestion$data3 = suggestion.data) === null || _suggestion$data3 === void 0 ? void 0 : _suggestion$data3.addressType) === 'community_address';
};
var isSavedSearchSuggestion = function isSavedSearchSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.savedSearch;
};

/**
 * @typedef {import('./suggestion').Suggestion} Suggestion
 */

var defaultFetchSuggestionsCache = {};
var currentLocationSuggestion = {
  category: SuggestionCategory.currentLocation,
  displayName: 'Current Location'
};

var createRegionsFromSemanticResult = function createRegionsFromSemanticResult(result) {
  if (!result.regionIds || !result.regionTypes || !result.regionDisplayIds) {
    return [];
  } // We source the id, type, and display name from different arrays.
  // These arrays should be of equal length and sorted in the same order,
  // but we'll be safe and only iterate over the smallest of the three.


  var length = Math.min(result.regionIds.length, result.regionTypes.length, result.regionDisplayIds.length, MAX_REGION_SELECTION_LENGTH);
  var regions = [];

  for (var i = 0; i < length; i += 1) {
    var _result$regionTypes$i;

    regions.push({
      id: parseInt(result.regionIds[i], 10),
      type: (_result$regionTypes$i = result.regionTypes[i]) === null || _result$regionTypes$i === void 0 ? void 0 : _result$regionTypes$i.toLowerCase(),
      displayName: result.regionDisplayIds[i]
    });
  }

  return regions;
};

var zgsAutocompleteResultToSuggestion = function zgsAutocompleteResultToSuggestion(result) {
  var _result$location, _result$location2;

  switch (result.__typename) {
    case 'SearchAssistanceRegionResult':
      return {
        category: SuggestionCategory.region,
        displayName: result.id,
        data: {
          regionId: result.regionId,
          regionType: result.subType.toLowerCase()
        }
      };

    case 'SearchAssistanceSemanticResult':
      return {
        category: SuggestionCategory.semantic,
        displayName: result.id,
        data: {
          nearMeSearch: result.nearMe,
          filters: result.filters,
          regions: createRegionsFromSemanticResult(result),
          queryResolutionStatus: result.queryResolutionStatus,
          viewLatitudeDelta: result.viewLatitudeDelta
        }
      };

    case 'SearchAssistanceAddressResult':
      return {
        category: SuggestionCategory.address,
        displayName: result.id
      };

    case 'SearchAssistanceRentalCommunityResult':
      return {
        category: SuggestionCategory.address,
        displayName: result.id,
        data: {
          addressType: 'community_address',
          lat: (_result$location = result.location) === null || _result$location === void 0 ? void 0 : _result$location.latitude,
          lng: (_result$location2 = result.location) === null || _result$location2 === void 0 ? void 0 : _result$location2.longitude
        }
      };

    default:
      return null;
  }
};
var fetchZgGraphSuggestions = function fetchZgGraphSuggestions(query, _ref) {
  return new Promise(function ($return, $error) {
    var zgGraphClient, graphqlQuery, autocompleteConfig, _ref$extraVariables, extraVariables, _ref3, _data$searchAssistanc, _data$searchAssistanc2, abKey, userSearchContext, centroid, fetchLimit, resultTypes, variables, _ref2, data, errors;

    zgGraphClient = _ref.zgGraphClient, graphqlQuery = _ref.graphqlQuery, autocompleteConfig = _ref.autocompleteConfig, _ref$extraVariables = _ref.extraVariables, extraVariables = _ref$extraVariables === void 0 ? {} : _ref$extraVariables;

    var $Try_1_Catch = function (err) {
      try {
        console.error(err);

        if (Array.isArray(err.cause)) {
          err.cause.forEach(function (_ref4) {
            var message = _ref4.message;

            if (message) {
              console.error(message);
            }
          });
        }

        return $return({
          suggestions: [],
          requestError: err
        });
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    try {
      abKey = autocompleteConfig.abKey, userSearchContext = autocompleteConfig.userSearchContext, centroid = autocompleteConfig.centroid, fetchLimit = autocompleteConfig.fetchLimit, resultTypes = autocompleteConfig.resultTypes;
      variables = Object.assign({
        query: query,
        queryOptions: {
          maxResults: fetchLimit,
          userIdentity: {
            abKey: abKey
          },
          userSearchContext: userSearchContext
        },
        resultType: resultTypes
      }, extraVariables);

      if ((centroid === null || centroid === void 0 ? void 0 : centroid.lat) && (centroid === null || centroid === void 0 ? void 0 : centroid.lon)) {
        variables.queryOptions.userLocation = {
          latitude: centroid.lat,
          longitude: centroid.lon
        };
      }

      return Promise.resolve(zgGraphClient.query({
        query: graphqlQuery,
        variables: variables
      })).then(function ($await_3) {
        try {
          _ref2 = $await_3, data = _ref2.data, errors = _ref2.errors;

          if (!data) {
            throw new Error('Error(s) encountered querying search-assistance-zillow', {
              cause: errors
            });
          }

          return $return({
            suggestions: (_ref3 = data === null || data === void 0 ? void 0 : (_data$searchAssistanc = data.searchAssistanceResult) === null || _data$searchAssistanc === void 0 ? void 0 : _data$searchAssistanc.results.map(zgsAutocompleteResultToSuggestion).filter(Boolean)) !== null && _ref3 !== void 0 ? _ref3 : [],
            requestId: data === null || data === void 0 ? void 0 : (_data$searchAssistanc2 = data.searchAssistanceResult) === null || _data$searchAssistanc2 === void 0 ? void 0 : _data$searchAssistanc2.requestId
          });
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (err) {
      $Try_1_Catch(err);
    }
  });
};
/**
 * Fetches suggestions from remote autocomplete endpoint.
 * @param {String} query - search text
 * @param {AutocompleteManager} autocompleteManager - instance of AutocompleteManager to use
 * @param {Object} autocompleteConfig - config options for autocomplete
 * @return {Promise} remote search autocomplete suggestions
 */

function fetchRemoteSuggestions(query, autocompleteManager, autocompleteConfig) {
  return autocompleteManager.fetchResults(query, [FetchTypes.REGION, FetchTypes.ADDRESS], autocompleteConfig);
}
/**
 * Fetches locally stored/saved searches.
 * @param {String} query - search text
 * @param {AutocompleteManager} autocompleteManager - instance of AutocompleteManager to use
 * @param {Object} autocompleteConfig - config options for autocomplete
 * @return {Promise} local search autocomplete suggestions
 */

function fetchLocalSuggestions(query, autocompleteManager, autocompleteConfig, disableCurrentLocation) {
  return autocompleteManager.fetchResults(query, [FetchTypes.RECENT_SEARCH, FetchTypes.SAVED_HOMES, FetchTypes.SAVED_SEARCH], autocompleteConfig).then(function (results) {
    if (disableCurrentLocation) {
      return results;
    }

    return [currentLocationSuggestion].concat(_toConsumableArray(results));
  });
}
/**
 * Fetches autocomplete suggestions
 * @param {String} query - search query suggestions are based off
 * @param {Object} location - { latitude, longitude } - override location for autocompleteConfig.centroid
 * @param {Object} autocompleteConfig - autocomplete config
 * @param {String} reason - the reason a fetch is being requested
 * @param {Number} [minAutocompleteChars] - minimum number of characters to trigger autocomplete
 * @param {Object} [zgGraphClient] - zgGraphClient instance
 * @return {Promise} suggestions promise
 */

function defaultFetchSuggestions(query, _ref5, autocompleteConfig, reason, minAutocompleteChars, zgGraphClient) {
  return new Promise(function ($return, $error) {
    var latitude, longitude, disableCurrentLocation, updatedAutocompleteConfig, returnLocalSuggestions, getLocalSuggestions, zgGraphAutocompleteConfig, _ref6, _ref7, zgGraphSuggestions, localSuggestions;

    latitude = _ref5.latitude, longitude = _ref5.longitude;

    if (minAutocompleteChars === void 0) {
      minAutocompleteChars = MIN_AUTOCOMPLETE_CHARS;
    }

    if (zgGraphClient === void 0) {
      zgGraphClient = undefined;
    }

    disableCurrentLocation = autocompleteConfig.disableCurrentLocation;
    updatedAutocompleteConfig = Object.assign({}, autocompleteConfig);

    // apply location override if a latitude/longitude is passed in
    if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined') {
      updatedAutocompleteConfig.centroid = {
        lat: latitude,
        lon: longitude
      };
    } // we have to reset autocomplete manager here to retrieve user personalization data
    // this would happen if the user started the session anonymously, then signed in later.


    if (reason === INIT_LOCAL_RESULTS) {
      defaultFetchSuggestionsCache.autocompleteManager = null;
    } // if we have a new location context, create a new AutocompleteManager with updated centroid


    if (updatedAutocompleteConfig.centroid && (updatedAutocompleteConfig.centroid.lat !== defaultFetchSuggestionsCache.latitude || updatedAutocompleteConfig.centroid.lon !== defaultFetchSuggestionsCache.longitude)) {
      defaultFetchSuggestionsCache.latitude = updatedAutocompleteConfig.centroid.lat;
      defaultFetchSuggestionsCache.longitude = updatedAutocompleteConfig.centroid.lon;
      defaultFetchSuggestionsCache.autocompleteManager = null;
    }

    if (!defaultFetchSuggestionsCache.autocompleteManager) {
      defaultFetchSuggestionsCache.autocompleteManager = new AutocompleteManager(updatedAutocompleteConfig);
    }

    returnLocalSuggestions = query.length < minAutocompleteChars || reason === 'input-focused' || reason === INIT_LOCAL_RESULTS;

    getLocalSuggestions = function getLocalSuggestions() {
      return fetchLocalSuggestions('', defaultFetchSuggestionsCache.autocompleteManager, updatedAutocompleteConfig, disableCurrentLocation);
    };

    if (zgGraphClient) {
      zgGraphAutocompleteConfig = Object.assign({}, updatedAutocompleteConfig, {
        fetchLimit: returnLocalSuggestions ? DEFAULT_MAX_PRE_TYPE_SEMANTIC : DEFAULT_MAX_POST_TYPE_SEMANTIC
      });
      return Promise.resolve(Promise.all([fetchZgGraphSuggestions(returnLocalSuggestions ? '' : query, {
        zgGraphClient: zgGraphClient,
        graphqlQuery: getAutocompleteResults,
        autocompleteConfig: zgGraphAutocompleteConfig
      }), returnLocalSuggestions ? getLocalSuggestions() : Promise.resolve([])])).then(function ($await_4) {
        try {
          _ref6 = $await_4, _ref7 = _slicedToArray(_ref6, 2), zgGraphSuggestions = _ref7[0], localSuggestions = _ref7[1];
          return $return([].concat(_toConsumableArray(localSuggestions), _toConsumableArray(zgGraphSuggestions.suggestions)));
        } catch ($boundEx) {
          return $error($boundEx);
        }
      }, $error);
    }

    if (returnLocalSuggestions) {
      return $return(getLocalSuggestions());
    }

    return $return(fetchRemoteSuggestions(query, defaultFetchSuggestionsCache.autocompleteManager, updatedAutocompleteConfig));
  });
}
var filterRecentSearches = function filterRecentSearches(results, isMultiRegionEnabled, isNaturalLanguageSearchEnabled) {
  var getIsValidSubCategory = function getIsValidSubCategory(subCategory) {
    if (isNaturalLanguageSearchEnabled) {
      return [SuggestionCategory.semantic, SuggestionCategory.region, SuggestionCategory.address].includes(subCategory);
    }

    if (isMultiRegionEnabled) {
      return [SuggestionCategory.region, SuggestionCategory.address].includes(subCategory);
    }

    return subCategory === null || ![SuggestionCategory.semantic, SuggestionCategory.currentLocation].includes(subCategory);
  };

  return results.filter(function (_ref8) {
    var category = _ref8.category,
        subCategory = _ref8.subCategory;
    return category === SuggestionCategory.recentSearch && getIsValidSubCategory(subCategory);
  });
};
/**
 * Default function for filtering autocomplete suggestions. Enforces max limits for various result types
 * @param {Suggestion[]} suggestions - Array of suggestions to filter
 * @return {Suggestion[]} filtered set of suggestions
 */

function defaultFilterSuggestions(suggestions, isMultiRegionEnabled, isNaturalLanguageSearchEnabled) {
  if (isMultiRegionEnabled === void 0) {
    isMultiRegionEnabled = false;
  }

  if (isNaturalLanguageSearchEnabled === void 0) {
    isNaturalLanguageSearchEnabled = false;
  }

  var filteredCurrentLocation = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.currentLocation;
  });
  var filteredSavedSearches = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.savedSearch;
  });
  filteredSavedSearches.splice(DEFAULT_MAX_SAVED_SEARCHES);
  var filteredClaimedHomes = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.claimedHome;
  });
  filteredClaimedHomes.splice(DEFAULT_MAX_CLAIMED_HOMES);
  var filteredSavedHomes = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.savedHome;
  });
  filteredSavedHomes.splice(DEFAULT_MAX_SAVED_HOMES);
  var filteredRecentSearches = filterRecentSearches(suggestions, isMultiRegionEnabled, isNaturalLanguageSearchEnabled);
  filteredRecentSearches.splice(DEFAULT_MAX_RECENT_SEARCHES);
  var filteredRegionAddress = suggestions.filter(function (suggestion) {
    return [SuggestionCategory.region, SuggestionCategory.address, SuggestionCategory.semantic].indexOf(suggestion.category) >= 0;
  });
  filteredRegionAddress.splice(DEFAULT_MAX_REGION_ADDRESS);
  return [].concat(filteredCurrentLocation, filteredSavedSearches, filteredClaimedHomes, filteredSavedHomes, filteredRecentSearches, filteredRegionAddress);
}
/**
 * Extracts search value from selected suggestion.
 * @param {Suggestion} suggestion
 *     @param {String} suggestion.displayName - value of fetched suggestion
 * @return {String} suggestion value
 */

function getSuggestionValue(_ref9) {
  var category = _ref9.category,
      displayName = _ref9.displayName;

  if ([SuggestionCategory.region, SuggestionCategory.semantic, SuggestionCategory.address, SuggestionCategory.recentSearch, SuggestionCategory.rawSearch].indexOf(category) >= 0) {
    return displayName;
  }

  return '';
}

var EventNames;
/* eslint-disable zillow/@typescript-eslint/naming-convention */

(function (EventNames) {
  EventNames["selectSearchSuggestion"] = "SELECT_SEARCH_SUGGESTION";
  EventNames["rawSearch"] = "RAW_SEARCH";
  EventNames["selectRecentSearch"] = "SELECT_RECENT_SEARCH";
  EventNames["removeMultiRegion"] = "REMOVE_MULTIREGION";
  EventNames["currentLocationSearch"] = "CURRENT_LOCATION_SEARCH";
  EventNames["removeAllMultiRegion"] = "REMOVE_ALL_MULTIREGION";
  EventNames["addNthRegion"] = "ADD_NTH_REGION";
  EventNames["dismissSearch"] = "DISMISS_SEARCH";
  EventNames["queryUnderstandingResponse"] = "QUERY_UNDERSTANDING_RESPONSE";
  EventNames["searchBoxFocus"] = "SEARCH_BOX_FOCUS";
})(EventNames || (EventNames = {}));

var EVENT_TYPE_IDS = {
  SEARCH_BOX_FOCUS: '65',
  SELECT_SEARCH_SUGGESTION: '74',
  RAW_SEARCH: '73',
  SELECT_RECENT_SEARCH: '1032',
  REMOVE_MULTIREGION: '1069',
  CURRENT_LOCATION_SEARCH: '1079',
  REMOVE_ALL_MULTIREGION: '1082',
  ADD_NTH_REGION: '1081',
  DISMISS_SEARCH: '1080',
  QUERY_UNDERSTANDING_RESPONSE: '3875'
};
var EVENT_TYPE_VERSION_IDS = {
  SEARCH_BOX_FOCUS: '1',
  SELECT_SEARCH_SUGGESTION: '1',
  RAW_SEARCH: '1',
  SELECT_RECENT_SEARCH: '1',
  REMOVE_MULTIREGION: '1',
  CURRENT_LOCATION_SEARCH: '1',
  REMOVE_ALL_MULTIREGION: '1',
  ADD_NTH_REGION: '1',
  DISMISS_SEARCH: '1',
  QUERY_UNDERSTANDING_RESPONSE: '4'
};
var EVENT_TEMPLATE_IDS = {
  SEARCH_BOX_FOCUS: '16',
  SELECT_SEARCH_SUGGESTION: '16',
  RAW_SEARCH: '16',
  SELECT_RECENT_SEARCH: '1',
  REMOVE_MULTIREGION: '16',
  CURRENT_LOCATION_SEARCH: '16',
  REMOVE_ALL_MULTIREGION: '16',
  ADD_NTH_REGION: '16',
  DISMISS_SEARCH: '16',
  QUERY_UNDERSTANDING_RESPONSE: '278'
};
var TRIGGER_TYPE_NM = {
  SEARCH_BOX_FOCUS: 'interaction',
  SELECT_SEARCH_SUGGESTION: 'interaction',
  RAW_SEARCH: 'interaction',
  SELECT_RECENT_SEARCH: 'interaction',
  REMOVE_MULTIREGION: 'interaction',
  CURRENT_LOCATION_SEARCH: 'interaction',
  REMOVE_ALL_MULTIREGION: 'interaction',
  ADD_NTH_REGION: 'interaction',
  DISMISS_SEARCH: 'interaction',
  QUERY_UNDERSTANDING_RESPONSE: 'interaction'
};
var TRIGGER_OBJECT_NM = {
  SEARCH_BOX_FOCUS: 'search_component',
  SELECT_SEARCH_SUGGESTION: 'search_field',
  RAW_SEARCH: 'search_field',
  SELECT_RECENT_SEARCH: 'search_field',
  REMOVE_MULTIREGION: 'search_field',
  CURRENT_LOCATION_SEARCH: 'search_box',
  REMOVE_ALL_MULTIREGION: 'search_box',
  ADD_NTH_REGION: 'search_box',
  DISMISS_SEARCH: 'no_trigger_object',
  QUERY_UNDERSTANDING_RESPONSE: 'search_field'
};
var TRIGGER_SOURCE_NM = {
  SEARCH_BOX_FOCUS: 'search_bar',
  SELECT_SEARCH_SUGGESTION: 'select_search_suggestion',
  RAW_SEARCH: 'manual_search_selection',
  SELECT_RECENT_SEARCH: 'button_to_select_recent_search',
  REMOVE_MULTIREGION: 'button_to_remove_multiregion',
  CURRENT_LOCATION_SEARCH: 'current_location',
  REMOVE_ALL_MULTIREGION: 'button_remove_regions',
  ADD_NTH_REGION: 'add_region',
  DISMISS_SEARCH: 'button_to_dismiss_search_dropdown',
  QUERY_UNDERSTANDING_RESPONSE: 'query_understanding_response'
};
var SEMANTIC_EVENT_NM = {
  SEARCH_BOX_FOCUS: 'search_box_focus',
  SELECT_SEARCH_SUGGESTION: 'search_home',
  RAW_SEARCH: 'search_home',
  SELECT_RECENT_SEARCH: 'search_home',
  REMOVE_MULTIREGION: 'change_search_filter',
  CURRENT_LOCATION_SEARCH: 'search_home',
  REMOVE_ALL_MULTIREGION: 'search_home',
  ADD_NTH_REGION: 'search_home',
  DISMISS_SEARCH: 'dismiss_search',
  QUERY_UNDERSTANDING_RESPONSE: 'search_home'
};
var TOPIC_TAG_TXT = {
  SEARCH_BOX_FOCUS: ['search_box_focus'],
  SELECT_SEARCH_SUGGESTION: ['search'],
  RAW_SEARCH: ['search'],
  SELECT_RECENT_SEARCH: ['search'],
  REMOVE_MULTIREGION: ['search'],
  CURRENT_LOCATION_SEARCH: ['search'],
  REMOVE_ALL_MULTIREGION: ['search'],
  ADD_NTH_REGION: ['search'],
  DISMISS_SEARCH: ['search'],
  QUERY_UNDERSTANDING_RESPONSE: ['search']
};
/* eslint-disable zillow/@typescript-eslint/naming-convention */

var QueryResolutionStatus;

(function (QueryResolutionStatus) {
  QueryResolutionStatus["fullResolution"] = "FULL_RESOLUTION";
  QueryResolutionStatus["partialResolution"] = "PARTIAL_RESOLUTION";
  QueryResolutionStatus["failed"] = "FAILED";
  QueryResolutionStatus["error"] = "ERROR";
})(QueryResolutionStatus || (QueryResolutionStatus = {}));

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports, zillow/@typescript-eslint/naming-convention */
var CLICKSTREAM_FILTER_IDS = ['ac_ind', 'age_55_plus_ind', 'apt_ind', 'apt_or_condo_ind', 'auction_ind', 'bath_cnt_txt', 'bed_max_cnt_txt', 'bed_min_cnt_txt', 'built_year_max_nb', 'built_year_min_nb', 'city_view_ind', 'coming_soon_ind', 'condo_ind', 'day_on_zillow_nb', 'finished_basement_ind', 'for_rent_ind', 'forclosure_ind', 'fsba_ind', 'fsbo_ind', 'garage_ind', 'hoa_ind', 'hoa_max_amt', 'keywords_txt', 'lot_land_ind', 'lot_size_sqft_max_nb', 'lot_size_sqft_min_nb', 'manufactured_ind', 'monthly_payment_max_amt', 'monthly_payment_min_amt', 'mountain_view_ind', 'multi_family_ind', 'new_construction_ind', 'only_open_house_ind', 'park_view_ind', 'parking_spot_txt', 'pool_ind', 'premarket_foreclosure_ind', 'price_range_max_amt', 'price_range_min_amt', 'recently_sold_ind', 'rent_large_pets_allowed_ind', 'rental_accepts_application_ind', 'rental_cats_allowed_ind', 'rental_in_unit_laundry_ind', 'rental_parking_available_ind', 'rental_income_restricted_ind', 'rental_small_pets_allowed_ind', 'single_family_ind', 'single_story_ind', 'sqft_max_nb', 'sqft_min_nb', 'tour_3d_ind', 'townhouse_ind', 'unfinished_basement_ind', 'water_view_ind', 'waterfront_ind'];
var CLICKSTREAM_SHOW_ONLY_MAP = {
  SINGLE_STORY: 'single_story_ind',
  INCOME_RESTRICTED: 'rental_income_restricted_ind',
  INUNIT_LAUNDRY: 'rental_in_unit_laundry_ind',
  ASSIGNED_PARKING: 'rental_parking_available_ind',
  OPEN_HOUSES: 'only_open_house_ind',
  WATER_FRONT: 'waterfront_ind',
  AIRCONDITIONING: 'ac_ind',
  POOL: 'pool_ind',
  ACCEPTS_RENTAL_APPLICATIONS: 'rental_accepts_application_ind',
  GARAGE: 'garage_ind',
  APPROVED_VIRTUALTOUR: 'tour_3d_ind'
};
var CLICKSTREAM_BASEMENT_MAP = {
  FINISHED: 'finished_basement_ind',
  UNFINISHED: 'unfinished_basement_ind'
};
var CLICKSTREAM_HOME_TYPE_MAP = {
  TOWNHOME: 'townhouse_ind',
  LAND: 'lot_land_ind',
  MANUFACTURED: 'manufactured_ind',
  MULTIFAMILY: 'multi_family_ind',
  APARTMENT: 'apt_ind',
  CONDO: 'condo_ind',
  SINGLEFAMILY: 'single_family_ind'
};
var CLICKSTREAM_LISTING_STATUS_MAP = {
  RECENTLY_SOLD: 'recently_sold_ind',
  SOLD: 'recently_sold_ind',
  // PREFORECLOSURE filter is not currently defined in the search_filter block schema
  // PREFORECLOSURE: 'preforeclosure_ind',
  FORECLOSED: 'premarket_foreclosure_ind',
  FOR_RENT: 'for_rent_ind',
  FSBA: 'fsba_ind',
  FSBO: 'fsbo_ind',
  AUCTION: 'auction_ind',
  COMINGSOON: 'coming_soon_ind',
  FORECLOSURE: 'forclosure_ind',
  NEWCONSTRUCTION: 'new_construction_ind'
};
var CLICKSTREAM_PETS_ALLOWED_MAP = {
  LARGEDOGS: 'rent_large_pets_allowed_ind',
  SMALLDOGS: 'rental_small_pets_allowed_ind',
  CATS: 'rental_cats_allowed_ind'
};
var CLICKSTREAM_VIEW_MAP = {
  CITY: 'city_view_ind',
  PARK: 'park_view_ind',
  MOUNTAIN: 'mountain_view_ind',
  WATER: 'water_view_ind'
};

var yieldWrapper = yieldCallback(function (callback) {
  callback();
});
/**
 * @typedef {import('./suggestion').Suggestion} Suggestion
 * @typedef {import('./suggestion').SuggestionMetadata} SuggestionMetadata
 */

var getEnvelopeBlock = function getEnvelopeBlock(eventName) {
  return {
    event_template_id: EVENT_TEMPLATE_IDS[eventName],
    event_template_version_id: '1',
    event_type_id: EVENT_TYPE_IDS[eventName],
    event_type_version_id: EVENT_TYPE_VERSION_IDS[eventName],
    event_client_start_dtm: new Date().toISOString()
  };
};

var getClickstreamBlock = function getClickstreamBlock(eventName) {
  return {
    trigger_location_nm: 'search_results',
    trigger_type_nm: TRIGGER_TYPE_NM[eventName],
    trigger_object_nm: TRIGGER_OBJECT_NM[eventName],
    trigger_source_nm: TRIGGER_SOURCE_NM[eventName]
  };
};

var getSemanticBlock = function getSemanticBlock(eventName) {
  return {
    semantic_event_nm: SEMANTIC_EVENT_NM[eventName],
    topic_tag_txt: TOPIC_TAG_TXT[eventName]
  };
};

var suggestionToSearchType = function suggestionToSearchType(suggestion) {
  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    return 'REGION';
  }

  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    return 'NATURAL';
  }

  if (isAddressSuggestion(suggestion) || isRecentAddressSuggestion(suggestion)) {
    return 'ADDRESS';
  }

  if (isRentalCommunitySuggestion(suggestion) || isRentalCommunitySuggestion(suggestion)) {
    return 'RENTAL_COMMUNITY';
  }

  return null;
};

var getModifiedRegionName = function getModifiedRegionName(displayName) {
  var shouldModifyRegionName = displayName.includes(',');
  return shouldModifyRegionName ? displayName.split(',')[0] : displayName;
};

var createEmptyRegionFields = function createEmptyRegionFields() {
  return {
    city_nm: null,
    city_id: null,
    state_id: null,
    state_cd: null,
    zip_cd: null,
    neighborhood_nm: null,
    neighborhood_id: null,
    custom_region_ids: [],
    region_types: []
  };
};
/**
 * @param {Suggestion} suggestion
 */


var getRegionTypeFields = function getRegionTypeFields(suggestion) {
  var _suggestion$data, _suggestion$data2, _suggestion$data3, _suggestion$data4;

  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    var fields = createEmptyRegionFields();
    var name = getModifiedRegionName(suggestion.displayName);

    switch ((_suggestion$data = suggestion.data) === null || _suggestion$data === void 0 ? void 0 : _suggestion$data.regionType) {
      case 'city':
        fields.city_nm = name;
        fields.city_id = (_suggestion$data2 = suggestion.data) === null || _suggestion$data2 === void 0 ? void 0 : _suggestion$data2.regionId;
        return fields;

      case 'zipcode':
        fields.zip_cd = name;
        return fields;

      case 'neighborhood':
        fields.neighborhood_nm = name;
        fields.neighborhood_id = (_suggestion$data3 = suggestion.data) === null || _suggestion$data3 === void 0 ? void 0 : _suggestion$data3.regionId;
        return fields;

      case 'state':
        fields.state_cd = name;
        fields.state_id = (_suggestion$data4 = suggestion.data) === null || _suggestion$data4 === void 0 ? void 0 : _suggestion$data4.regionId;
        return fields;

      default:
        return fields;
    }
  }

  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    var _ref, _suggestion$data5;

    var _fields = createEmptyRegionFields();

    var regions = (_ref = (_suggestion$data5 = suggestion.data) === null || _suggestion$data5 === void 0 ? void 0 : _suggestion$data5.regions) !== null && _ref !== void 0 ? _ref : [];

    if (regions.length === 0) {
      return _fields;
    }

    _fields.custom_region_ids = regions.map(function (entry) {
      return entry.id;
    });
    _fields.region_types = regions.map(function (entry) {
      return entry.type;
    });
    var region = regions[0];
    var regionName = getModifiedRegionName(region.displayName);

    switch (region.type) {
      case 'city':
        _fields.city_nm = regionName;
        _fields.city_id = region.id;
        return _fields;

      case 'zipcode':
        _fields.zip_cd = regionName;
        return _fields;

      case 'neighborhood':
        _fields.neighborhood_nm = regionName;
        _fields.neighborhood_id = region.id;
        return _fields;

      case 'state':
        _fields.state_cd = regionName;
        _fields.state_id = region.id;
        return _fields;

      default:
        return _fields;
    }
  }

  return createEmptyRegionFields();
};
/**
 * @param {Suggestion} suggestion
 * @param {*} newLaneEventProperties
 */


var getUpdatedSearchFilterBlock = function getUpdatedSearchFilterBlock(suggestion, newLaneEventProperties) {
  var updateSearchFilter = (newLaneEventProperties === null || newLaneEventProperties === void 0 ? void 0 : newLaneEventProperties.search_filter) || {};

  if (!suggestion || isEmpty(suggestion)) {
    return updateSearchFilter;
  }

  var displayName = suggestion.displayName,
      data = suggestion.data,
      metadata = suggestion.metadata;
  updateSearchFilter.request_id = metadata === null || metadata === void 0 ? void 0 : metadata.requestId;
  updateSearchFilter.interaction_id = metadata === null || metadata === void 0 ? void 0 : metadata.interactionId;
  updateSearchFilter.search_type_txt = suggestionToSearchType(suggestion);
  updateSearchFilter.search_selection_type_txt = metadata === null || metadata === void 0 ? void 0 : metadata.selectionType;
  updateSearchFilter.selection_index_nb = metadata === null || metadata === void 0 ? void 0 : metadata.selectionIndex;
  updateSearchFilter.understood_query_txt = displayName;
  updateSearchFilter.user_search_terms_txt = (metadata === null || metadata === void 0 ? void 0 : metadata.rawValue) ? [metadata === null || metadata === void 0 ? void 0 : metadata.rawValue] : [];

  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    var _suggestion$data6;

    updateSearchFilter.region_types = [(_suggestion$data6 = suggestion.data) === null || _suggestion$data6 === void 0 ? void 0 : _suggestion$data6.regionType];
    Object.entries(getRegionTypeFields(suggestion)).forEach(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 2),
          key = _ref3[0],
          value = _ref3[1];

      updateSearchFilter[key] = value;
    });
  }

  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    var _ref6;

    updateSearchFilter.query_resolution_status_txt = data.queryResolutionStatus;
    Object.entries(getRegionTypeFields(suggestion)).forEach(function (_ref4) {
      var _ref5 = _slicedToArray(_ref4, 2),
          key = _ref5[0],
          value = _ref5[1];

      updateSearchFilter[key] = value;
    });

    if (metadata === null || metadata === void 0 ? void 0 : metadata.searchFilterBlock) {
      CLICKSTREAM_FILTER_IDS.forEach(function (key) {
        if (key in updateSearchFilter && metadata.searchFilterBlock[key] === undefined) {
          delete updateSearchFilter[key];
        }

        if (metadata.searchFilterBlock[key] !== undefined) {
          updateSearchFilter[key] = metadata.searchFilterBlock[key];
        }
      });
    }

    updateSearchFilter.changed_filter_fields = (_ref6 = metadata === null || metadata === void 0 ? void 0 : metadata.searchFilterBlock.changed_filter_fields) !== null && _ref6 !== void 0 ? _ref6 : [];
  } else {
    updateSearchFilter.query_resolution_status_txt = QueryResolutionStatus.fullResolution;
  }

  return updateSearchFilter;
};
/**
 * @param {Suggestion} suggestion
 * @param {*} newLaneEventProperties
 */


var getUpdatedSearchMapBlock = function getUpdatedSearchMapBlock(suggestion, newLaneEventProperties) {
  var updatedSearchMap = newLaneEventProperties.search_map || {};

  if (!suggestion || isEmpty(suggestion)) {
    return updatedSearchMap;
  }

  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    updatedSearchMap.selected_regions_txt = [suggestion.displayName];
  }

  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    var _ref7, _suggestion$data7, _suggestion$data7$reg;

    updatedSearchMap.selected_regions_txt = (_ref7 = (_suggestion$data7 = suggestion.data) === null || _suggestion$data7 === void 0 ? void 0 : (_suggestion$data7$reg = _suggestion$data7.regions) === null || _suggestion$data7$reg === void 0 ? void 0 : _suggestion$data7$reg.map(function (entry) {
      return entry.displayName;
    })) !== null && _ref7 !== void 0 ? _ref7 : [];
  }

  return updatedSearchMap;
};

var getEventSpecificData = function getEventSpecificData(result, newLaneEventProperties) {
  switch (result === null || result === void 0 ? void 0 : result.category) {
    case 'RECENT_SEARCH':
      var updatedNewLaneEventProperties = omit(newLaneEventProperties, ['search_map', 'search_result']);
      return Object.assign({}, updatedNewLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.selectRecentSearch),
        clickstream_trigger: getClickstreamBlock(EventNames.selectRecentSearch),
        envelope: getEnvelopeBlock(EventNames.selectRecentSearch)
      });

    case 'RAW_SEARCH':
      return Object.assign({}, newLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.rawSearch),
        clickstream_trigger: getClickstreamBlock(EventNames.rawSearch),
        envelope: getEnvelopeBlock(EventNames.rawSearch)
      });

    case 'CURRENT_LOCATION':
      return Object.assign({}, newLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.currentLocationSearch),
        clickstream_trigger: getClickstreamBlock(EventNames.currentLocationSearch),
        envelope: getEnvelopeBlock(EventNames.currentLocationSearch)
      });

    default:
      return Object.assign({}, newLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.selectSearchSuggestion),
        clickstream_trigger: getClickstreamBlock(EventNames.selectSearchSuggestion),
        envelope: getEnvelopeBlock(EventNames.selectSearchSuggestion)
      });
  }
};

var getRemoveRegionEventData = function getRemoveRegionEventData(eventName, newLaneEventProperties, removedRegionIndex) {
  var updateSearchFilter = newLaneEventProperties.search_filter || {};
  var updatedSearchMap = newLaneEventProperties.search_map || {};

  var userSearchTermsArray = _toConsumableArray(updateSearchFilter.user_search_terms_txt);

  var customRegionIdsArray = _toConsumableArray(updateSearchFilter.custom_region_ids);

  var regionTypesArray = _toConsumableArray(updateSearchFilter.region_types);

  var selectedRegionsArray = _toConsumableArray(updatedSearchMap.selected_regions_txt);

  switch (eventName) {
    case EventNames.removeMultiRegion:
      var _updateSearchFilter$c;

      userSearchTermsArray.splice(removedRegionIndex - 1, 1);
      customRegionIdsArray.splice(removedRegionIndex - 1, 1);
      regionTypesArray.splice(removedRegionIndex - 1, 1);
      selectedRegionsArray.splice(removedRegionIndex - 1, 1);
      updateSearchFilter.user_search_terms_txt = userSearchTermsArray;
      updateSearchFilter.custom_region_ids = customRegionIdsArray;
      updateSearchFilter.region_types = regionTypesArray;
      updatedSearchMap.selected_regions_txt = selectedRegionsArray;

      if (!((_updateSearchFilter$c = updateSearchFilter.custom_region_ids) === null || _updateSearchFilter$c === void 0 ? void 0 : _updateSearchFilter$c.length)) {
        updateSearchFilter.city_nm = null;
        updateSearchFilter.city_id = null;
        updateSearchFilter.state_id = null;
        updateSearchFilter.state_cd = null;
        updateSearchFilter.zip_cd = null;
        updateSearchFilter.neighborhood_nm = null;
        updateSearchFilter.neighborhood_id = null;
      }

      return Object.assign({}, newLaneEventProperties, {
        search_filter: updateSearchFilter,
        search_map: updatedSearchMap,
        semantic: getSemanticBlock(EventNames.removeMultiRegion),
        clickstream_trigger: getClickstreamBlock(EventNames.removeMultiRegion),
        envelope: getEnvelopeBlock(EventNames.removeMultiRegion)
      });

    case EventNames.removeAllMultiRegion:
      userSearchTermsArray.splice(0);
      customRegionIdsArray.splice(0);
      regionTypesArray.splice(0);
      selectedRegionsArray.splice(0);
      updateSearchFilter.user_search_terms_txt = userSearchTermsArray;
      updateSearchFilter.custom_region_ids = customRegionIdsArray;
      updateSearchFilter.region_types = regionTypesArray;
      updateSearchFilter.city_nm = null;
      updateSearchFilter.city_id = null;
      updateSearchFilter.state_id = null;
      updateSearchFilter.state_cd = null;
      updateSearchFilter.zip_cd = null;
      updateSearchFilter.neighborhood_nm = null;
      updateSearchFilter.neighborhood_id = null;
      updatedSearchMap.selected_regions_txt = selectedRegionsArray;
      return Object.assign({}, newLaneEventProperties, {
        search_filter: updateSearchFilter,
        search_map: updatedSearchMap,
        semantic: getSemanticBlock(EventNames.removeAllMultiRegion),
        clickstream_trigger: getClickstreamBlock(EventNames.removeAllMultiRegion),
        envelope: getEnvelopeBlock(EventNames.removeAllMultiRegion)
      });

    default:
      return null;
  }
};

var getAddRegionEventData = function getAddRegionEventData(selection, newLaneEventProperties, isMultiregionSearch, isRegionInMultiRegionSelection, isPointRegionSelected) {
  if (isRegionInMultiRegionSelection === void 0) {
    isRegionInMultiRegionSelection = false;
  }

  var selectedRegionTxt = null;
  var updateSearchFilter = newLaneEventProperties.search_filter || {};
  var updatedSearchMap = newLaneEventProperties.search_map || {};
  var regionId = null;
  var regionType = null;
  var updatedNewLaneEventProperties = getEventSpecificData(selection, newLaneEventProperties);

  if (selection && (selection === null || selection === void 0 ? void 0 : selection.displayName)) {
    var _updateSearchFilter$u;

    selectedRegionTxt = getModifiedRegionName(selection === null || selection === void 0 ? void 0 : selection.displayName);

    if (!((updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.custom_region_ids) && (updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.region_types)) && (updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : (_updateSearchFilter$u = updateSearchFilter.user_search_terms_txt) === null || _updateSearchFilter$u === void 0 ? void 0 : _updateSearchFilter$u.length) === 1) {
      updateSearchFilter.user_search_terms_txt = [];
      updateSearchFilter.school_id = null;
    }

    if (selection === null || selection === void 0 ? void 0 : selection.data) {
      var _selection$data, _selection$data2;

      regionId = selection === null || selection === void 0 ? void 0 : (_selection$data = selection.data) === null || _selection$data === void 0 ? void 0 : _selection$data.regionId;
      regionType = selection === null || selection === void 0 ? void 0 : (_selection$data2 = selection.data) === null || _selection$data2 === void 0 ? void 0 : _selection$data2.regionType;

      if (isPointRegionSelected) {
        updateSearchFilter.custom_region_ids = [];
        updateSearchFilter.region_types = [];
        updateSearchFilter.user_search_terms_txt = [];
      }

      if (!((updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.custom_region_ids) && (updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.region_types)) || isPointRegionSelected) {
        Object.entries(getRegionTypeFields(selection)).forEach(function (_ref8) {
          var _ref9 = _slicedToArray(_ref8, 2),
              key = _ref9[0],
              value = _ref9[1];

          updateSearchFilter[key] = value;
        });
      }
    }

    if (selection.category === 'CURRENT_LOCATION') {
      updateSearchFilter.user_search_terms_txt = [selectedRegionTxt];
      updateSearchFilter.custom_region_ids = [];
      updateSearchFilter.region_types = [];
      updateSearchFilter.city_id = null;
      updateSearchFilter.city_nm = null;
      updateSearchFilter.neighborhood_id = null;
      updateSearchFilter.neighborhood_nm = null;
      updateSearchFilter.state_cd = null;
      updateSearchFilter.state_id = null;
      updateSearchFilter.zip_cd = null;
      updatedSearchMap.selected_regions_txt = [];
    } else if (isMultiregionSearch) {
      if (isRegionInMultiRegionSelection) {
        return Object.assign({}, updatedNewLaneEventProperties);
      }

      updateSearchFilter.user_search_terms_txt = [].concat(_toConsumableArray((updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.user_search_terms_txt) || []), [selectedRegionTxt]);

      if (!updateSearchFilter.custom_region_ids) {
        updateSearchFilter.custom_region_ids = [];
      }

      updateSearchFilter.custom_region_ids = [].concat(_toConsumableArray(updateSearchFilter.custom_region_ids), [regionId]);

      if (!updateSearchFilter.region_types) {
        updateSearchFilter.region_types = [];
      }

      updateSearchFilter.region_types = [].concat(_toConsumableArray(updateSearchFilter.region_types), [regionType]);

      if (selection.category !== 'RECENT_SEARCH' && (selection.category === 'REGION' || (selection === null || selection === void 0 ? void 0 : selection.subCategory) === 'REGION')) {
        updatedSearchMap.selected_regions_txt = [].concat(_toConsumableArray((updatedSearchMap === null || updatedSearchMap === void 0 ? void 0 : updatedSearchMap.selected_regions_txt) || []), [selectedRegionTxt]);
      }
    } else {
      updateSearchFilter.user_search_terms_txt = [selectedRegionTxt];
      updateSearchFilter.custom_region_ids = [regionId];
      updateSearchFilter.region_types = [regionType];

      if (selection.category !== 'RECENT_SEARCH' && (selection.category === 'REGION' || (selection === null || selection === void 0 ? void 0 : selection.subCategory) === 'REGION')) {
        updatedSearchMap.selected_regions_txt = [selectedRegionTxt];
      }
    }

    if (selection.category !== 'RECENT_SEARCH') {
      return Object.assign({}, updatedNewLaneEventProperties, {
        search_filter: updateSearchFilter,
        search_map: updatedSearchMap
      });
    }

    return Object.assign({}, updatedNewLaneEventProperties, {
      search_filter: updateSearchFilter
    });
  }

  return null;
};
/**
 * @returns {Object} - The new lane properties object.
 */


function getNewLaneProperties(eventName) {
  return {
    semantic: getSemanticBlock(eventName),
    clickstream_trigger: getClickstreamBlock(eventName),
    envelope: getEnvelopeBlock(eventName)
  };
}
/**
 *
 * @param {*} trackEventProp
 * @param {Suggestion | null} autocompleteResult
 * @param {number | null} [regionIndex]
 * @param {boolean} isMultiregionSearch
 * @param {boolean} isRegionInMultiRegionSelection
 * @param {string} eventName
 */

var fireTrackEvent = function fireTrackEvent(_ref10) {
  var trackEventProp = _ref10.trackEventProp,
      _ref10$autocompleteRe = _ref10.autocompleteResult,
      autocompleteResult = _ref10$autocompleteRe === void 0 ? null : _ref10$autocompleteRe,
      _ref10$regionIndex = _ref10.regionIndex,
      regionIndex = _ref10$regionIndex === void 0 ? null : _ref10$regionIndex,
      _ref10$isMultiregionS = _ref10.isMultiregionSearch,
      isMultiregionSearch = _ref10$isMultiregionS === void 0 ? false : _ref10$isMultiregionS,
      _ref10$isRegionInMult = _ref10.isRegionInMultiRegionSelection,
      isRegionInMultiRegionSelection = _ref10$isRegionInMult === void 0 ? false : _ref10$isRegionInMult,
      _ref10$eventName = _ref10.eventName,
      eventName = _ref10$eventName === void 0 ? '' : _ref10$eventName,
      _ref10$clickstreamTri = _ref10.clickstreamTriggerOverrides,
      clickstreamTriggerOverrides = _ref10$clickstreamTri === void 0 ? null : _ref10$clickstreamTri,
      _ref10$clickstreamDec = _ref10.clickstreamDecorators,
      clickstreamDecorators = _ref10$clickstreamDec === void 0 ? null : _ref10$clickstreamDec,
      _ref10$screenName = _ref10.screenName,
      screenName = _ref10$screenName === void 0 ? 'sxp' : _ref10$screenName,
      _ref10$allowEmptyInit = _ref10.allowEmptyInitialClickstreamBlocks,
      allowEmptyInitialClickstreamBlocks = _ref10$allowEmptyInit === void 0 ? false : _ref10$allowEmptyInit,
      _ref10$isPointRegionS = _ref10.isPointRegionSelected,
      isPointRegionSelected = _ref10$isPointRegionS === void 0 ? false : _ref10$isPointRegionS;
  yieldWrapper(function () {
    return new Promise(function ($return, $error) {
      var newLaneEventProperties, data;
      newLaneEventProperties = {};

      if (isInitialized()) {
        var $Try_1_Finally = function ($Try_1_Exit) {
          return function ($Try_1_Value) {
            try {
              if (data && !isEmpty(data)) {
                newLaneEventProperties = data;
              }

              if (allowEmptyInitialClickstreamBlocks || Object.keys(newLaneEventProperties).length) {
                if (eventName === EventNames.removeMultiRegion || eventName === EventNames.removeAllMultiRegion) {
                  newLaneEventProperties = getRemoveRegionEventData(eventName, newLaneEventProperties, regionIndex);
                } else if (eventName === EventNames.searchBoxFocus) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, getNewLaneProperties(eventName));
                } else {
                  newLaneEventProperties = getAddRegionEventData(autocompleteResult, newLaneEventProperties, isMultiregionSearch, isRegionInMultiRegionSelection, isPointRegionSelected);
                }

                if (clickstreamTriggerOverrides) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                    clickstream_trigger: Object.assign({}, newLaneEventProperties.clickstream_trigger, clickstreamTriggerOverrides)
                  });
                }

                if (clickstreamDecorators) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, clickstreamDecorators);
                }

                track(trackEventProp, {
                  newLaneEvent: newLaneEventProperties
                });
              } else {
                track(trackEventProp);
              }

              return $Try_1_Exit && $Try_1_Exit.call(this, $Try_1_Value);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this);
        }.bind(this);

        var $Try_1_Post = function () {
          try {
            return $If_5.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);

        var $Try_1_Catch = function ($exception_2) {
          try {
            throw $exception_2;
          } catch ($boundEx) {
            return $Try_1_Finally($error)($boundEx);
          }
        };

        try {
          if (!allowEmptyInitialClickstreamBlocks && screenName) {
            return Promise.resolve(datalayer.getAll({
              screenName: screenName
            })).then(function ($await_9) {
              try {
                data = $await_9;
                return $If_6.call(this);
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }.bind(this), $Try_1_Catch);
          }

          function $If_6() {
            return $Try_1_Finally($Try_1_Post)();
          }

          return $If_6.call(this);
        } catch ($exception_2) {
          $Try_1_Catch($exception_2);
        }
      } else {
        track(trackEventProp);
        return $If_5.call(this);
      }

      function $If_5() {
        return $return();
      }
    });
  });
};
var fireEvent = function fireEvent(_ref11) {
  var eventName = _ref11.eventName,
      _ref11$suggestion = _ref11.suggestion,
      suggestion = _ref11$suggestion === void 0 ? null : _ref11$suggestion,
      _ref11$clickstreamTri = _ref11.clickstreamTriggerOverrides,
      clickstreamTriggerOverrides = _ref11$clickstreamTri === void 0 ? null : _ref11$clickstreamTri,
      _ref11$clickstreamDec = _ref11.clickstreamDecorators,
      clickstreamDecorators = _ref11$clickstreamDec === void 0 ? null : _ref11$clickstreamDec,
      _ref11$screenName = _ref11.screenName,
      screenName = _ref11$screenName === void 0 ? 'sxp' : _ref11$screenName,
      _ref11$allowEmptyInit = _ref11.allowEmptyInitialClickstreamBlocks,
      allowEmptyInitialClickstreamBlocks = _ref11$allowEmptyInit === void 0 ? false : _ref11$allowEmptyInit;
  yieldWrapper(function () {
    return new Promise(function ($return, $error) {
      var newLaneEventProperties, data;
      newLaneEventProperties = {};

      if (isInitialized()) {
        var $Try_3_Finally = function ($Try_3_Exit) {
          return function ($Try_3_Value) {
            try {
              if (data && !isEmpty(data)) {
                newLaneEventProperties = data;
              }

              if (allowEmptyInitialClickstreamBlocks || Object.keys(newLaneEventProperties).length) {
                newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                  semantic: getSemanticBlock(eventName),
                  clickstream_trigger: getClickstreamBlock(eventName),
                  envelope: getEnvelopeBlock(eventName)
                });

                if (suggestion && eventName === EventNames.queryUnderstandingResponse) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                    search_filter: getUpdatedSearchFilterBlock(suggestion, newLaneEventProperties),
                    search_map: getUpdatedSearchMapBlock(suggestion, newLaneEventProperties)
                  });
                }

                if (clickstreamTriggerOverrides) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                    clickstream_trigger: Object.assign({}, newLaneEventProperties.clickstream_trigger, clickstreamTriggerOverrides)
                  });
                }

                if (clickstreamDecorators) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, clickstreamDecorators);
                }

                event(newLaneEventProperties);
              }

              return $Try_3_Exit && $Try_3_Exit.call(this, $Try_3_Value);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this);
        }.bind(this);

        var $Try_3_Post = function () {
          try {
            return $If_7.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);

        var $Try_3_Catch = function ($exception_4) {
          try {
            throw $exception_4;
          } catch ($boundEx) {
            return $Try_3_Finally($error)($boundEx);
          }
        };

        try {
          if (!allowEmptyInitialClickstreamBlocks && screenName) {
            return Promise.resolve(datalayer.getAll({
              screenName: screenName
            })).then(function ($await_10) {
              try {
                data = $await_10;
                return $If_8.call(this);
              } catch ($boundEx) {
                return $Try_3_Catch($boundEx);
              }
            }.bind(this), $Try_3_Catch);
          }

          function $If_8() {
            return $Try_3_Finally($Try_3_Post)();
          }

          return $If_8.call(this);
        } catch ($exception_4) {
          $Try_3_Catch($exception_4);
        }
      }

      function $If_7() {
        return $return();
      }

      return $If_7.call(this);
    });
  });
};

var defaultPlatformConfig = {
  DESKTOP: {
    placeholderText: 'Enter an address, neighborhood, city, or ZIP code'
  },
  DESKTOP_RESULTS_PAGE: {
    placeholderText: 'Address, neighborhood, city, ZIP'
  },
  MOBILE: {
    placeholderText: 'Address, neighborhood, city, ZIP',
    supportsLocationIcon: true
  },
  MOBILE_RESULTS_PAGE: {
    placeholderText: 'Address, neighborhood, city, ZIP',
    supportsLocationIcon: true
  }
};

var _jsxFileName$3 = "/builds/zillow/searchxp/static-search-box/src/LocationAccessButton.jsx";

var LocationAccessButton =
/*#__PURE__*/
function (_Component) {
  _inheritsLoose(LocationAccessButton, _Component);

  function LocationAccessButton(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this._requestLocationAccess = _this._requestLocationAccess.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = LocationAccessButton.prototype;

  _proto._requestLocationAccess = function _requestLocationAccess(e) {
    e.preventDefault();

    if (global.navigator && global.navigator.geolocation) {
      global.navigator.geolocation.getCurrentPosition(this.props.onLocationAccessGranted, this.props.onLocationAccessFailed);
    } else {
      console.warn('No location object available');
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props = this.props,
        isCurrentLocationSearch = _this$props.isCurrentLocationSearch,
        isUserPositionInMapBounds = _this$props.isUserPositionInMapBounds,
        onReCenter = _this$props.onReCenter,
        enableNaturalLanguageSearch = _this$props.enableNaturalLanguageSearch,
        size = _this$props.size,
        padding = _this$props.padding; // eslint-disable-next-line no-confusing-arrow

    var LocationOutlineIcon = function LocationOutlineIcon(props) {
      return enableNaturalLanguageSearch ?
      /*#__PURE__*/
      React.createElement(IconLocationArrowOutline, _extends({}, props, {
        __self: _this2,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 45,
          columnNumber: 17
        }
      })) :
      /*#__PURE__*/
      React.createElement(IconLocationOutline, _extends({}, props, {
        __self: _this2,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 47,
          columnNumber: 17
        }
      }));
    }; // eslint-disable-next-line no-confusing-arrow


    var LocationIcon = function LocationIcon(props) {
      return enableNaturalLanguageSearch ?
      /*#__PURE__*/
      React.createElement(IconLocationArrow, _extends({}, props, {
        __self: _this2,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 52,
          columnNumber: 17
        }
      })) :
      /*#__PURE__*/
      React.createElement(IconLocation, _extends({}, props, {
        __self: _this2,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 54,
          columnNumber: 17
        }
      }));
    };

    return (
      /*#__PURE__*/
      React.createElement(IconButton, {
        title: "Use your location",
        onMouseDown: !isCurrentLocationSearch ? this._requestLocationAccess : onReCenter,
        icon: isCurrentLocationSearch && !isUserPositionInMapBounds ?
        /*#__PURE__*/
        React.createElement(LocationOutlineIcon, {
          __self: this
        }) :
        /*#__PURE__*/
        React.createElement(LocationIcon, {
          __self: this
        }),
        size: size,
        padding: padding,
        type: "button",
        bare: true,
        __self: this,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 58,
          columnNumber: 13
        }
      })
    );
  };

  return LocationAccessButton;
}(Component);

LocationAccessButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Callback on user allowing permission location access.
   * @param {Object} position
   *     @param {Object} position.coords - { latitude, longitude, accuracy }
   */
  onLocationAccessGranted: PropTypes.func,

  /**
   * Callback on location request failing.
   * @param {PositionError} error - the reason for geolocation failure
   *
   */
  onLocationAccessFailed: PropTypes.func,

  /**
   * whether or a current location search is active
   */
  isCurrentLocationSearch: PropTypes.bool,

  /**
   * whether the user position is visible in the map
   */
  isUserPositionInMapBounds: PropTypes.bool,

  /**
   * update map bounds to center around user position
   */
  onReCenter: PropTypes.func,

  /**
   * If SXP_NATURAL_LANGUAGE_SEARCH is on.
   */
  enableNaturalLanguageSearch: PropTypes.bool,

  /**
   * Size of the button, passed through to IconButton
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),

  /**
   * Padding for the button, passed through to IconButton
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  padding: PropTypes.object
} : {};
LocationAccessButton.defaultProps = {
  onLocationAccessGranted: function onLocationAccessGranted() {},
  onLocationAccessFailed: function onLocationAccessFailed() {},
  isUserPositionInMapBounds: true,
  onReCenter: function onReCenter() {},
  isCurrentLocationSearch: false,
  size: BUTTON_SIZES.sm,
  padding: {}
};
var LocationAccessButton$1 = LocationAccessButton;

var _DOZ_MAP;

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports, zillow/@typescript-eslint/naming-convention */
var FOR_SALE_LISTING_STATUS_TYPES = ['FOR_SALE', 'FSBA', 'FSBO', 'AUCTION', 'COMINGSOON', 'FORECLOSURE', 'NEWCONSTRUCTION'];
var SHOW_ONLY_MAP = {
  SINGLE_STORY: 'singleStory',
  INCOME_RESTRICTED: 'onlyRentalIncomeRestricted',
  INUNIT_LAUNDRY: 'onlyRentalInUnitLaundry',
  ASSIGNED_PARKING: 'onlyRentalParkingAvailable',
  OPEN_HOUSES: 'isOpenHousesOnly',
  WATER_FRONT: 'isWaterfront',
  AIRCONDITIONING: 'hasAirConditioning',
  POOL: 'hasPool',
  ACCEPTS_RENTAL_APPLICATIONS: 'onlyRentalAcceptsApplications',
  GARAGE: 'hasGarage',
  APPROVED_VIRTUALTOUR: 'is3dHome'
};
var BASEMENT_MAP = {
  FINISHED: 'isBasementFinished',
  UNFINISHED: 'isBasementUnfinished'
};
var HOME_TYPE_MAP = {
  TOWNHOME: 'isTownhouse',
  LAND: 'isLotLand',
  MANUFACTURED: 'isManufactured',
  MULTIFAMILY: 'isMultiFamily',
  APARTMENT: 'isApartment',
  CONDO: 'isCondo',
  SINGLEFAMILY: 'isSingleFamily'
};
var LISTING_STATUS_MAP = {
  RECENTLY_SOLD: 'isRecentlySold',
  SOLD: 'isRecentlySold',
  PREFORECLOSURE: 'isPreMarketPreForeclosure',
  FORECLOSED: 'isPreMarketForeclosure',
  FOR_RENT: 'isForRent',
  FSBA: 'isForSaleByAgent',
  FSBO: 'isForSaleByOwner',
  AUCTION: 'isAuction',
  COMINGSOON: 'isComingSoon',
  FORECLOSURE: 'isForSaleForeclosure',
  NEWCONSTRUCTION: 'isNewConstruction'
};
var PETS_ALLOWED_MAP = {
  LARGEDOGS: 'onlyRentalLargeDogsAllowed',
  SMALLDOGS: 'onlyRentalSmallDogsAllowed',
  CATS: 'onlyRentalCatsAllowed'
};
var VIEW_MAP = {
  CITY: 'isCityView',
  PARK: 'isParkView',
  MOUNTAIN: 'isMountainView',
  WATER: 'isWaterView'
};
var ONE_DAY = 1;
var ONE_WEEK = 7;
var TWO_WEEKS = 14;
var ONE_MONTH = 30;
var THREE_MONTHS = 90;
var SIX_MONTHS = 182;
var ONE_YEAR = 365;
var TWO_YEARS = 730;
var THREE_YEARS = 1095;
var DOZ_MAP = (_DOZ_MAP = {}, _DOZ_MAP[ONE_DAY] = '1', _DOZ_MAP[ONE_WEEK] = '7', _DOZ_MAP[TWO_WEEKS] = '14', _DOZ_MAP[ONE_MONTH] = '30', _DOZ_MAP[THREE_MONTHS] = '90', _DOZ_MAP[SIX_MONTHS] = '6m', _DOZ_MAP[ONE_YEAR] = '12m', _DOZ_MAP[TWO_YEARS] = '24m', _DOZ_MAP[THREE_YEARS] = '36m', _DOZ_MAP);
var ONE_FOURTH_ACRE = 10890;
var HALF_ACRE = 21780;
var ONE_ACRE = 43560;
var TWO_ACRES = 87120;
var FIVE_ACRES = 217800;
var TEN_ACRES = 435600;
var TWENTY_ACRES = 871200;
var FIFTY_ACRES = 2178000;
var HUNDRED_ACRES = 4356000;

var roundToNearest = function roundToNearest(num, multiple, type) {
  var func = type === 'up' ? Math.ceil : Math.floor;
  return func(num / multiple) * multiple;
};

var getValueBounds = function getValueBounds(value, bounds) {
  var _bounds$find;

  return (_bounds$find = bounds.find(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        start = _ref2[0],
        end = _ref2[1];

    return start <= value && value <= end;
  })) !== null && _bounds$find !== void 0 ? _bounds$find : [];
};

var getAreaValue = function getAreaValue(value, bounds, type) {
  var min = bounds[0][0];
  var max = bounds[bounds.length - 1][1];

  if (value < min) {
    return type === 'min' ? null : min;
  }

  if (value > max) {
    return type === 'min' ? max : null;
  }

  var _getValueBounds = getValueBounds(value, bounds),
      _getValueBounds2 = _slicedToArray(_getValueBounds, 2),
      start = _getValueBounds2[0],
      end = _getValueBounds2[1];

  if (value === start || value === end) {
    return value;
  }

  return type === 'min' ? start : end;
};
/**
 * @param map - map of possible QU SearchFilter values to their corresponding filter ids
 * @returns SearchFilterState object with all filters set to false
 */


var getInitialSearchFilterState = function getInitialSearchFilterState(map) {
  return Object.values(map).reduce(function (acc, filterId) {
    acc[filterId] = {
      value: false
    };
    return acc;
  }, {});
};
/**
 * @param values - list of QU SearchFilter values
 * @param map - map of possible QU SearchFilter values to their corresponding filter ids
 * @returns SearchFilterState object with filters present in values set to true
 */


var getSearchFilterState = function getSearchFilterState(values, map) {
  return values.reduce(function (acc, searchFilterValue) {
    if (searchFilterValue && searchFilterValue in map) {
      var filterId = map[searchFilterValue];
      acc[filterId] = {
        value: true
      };
      return acc;
    }

    return acc;
  }, {});
};
/**
 * @param map - map of possible QU SearchFilter values to their corresponding SearchFilterBlock block fields
 * @returns SearchFilterBlock object with all filters set to false
 */


var getInitialSearchFilterBlock = function getInitialSearchFilterBlock(map) {
  return Object.values(map).reduce(function (acc, filterId) {
    acc[filterId] = false;
    return acc;
  }, {});
};
/**
 * @param values - list of QU SearchFilter values
 * @param map - map of possible QU SearchFilter values to their corresponding SearchFilterBlock block fields
 * @returns SearchFilterBlock object with filters present in values set to true
 */


var getSearchFilterBlock = function getSearchFilterBlock(values, map) {
  return values.reduce(function (acc, searchFilterValue) {
    if (searchFilterValue && searchFilterValue in map) {
      var filterId = map[searchFilterValue];
      acc[filterId] = true;
      return acc;
    }

    return acc;
  }, {});
};
/* eslint-disable zillow/@typescript-eslint/naming-convention */


var bathsMapper = {
  toSearchFilterState: function toSearchFilterState(baths) {
    if (baths === null || baths === void 0 ? void 0 : baths.min) {
      return {
        baths: {
          min: baths.min,
          max: null
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(baths) {
    var _baths$min;

    return {
      bath_cnt_txt: baths === null || baths === void 0 ? void 0 : (_baths$min = baths.min) === null || _baths$min === void 0 ? void 0 : _baths$min.toString()
    };
  }
};
var bedsMapper = {
  toSearchFilterState: function toSearchFilterState(beds) {
    if (beds) {
      var _beds$min = beds.min,
          min = _beds$min === void 0 ? null : _beds$min,
          _beds$max = beds.max,
          max = _beds$max === void 0 ? null : _beds$max;
      return {
        beds: {
          min: min,
          max: max
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(beds) {
    var _beds$min2, _beds$max2;

    return {
      bed_min_cnt_txt: beds === null || beds === void 0 ? void 0 : (_beds$min2 = beds.min) === null || _beds$min2 === void 0 ? void 0 : _beds$min2.toString(),
      bed_max_cnt_txt: beds === null || beds === void 0 ? void 0 : (_beds$max2 = beds.max) === null || _beds$max2 === void 0 ? void 0 : _beds$max2.toString()
    };
  }
};
var hoaFeesPerMonthMapper = {
  transform: function transform(hoaFeesPerMonth) {
    if (typeof (hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) !== 'number') {
      return hoaFeesPerMonth;
    }

    if (hoaFeesPerMonth.max > 1000) {
      return {
        max: null
      };
    }

    if (hoaFeesPerMonth.max < 75) {
      return {
        max: roundToNearest(hoaFeesPerMonth.max, 50, 'up')
      };
    }

    return {
      max: roundToNearest(hoaFeesPerMonth.max, 100, 'up')
    };
  },
  toSearchFilterState: function toSearchFilterState(hoaFeesPerMonth) {
    if (typeof (hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) === 'number' || (hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) === null) {
      var max = hoaFeesPerMonth.max;
      return {
        hoa: {
          min: null,
          max: max
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(hoaFeesPerMonth) {
    var _ref3;

    return {
      hoa_max_amt: (_ref3 = hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) !== null && _ref3 !== void 0 ? _ref3 : undefined
    };
  }
};
var livingAreaSqftMapper = {
  transform: function transform(livingAreaSqft) {
    if (livingAreaSqft) {
      var min = livingAreaSqft.min,
          max = livingAreaSqft.max;
      var bounds = [[500, 750], [750, 1000], [1000, 1250], [1250, 1500], [1500, 1750], [1750, 2000], [2000, 2250], [2250, 2500], [2500, 2750], [2750, 3000], [3000, 3500], [3500, 4000], [4000, 5000], [5000, 7500]];
      return {
        min: min ? getAreaValue(min, bounds, 'min') : null,
        max: max ? getAreaValue(max, bounds, 'max') : null
      };
    }

    return livingAreaSqft;
  },
  toSearchFilterState: function toSearchFilterState(livingAreaSqft) {
    if (livingAreaSqft) {
      var _livingAreaSqft$min = livingAreaSqft.min,
          min = _livingAreaSqft$min === void 0 ? null : _livingAreaSqft$min,
          _livingAreaSqft$max = livingAreaSqft.max,
          max = _livingAreaSqft$max === void 0 ? null : _livingAreaSqft$max;
      return {
        sqft: {
          min: min,
          max: max
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(livingAreaSqft) {
    var _ref4, _ref5;

    return {
      sqft_min_nb: (_ref4 = livingAreaSqft === null || livingAreaSqft === void 0 ? void 0 : livingAreaSqft.min) !== null && _ref4 !== void 0 ? _ref4 : undefined,
      sqft_max_nb: (_ref5 = livingAreaSqft === null || livingAreaSqft === void 0 ? void 0 : livingAreaSqft.max) !== null && _ref5 !== void 0 ? _ref5 : undefined
    };
  }
};
var lotSizeSqftMapper = {
  transform: function transform(lotSizeSqft) {
    if (lotSizeSqft) {
      var min = lotSizeSqft.min,
          max = lotSizeSqft.max;
      var bounds = [[1000, 2000], [2000, 3000], [4000, 5000], [5000, 7500], [7500, ONE_FOURTH_ACRE], [ONE_FOURTH_ACRE, HALF_ACRE], [HALF_ACRE, ONE_ACRE], [ONE_ACRE, TWO_ACRES], [TWO_ACRES, FIVE_ACRES], [FIVE_ACRES, TEN_ACRES], [TEN_ACRES, TWENTY_ACRES], [TWENTY_ACRES, FIFTY_ACRES], [FIFTY_ACRES, HUNDRED_ACRES]];
      return {
        min: min ? getAreaValue(min, bounds, 'min') : null,
        max: max ? getAreaValue(max, bounds, 'max') : null
      };
    }

    return lotSizeSqft;
  },
  toSearchFilterState: function toSearchFilterState(lotSizeSqft) {
    if (lotSizeSqft) {
      var _lotSizeSqft$min = lotSizeSqft.min,
          min = _lotSizeSqft$min === void 0 ? null : _lotSizeSqft$min,
          _lotSizeSqft$max = lotSizeSqft.max,
          max = _lotSizeSqft$max === void 0 ? null : _lotSizeSqft$max;
      return {
        lotSize: {
          min: min,
          max: max
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(lotSizeSqft) {
    var _ref6, _ref7;

    return {
      lot_size_sqft_min_nb: (_ref6 = lotSizeSqft === null || lotSizeSqft === void 0 ? void 0 : lotSizeSqft.min) !== null && _ref6 !== void 0 ? _ref6 : undefined,
      lot_size_sqft_max_nb: (_ref7 = lotSizeSqft === null || lotSizeSqft === void 0 ? void 0 : lotSizeSqft.max) !== null && _ref7 !== void 0 ? _ref7 : undefined
    };
  }
};
var parkingSpotsMapper = {
  toSearchFilterState: function toSearchFilterState(parkingSpots) {
    if (parkingSpots === null || parkingSpots === void 0 ? void 0 : parkingSpots.min) {
      return {
        parkingSpots: {
          min: parkingSpots.min,
          max: null
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(parkingSpots) {
    var _parkingSpots$min;

    return {
      parking_spot_txt: parkingSpots === null || parkingSpots === void 0 ? void 0 : (_parkingSpots$min = parkingSpots.min) === null || _parkingSpots$min === void 0 ? void 0 : _parkingSpots$min.toString()
    };
  }
};
var priceMapper = {
  toSearchFilterState: function toSearchFilterState(price) {
    if (price) {
      var _price$min = price.min,
          min = _price$min === void 0 ? null : _price$min,
          _price$max = price.max,
          max = _price$max === void 0 ? null : _price$max;
      return {
        price: {
          min: min,
          max: max
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(price) {
    var _ref8, _ref9;

    return {
      price_range_min_amt: (_ref8 = price === null || price === void 0 ? void 0 : price.min) !== null && _ref8 !== void 0 ? _ref8 : undefined,
      price_range_max_amt: (_ref9 = price === null || price === void 0 ? void 0 : price.max) !== null && _ref9 !== void 0 ? _ref9 : undefined
    };
  }
};
var yearBuiltMapper = {
  toSearchFilterState: function toSearchFilterState(yearBuilt) {
    if (yearBuilt) {
      var _yearBuilt$min = yearBuilt.min,
          min = _yearBuilt$min === void 0 ? null : _yearBuilt$min,
          _yearBuilt$max = yearBuilt.max,
          max = _yearBuilt$max === void 0 ? null : _yearBuilt$max;
      return {
        built: {
          min: min,
          max: max
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(yearBuilt) {
    var _ref10, _ref11;

    return {
      built_year_min_nb: (_ref10 = yearBuilt === null || yearBuilt === void 0 ? void 0 : yearBuilt.min) !== null && _ref10 !== void 0 ? _ref10 : undefined,
      built_year_max_nb: (_ref11 = yearBuilt === null || yearBuilt === void 0 ? void 0 : yearBuilt.max) !== null && _ref11 !== void 0 ? _ref11 : undefined
    };
  }
};
var searchRentalFiltersMapper = {
  toSearchFilterState: function toSearchFilterState(searchRentalFilters) {
    var _searchRentalFilters$4;

    // eslint-disable-next-line zillow/@typescript-eslint/init-declarations
    var searchFilterState = {};

    if (searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : searchRentalFilters.monthlyPayment) {
      var _searchRentalFilters$ = searchRentalFilters.monthlyPayment,
          _searchRentalFilters$2 = _searchRentalFilters$.min,
          min = _searchRentalFilters$2 === void 0 ? null : _searchRentalFilters$2,
          _searchRentalFilters$3 = _searchRentalFilters$.max,
          max = _searchRentalFilters$3 === void 0 ? null : _searchRentalFilters$3;
      searchFilterState.monthlyPayment = {
        min: min,
        max: max
      };
    }

    if (searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : (_searchRentalFilters$4 = searchRentalFilters.petsAllowed) === null || _searchRentalFilters$4 === void 0 ? void 0 : _searchRentalFilters$4.length) {
      var petsAllowed = searchRentalFilters.petsAllowed;
      searchFilterState = Object.assign({}, searchFilterState, getInitialSearchFilterState(PETS_ALLOWED_MAP), getSearchFilterState(petsAllowed, PETS_ALLOWED_MAP));
    }

    return searchFilterState;
  },
  toSearchFilterBlock: function toSearchFilterBlock(searchRentalFilters) {
    var mapMonthlyPayment = function mapMonthlyPayment() {
      var _ref12, _searchRentalFilters$5, _ref13, _searchRentalFilters$6;

      return {
        monthly_payment_min_amt: (_ref12 = searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : (_searchRentalFilters$5 = searchRentalFilters.monthlyPayment) === null || _searchRentalFilters$5 === void 0 ? void 0 : _searchRentalFilters$5.min) !== null && _ref12 !== void 0 ? _ref12 : undefined,
        monthly_payment_max_amt: (_ref13 = searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : (_searchRentalFilters$6 = searchRentalFilters.monthlyPayment) === null || _searchRentalFilters$6 === void 0 ? void 0 : _searchRentalFilters$6.max) !== null && _ref13 !== void 0 ? _ref13 : undefined
      };
    };

    var mapPetsAllowed = function mapPetsAllowed() {
      if (searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : searchRentalFilters.petsAllowed) {
        var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_PETS_ALLOWED_MAP);
        return Object.assign({}, searchFilterBlock, getSearchFilterBlock(searchRentalFilters.petsAllowed, CLICKSTREAM_PETS_ALLOWED_MAP));
      }

      return {};
    };

    return Object.assign({}, mapMonthlyPayment(), mapPetsAllowed());
  }
};
var searchSaleFiltersMapper = {
  transform: function transform(searchSaleFilters) {
    var _searchSaleFilters$da;

    if (searchSaleFilters === null || searchSaleFilters === void 0 ? void 0 : (_searchSaleFilters$da = searchSaleFilters.daysOnZillow) === null || _searchSaleFilters$da === void 0 ? void 0 : _searchSaleFilters$da.max) {
      var bounds = [[ONE_DAY, ONE_WEEK], [ONE_WEEK, TWO_WEEKS], [TWO_WEEKS, ONE_MONTH], [ONE_MONTH, THREE_MONTHS], [THREE_MONTHS, SIX_MONTHS], [SIX_MONTHS, ONE_YEAR], [ONE_YEAR, TWO_YEARS], [TWO_YEARS, THREE_YEARS]];

      var getDaysOnZillow = function getDaysOnZillow(value) {
        var _getValueBounds3 = getValueBounds(value, bounds),
            _getValueBounds4 = _slicedToArray(_getValueBounds3, 2),
            start = _getValueBounds4[0],
            end = _getValueBounds4[1];

        if (value === start || value === end) {
          return value;
        }

        return typeof start === 'number' && typeof end === 'number' ? end : null;
      };

      var dozValue = getDaysOnZillow(searchSaleFilters.daysOnZillow.max);
      return Object.assign({}, searchSaleFilters, {
        daysOnZillow: {
          min: null,
          max: dozValue
        }
      });
    }

    return searchSaleFilters;
  },
  toSearchFilterState: function toSearchFilterState(searchSaleFilters) {
    var _searchSaleFilters$da2;

    var max = searchSaleFilters === null || searchSaleFilters === void 0 ? void 0 : (_searchSaleFilters$da2 = searchSaleFilters.daysOnZillow) === null || _searchSaleFilters$da2 === void 0 ? void 0 : _searchSaleFilters$da2.max;

    if (max && max in DOZ_MAP) {
      return {
        doz: {
          value: DOZ_MAP[max]
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(searchSaleFilters) {
    var _ref14, _searchSaleFilters$da3;

    return {
      day_on_zillow_nb: (_ref14 = searchSaleFilters === null || searchSaleFilters === void 0 ? void 0 : (_searchSaleFilters$da3 = searchSaleFilters.daysOnZillow) === null || _searchSaleFilters$da3 === void 0 ? void 0 : _searchSaleFilters$da3.max) !== null && _ref14 !== void 0 ? _ref14 : undefined
    };
  }
};
var keywordsMapper = {
  toSearchFilterState: function toSearchFilterState(keywords) {
    if (keywords) {
      return {
        keywords: {
          value: keywords.toString()
        }
      };
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(keywords) {
    return {
      keywords_txt: keywords === null || keywords === void 0 ? void 0 : keywords.toString().replace(/\s*,\s*/g, ',').split(',')
    };
  }
};
var showOnlyTypeMapper = {
  toSearchFilterState: function toSearchFilterState(showOnlyType) {
    if (showOnlyType === null || showOnlyType === void 0 ? void 0 : showOnlyType.length) {
      return Object.assign({}, getInitialSearchFilterState(SHOW_ONLY_MAP), getSearchFilterState(showOnlyType, SHOW_ONLY_MAP));
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(showOnlyType) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_SHOW_ONLY_MAP);
    return showOnlyType ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(showOnlyType, CLICKSTREAM_SHOW_ONLY_MAP)) : searchFilterBlock;
  }
};
var basementStatusTypeMapper = {
  toSearchFilterState: function toSearchFilterState(basementStatusType) {
    if (basementStatusType === null || basementStatusType === void 0 ? void 0 : basementStatusType.length) {
      return Object.assign({}, getInitialSearchFilterState(BASEMENT_MAP), getSearchFilterState(basementStatusType, BASEMENT_MAP));
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(basementStatusType) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_BASEMENT_MAP);
    return basementStatusType ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(basementStatusType, CLICKSTREAM_BASEMENT_MAP)) : searchFilterBlock;
  }
};
var homeTypeMapper = {
  toSearchFilterState: function toSearchFilterState(homeType) {
    if (homeType === null || homeType === void 0 ? void 0 : homeType.length) {
      return Object.assign({}, getInitialSearchFilterState(HOME_TYPE_MAP), getSearchFilterState(homeType, HOME_TYPE_MAP), {
        isApartmentOrCondo: {
          value: homeType.includes('APARTMENT') || homeType.includes('CONDO')
        }
      });
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(homeType) {
    var searchFilterBlock = Object.assign({}, getInitialSearchFilterBlock(CLICKSTREAM_HOME_TYPE_MAP), {
      apt_or_condo_ind: false
    });
    return (homeType === null || homeType === void 0 ? void 0 : homeType.length) ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(homeType, CLICKSTREAM_HOME_TYPE_MAP), {
      apt_or_condo_ind: homeType.includes('APARTMENT') || homeType.includes('CONDO')
    }) : searchFilterBlock;
  }
};
var listingStatusTypeMapper = {
  transform: function transform(listingStatusType) {
    if (listingStatusType === null || listingStatusType === void 0 ? void 0 : listingStatusType.length) {
      var searchFilterList; // Set all the For Sale listing statuses. Also remove SOLD if it is included with FOR_SALE

      if (listingStatusType.includes('FOR_SALE')) {
        searchFilterList = _toConsumableArray(FOR_SALE_LISTING_STATUS_TYPES);
      } // If SOLD is included with any other listing status, remove SOLD
      else if (listingStatusType.includes('SOLD') && listingStatusType.length > 1) {
          searchFilterList = listingStatusType.filter(function (type) {
            return type !== 'SOLD';
          });
        } else {
          searchFilterList = listingStatusType;
        }

      return searchFilterList;
    }

    return listingStatusType;
  },
  toSearchFilterState: function toSearchFilterState(listingStatusType) {
    if (listingStatusType === null || listingStatusType === void 0 ? void 0 : listingStatusType.length) {
      return Object.assign({}, getInitialSearchFilterState(LISTING_STATUS_MAP), getSearchFilterState(listingStatusType, LISTING_STATUS_MAP));
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(listingStatusType) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_LISTING_STATUS_MAP);
    return listingStatusType ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(listingStatusType, CLICKSTREAM_LISTING_STATUS_MAP)) : searchFilterBlock;
  }
};
var viewMapper = {
  toSearchFilterState: function toSearchFilterState(view) {
    if (view === null || view === void 0 ? void 0 : view.length) {
      return Object.assign({}, getInitialSearchFilterState(VIEW_MAP), getSearchFilterState(view, VIEW_MAP));
    }

    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(view) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_VIEW_MAP);
    return view ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(view, CLICKSTREAM_VIEW_MAP)) : searchFilterBlock;
  }
};
var excludeTypesMapper = {
  toSearchFilterState: function toSearchFilterState(excludeTypes) {
    var searchFilterState = {};

    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('NO_HOA_DATA')) {
      searchFilterState.includeHomesWithNoHoaData = {
        value: false
      };
    }

    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('SENIOR_COMMUNITY')) {
      searchFilterState.ageRestricted55Plus = {
        value: 'e'
      };
    }

    return searchFilterState;
  },
  toSearchFilterBlock: function toSearchFilterBlock(excludeTypes) {
    var searchFilterBlock = {
      hoa_ind: true,
      age_55_plus_ind: true
    };

    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('NO_HOA_DATA')) {
      searchFilterBlock.hoa_ind = false;
    }

    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('SENIOR_COMMUNITY')) {
      searchFilterBlock.age_55_plus_ind = false;
    }

    return searchFilterBlock;
  }
};
/* eslint-enable zillow/@typescript-eslint/naming-convention */

var mappers = {
  baths: bathsMapper,
  beds: bedsMapper,
  hoaFeesPerMonth: hoaFeesPerMonthMapper,
  livingAreaSqft: livingAreaSqftMapper,
  lotSizeSqft: lotSizeSqftMapper,
  parkingSpots: parkingSpotsMapper,
  price: priceMapper,
  yearBuilt: yearBuiltMapper,
  searchRentalFilters: searchRentalFiltersMapper,
  searchSaleFilters: searchSaleFiltersMapper,
  keywords: keywordsMapper,
  showOnlyType: showOnlyTypeMapper,
  basementStatusType: basementStatusTypeMapper,
  homeType: homeTypeMapper,
  listingStatusType: listingStatusTypeMapper,
  view: viewMapper,
  excludeTypes: excludeTypesMapper
};
var mappers$1 = mappers;

/**
 * Maps ZG Graph SearchFilters object to SearchFilterState object
 * @param {SearchFilters} searchFilters - ZG Graph SearchFilters object returned from
 * the search-assistance subgraph
 * @param {string[]} regionIds - region ids associated with the search
 * @return {UpdatedFilters} Object containing SearchFilterState object used to set filters on the SRP and
 * SearchFilterBlock object sent in clickstream events
 */

var getUpdatedFilterData = function getUpdatedFilterData(searchFilters) {
  var filterData = {
    filterState: {},
    filterBlock: {
      // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
      changed_filter_fields: []
    }
  };

  if (!searchFilters) {
    return filterData;
  }

  Object.entries(searchFilters).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    if (key in mappers$1 && value !== null) {
      var _mapper$transform, _mapper$transform2;

      var mapper = mappers$1[key];
      var searchFilterState = flow((_mapper$transform = mapper.transform) !== null && _mapper$transform !== void 0 ? _mapper$transform : identity, mapper.toSearchFilterState)(value);
      var searchFilterBlock = flow((_mapper$transform2 = mapper.transform) !== null && _mapper$transform2 !== void 0 ? _mapper$transform2 : identity, mapper.toSearchFilterBlock)(value);
      Object.assign(filterData.filterState, searchFilterState);
      Object.assign(filterData.filterBlock, searchFilterBlock);
      Object.entries(searchFilterBlock).forEach(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 2),
            filterId = _ref4[0],
            filterBlockValue = _ref4[1];

        if (filterBlockValue !== undefined) {
          var _filterData$filterBlo;

          (_filterData$filterBlo = filterData.filterBlock.changed_filter_fields) === null || _filterData$filterBlo === void 0 ? void 0 : _filterData$filterBlo.push(filterId);
        }
      });
    }
  });
  return filterData;
};
/**
 * Checks if only simple filters were updated. Confirms every filter is a simple filter or is null (no change).
 * listingStatusType, homeType, and userSearchContext are considered "simple" filters.
 */

var isSimpleFilterChange = function isSimpleFilterChange(newFilters) {
  var simpleFilters = ['listingStatusType', 'homeType', 'userSearchContext'];
  return Object.entries(newFilters).every(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
        filter = _ref6[0],
        value = _ref6[1];

    // Filter value could be an object, if so, recurse through it
    if (_typeof(value) === 'object' && value !== null && !Array.isArray(value)) {
      return isSimpleFilterChange(value);
    }

    return simpleFilters.includes(filter) || value === null;
  });
};

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */
var useOnClickInsideOutside = function useOnClickInsideOutside(ref, onInsideHandler, onOutsideHandler) {
  useEffect(function () {
    var listener = function listener(_ref) {
      var target = _ref.target;

      if (ref.current) {
        if (ref.current.contains(target)) {
          onInsideHandler === null || onInsideHandler === void 0 ? void 0 : onInsideHandler(target);
        } else {
          onOutsideHandler === null || onOutsideHandler === void 0 ? void 0 : onOutsideHandler();
        }
      }
    };

    document.addEventListener('click', listener);
    return function () {
      document.removeEventListener('click', listener);
    };
  }, [ref, onOutsideHandler, onInsideHandler]);
};

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n    query getQueryUnderstandingResults(\n        $query: String!\n        $queryOptions: SearchAssistanceQueryOptions\n        $querySource: SearchAssistanceQuerySource = UNKNOWN\n        $resultType: [SearchAssistanceResultType]\n    ) {\n        searchAssistanceResult: zgsQueryUnderstandingRequest(\n            query: $query\n            queryOptions: $queryOptions\n            querySource: $querySource\n            resultType: $resultType\n        ) {\n            requestId\n            results {\n                __typename\n                id\n                ...RegionResultFields\n                ...SemanticResultFields\n                ...RentalCommunityResultFields\n            }\n        }\n    }\n    ", "\n    ", "\n    ", "\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}
var getQueryUnderstandingResults = gql(_templateObject$1(), REGION_RESULT_FIELDS, SEMANTIC_RESULT_FIELDS, RENTAL_COMMUNITY_RESULT_FIELDS);

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */
/**
 * Sets the listbox height to fill the remaining vertical screen below the search box.
 */

var useMaximizeListboxHeight = function useMaximizeListboxHeight(parentRef, paddingBottom) {
  if (paddingBottom === void 0) {
    paddingBottom = 16;
  }

  useEffect(function () {
    var callback = function callback() {
      var _parentRef$current;

      var listbox = ((_parentRef$current = parentRef.current) === null || _parentRef$current === void 0 ? void 0 : _parentRef$current.querySelector.bind(_parentRef$current))('[role="listbox"]');

      if (listbox) {
        window.requestAnimationFrame(function () {
          /**
           * Max height is 100vh minus the distance from the top of the screen to the top of the listbox, with some padding added
           */
          listbox.style.maxHeight = "calc(100vh - (" + listbox.getBoundingClientRect().top + "px + " + paddingBottom + "px))";
        });
      }
    };

    var observer = new MutationObserver(callback);

    if (parentRef.current) {
      observer.observe(parentRef.current, {
        childList: true,
        subtree: true
      });
    }

    return function () {
      observer.disconnect();
    };
  }, [parentRef, paddingBottom]);
};

var _this$2 = undefined,
    _jsxFileName$2 = "/builds/zillow/searchxp/static-search-box/src/SearchBox.jsx";
/**
 * @typedef {import('./util/suggestion').Suggestion} Suggestion
 */
// Workaround for SXP-12248

var categoryValuePattern$1 = /^\[[A-Z_]+\]/g; // Fix for the default icon padding
// increasng the height of the search box

var StyledSearchBoxAdornment$1 = styled(Adornment).withConfig({
  displayName: "SearchBox__StyledSearchBoxAdornment",
  componentId: "sxp-sb-8-100-7__rxevgz-0"
})(["padding:0 0 0 5px;border-radius:0 ", "px ", "px 0;button{border-radius:0 ", "px ", "px 0;}", ";"], token('radii.md'), token('radii.md'), token('radii.md'), token('radii.md'), function (_ref) {
  var shouldHideAdornment = _ref.shouldHideAdornment;
  return shouldHideAdornment && 'display: none;';
});
var StyledSearchIconWrapper = styled.span.withConfig({
  displayName: "SearchBox__StyledSearchIconWrapper",
  componentId: "sxp-sb-8-100-7__rxevgz-1"
})(["", " cursor:text !important;", ""], IconButtonMixin, function (_ref2) {
  var hideButton = _ref2.hideButton;
  return hideButton && 'display: none !important;';
}); // Remove the overlapping focus indicators

var StyledSearchBoxButton = styled(IconButton).withConfig({
  displayName: "SearchBox__StyledSearchBoxButton",
  componentId: "sxp-sb-8-100-7__rxevgz-2"
})(["&:focus{box-shadow:none;}"]);
var StyledClearButton = styled(IconButton).withConfig({
  displayName: "SearchBox__StyledClearButton",
  componentId: "sxp-sb-8-100-7__rxevgz-3"
})(["", ""], function (_ref3) {
  var hideButton = _ref3.hideButton;
  return hideButton && 'display: none !important;';
}); // The height of the Listbox is not configurable via the Combobox interface.
// We need fixed height ListBox across all viewport sizes and full width on narrow viewports.
// The useMaximizeListboxHeight hook is used to set the height of the Listbox.
// Updating the height via a prop here is less performant than setting an inline style.
// Width is set below

var StyledCombobox$1 = styled(Combobox).withConfig({
  displayName: "SearchBox__StyledCombobox",
  componentId: "sxp-sb-8-100-7__rxevgz-4"
})(["@media screen and (min-width:890px){&&& [role='listbox'],&&& [role='dialog']{", "}}@media screen and (max-width:889px){&&& [role='listbox'],&&& [role='dialog']{width:100vw !important;}}&&& [role='listbox'],&&& [role='dialog']{height:auto;z-index:100;}input,label{background-color:", ";border:none;}input{text-overflow:ellipsis;}"], function (_ref4) {
  var enableNaturalLanguageSearch = _ref4.enableNaturalLanguageSearch;
  return enableNaturalLanguageSearch && 'min-width: 540px;';
}, token('colors.backgroundWhite'));
var StyledForm$1 = styled(Form).withConfig({
  displayName: "SearchBox__StyledForm",
  componentId: "sxp-sb-8-100-7__rxevgz-5"
})(["border:1px solid;border-radius:", "px;border-color:", ";background-color:", ";&:active,&:hover,&:focus{border-color:", ";}"], token('radii.md'), token('colors.gray300'), token('colors.backgroundWhite'), token('colors.blue400'));
var StyledListboxOptionLabelText$1 = styled.span.withConfig({
  displayName: "SearchBox__StyledListboxOptionLabelText",
  componentId: "sxp-sb-8-100-7__rxevgz-6"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
var StyledListboxOptionLabel$1 = styled(ListboxOptionLabel).withConfig({
  displayName: "SearchBox__StyledListboxOptionLabel",
  componentId: "sxp-sb-8-100-7__rxevgz-7"
})(["display:flex;"]);
var StyledListboxOptionLabelGleam$1 = styled(Gleam).withConfig({
  displayName: "SearchBox__StyledListboxOptionLabelGleam",
  componentId: "sxp-sb-8-100-7__rxevgz-8"
})(["align-self:center;margin-left:auto;"]);

var createUniqueOptionValue$1 = function createUniqueOptionValue(displayName, category) {
  return "[" + category + "]" + displayName;
};

var getSuggestionCategoryIcon$1 = function getSuggestionCategoryIcon(category, enableNaturalLanguageSearch) {
  if (enableNaturalLanguageSearch === void 0) {
    enableNaturalLanguageSearch = false;
  }

  switch (category) {
    case SuggestionCategory.region:
      return (
        /*#__PURE__*/
        React.createElement(IconLocationOutline, {
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 184,
            columnNumber: 20
          }
        })
      );

    case SuggestionCategory.semantic:
      return (
        /*#__PURE__*/
        React.createElement(IconSearchOutline, {
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 186,
            columnNumber: 20
          }
        })
      );

    case SuggestionCategory.currentLocation:
      return enableNaturalLanguageSearch ?
      /*#__PURE__*/
      React.createElement(IconLocationOutline, {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 188,
          columnNumber: 50
        }
      }) :
      /*#__PURE__*/
      React.createElement(IconLocation, {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 188,
          columnNumber: 76
        }
      });

    case SuggestionCategory.recentSearch:
      return enableNaturalLanguageSearch ?
      /*#__PURE__*/
      React.createElement(IconClockOutline, {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 190,
          columnNumber: 50
        }
      }) :
      /*#__PURE__*/
      React.createElement(IconClock, {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 190,
          columnNumber: 73
        }
      });

    case SuggestionCategory.savedSearch:
      return enableNaturalLanguageSearch ?
      /*#__PURE__*/
      React.createElement(IconSavedSearchOutline, {
        "data-test": "saved-search-icon",
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 193,
          columnNumber: 17
        }
      }) :
      /*#__PURE__*/
      React.createElement(IconSavedSearch, {
        "data-test": "saved-search-icon",
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 195,
          columnNumber: 17
        }
      });

    case SuggestionCategory.savedHome:
    case SuggestionCategory.claimedHome:
      return (
        /*#__PURE__*/
        React.createElement(IconFavoriteHome, {
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 199,
            columnNumber: 20
          }
        })
      );

    default:
      return null;
  }
};

var convertRemoteSuggestionsToOptions = function convertRemoteSuggestionsToOptions(suggestions, enableNaturalLanguageSearch) {
  if (enableNaturalLanguageSearch === void 0) {
    enableNaturalLanguageSearch = false;
  }

  return suggestions.map(function (_ref5, index) {
    var displayName = _ref5.displayName,
        category = _ref5.category;
    var option = {
      index: index,
      label: displayName,
      // Workaround for SXP-12248
      value: createUniqueOptionValue$1(displayName, category)
    };

    if (enableNaturalLanguageSearch) {
      option.icon = getSuggestionCategoryIcon$1(category, enableNaturalLanguageSearch);
    }

    return option;
  });
};
var convertLocalSuggestionsToOptions = function convertLocalSuggestionsToOptions(suggestions, enableNaturalLanguageSearch) {
  var _optionGroups;

  if (enableNaturalLanguageSearch === void 0) {
    enableNaturalLanguageSearch = false;
  }

  if (!enableNaturalLanguageSearch) {
    return suggestions.map(function (_ref6, index) {
      var displayName = _ref6.displayName,
          category = _ref6.category,
          notificationCount = _ref6.notificationCount;
      return {
        index: index,
        label: displayName,
        // Workaround for SXP-12248
        value: createUniqueOptionValue$1(displayName, category),
        icon: getSuggestionCategoryIcon$1(category),
        notificationCount: notificationCount
      };
    });
  }

  var optionGroups = (_optionGroups = {}, _optionGroups[SuggestionCategory.currentLocation] = [{
    label: 'Current Location',
    visuallyHidden: true
  }], _optionGroups[SuggestionCategory.recentSearch] = [{
    label: 'Search History'
  }], _optionGroups[SuggestionCategory.semantic] = [{
    label: 'Suggested Searches'
  }], _optionGroups[SuggestionCategory.savedSearch] = [{
    label: 'Saved Searches'
  }], _optionGroups);
  suggestions.forEach(function (_ref7, index) {
    var displayName = _ref7.displayName,
        category = _ref7.category,
        notificationCount = _ref7.notificationCount;

    if (optionGroups[category]) {
      optionGroups[category].push({
        index: index,
        label: displayName,
        // Workaround for SXP-12248
        value: createUniqueOptionValue$1(displayName, category),
        icon: getSuggestionCategoryIcon$1(category, enableNaturalLanguageSearch),
        notificationCount: notificationCount
      });
    }
  });
  return Object.values(optionGroups).filter(function (options) {
    return options.length > 1;
  });
}; // eslint-disable-next-line no-confusing-arrow

var getSearchBoxSize = function getSearchBoxSize(size) {
  return size === 'xl' ? 'lg' : size;
};

var SearchBox$1 = function SearchBox(_ref8) {
  var abKey = _ref8.abKey,
      allowEmptyInitialClickstreamBlocks = _ref8.allowEmptyInitialClickstreamBlocks,
      centroid = _ref8.centroid,
      className = _ref8.className,
      clickstreamDecorators = _ref8.clickstreamDecorators,
      clickstreamTriggerOverrides = _ref8.clickstreamTriggerOverrides,
      clientId = _ref8.clientId,
      disableCurrentLocation = _ref8.disableCurrentLocation,
      enableNaturalLanguageSearch = _ref8.enableNaturalLanguageSearch,
      fetchSuggestions = _ref8.fetchResults,
      fetchSuggestionsError = _ref8.fetchResultsError,
      filterSuggestions = _ref8.filterResults,
      gaEventsConfig = _ref8.gaEventsConfig,
      handleChangeCallback = _ref8.handleChangeCallback,
      handleSubmit = _ref8.handleSubmit,
      initSearchTerm = _ref8.initSearchTerm,
      inputRef = _ref8.inputRef,
      isCurrentLocationSearch = _ref8.isCurrentLocationSearch,
      isUserPositionInMapBounds = _ref8.isUserPositionInMapBounds,
      onClearButtonClick = _ref8.onClearButtonClick,
      onReCenter = _ref8.onReCenter,
      platform = _ref8.platform,
      platformConfig = _ref8.platformConfig,
      recentSearchLocalStorageKey = _ref8.recentSearchLocalStorageKey,
      regionAddressAutocompleteURL = _ref8.regionAddressAutocompleteURL,
      resultTypes = _ref8.resultTypes,
      searchBoxOnPopState = _ref8.searchBoxOnPopState,
      searchBoxDidFocusInput = _ref8.searchBoxDidFocusInput,
      searchBoxDidSelectOption = _ref8.searchBoxDidSelectOption,
      shouldShowSavedHomes = _ref8.shouldShowSavedHomes,
      shouldShowSavedSearches = _ref8.shouldShowSavedSearches,
      showAdornments = _ref8.showAdornments,
      size = _ref8.size,
      trackSearchBoxInputFocus = _ref8.trackSearchBoxInputFocus,
      userSearchContext = _ref8.userSearchContext,
      zgGraphClient = _ref8.zgGraphClient;

  var _useState = useState('local'),
      _useState2 = _slicedToArray(_useState, 2),
      autocompleteSource = _useState2[0],
      setAutocompleteSource = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      autocompleteSuggestions = _useState4[0],
      setAutocompleteSuggestions = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      shouldUpdateSuggestions = _useState6[0],
      setShouldUpdateSuggestions = _useState6[1];

  var _useState7 = useState({}),
      _useState8 = _slicedToArray(_useState7, 2),
      location = _useState8[0],
      setLocation = _useState8[1];

  var _useState9 = useState(initSearchTerm),
      _useState10 = _slicedToArray(_useState9, 2),
      currentSearchTerm = _useState10[0],
      setCurrentSearchTerm = _useState10[1];

  var _useState11 = useState(true),
      _useState12 = _slicedToArray(_useState11, 2),
      showLocationIcon = _useState12[0],
      setShowLocationIcon = _useState12[1];

  var _useState13 = useState(null),
      _useState14 = _slicedToArray(_useState13, 2),
      screenReaderMessage = _useState14[0],
      setScreenReaderMessage = _useState14[1];

  var _useState15 = useState(false),
      _useState16 = _slicedToArray(_useState15, 2),
      isSearchBoxFocused = _useState16[0],
      setIsSearchBoxFocused = _useState16[1];

  var screenReaderTimeoutRef = useRef();
  var comboboxRef = useRef();
  var interactionIdRef = useRef();
  var autocompleteRequestIdRef = useRef();
  var formRef = useRef();
  var shouldFireDismissSearchEventRef = useRef(true);
  var clearButtonRef = useRef();
  var yieldWrapper = useYieldCallback(function (callback) {
    callback();
  });
  var yieldHandleSubmit = useYieldCallback(handleSubmit);
  var onContainerClick = useCallback(function (eventTarget) {
    // Don't focus on input if the clear button is clicked
    if (enableNaturalLanguageSearch && !eventTarget.contains(clearButtonRef.current)) {
      setIsSearchBoxFocused(true);
    }
  }, [enableNaturalLanguageSearch, clearButtonRef]);
  var onContainerClickOutside = useCallback(function () {
    var _formRef$current;

    if (enableNaturalLanguageSearch && document.activeElement !== ((_formRef$current = formRef.current) === null || _formRef$current === void 0 ? void 0 : _formRef$current.querySelector('input'))) {
      setIsSearchBoxFocused(false);
    }
  }, [enableNaturalLanguageSearch]);
  useOnClickInsideOutside(formRef, onContainerClick, onContainerClickOutside);
  useMaximizeListboxHeight(formRef);

  var getIsLocalQuery = function getIsLocalQuery(results) {
    return results.length && results[0].category === SuggestionCategory.currentLocation;
  };

  var getDisplayTextFromOptionValue = function getDisplayTextFromOptionValue(value) {
    return value.replace(categoryValuePattern$1, '');
  };

  var getResultTypes = function getResultTypes() {
    return resultTypes !== null && resultTypes !== void 0 ? resultTypes : enableNaturalLanguageSearch ? ['SEMANTIC_REGIONS', 'REGIONS', 'FORSALE', 'RENTALS', 'SOLD'] : undefined;
  };

  var fetchSuggestionsWrapper = function fetchSuggestionsWrapper(query, reason) {
    // autocomplete config only used for default fetch
    var autocompleteConfig = {
      abKey: abKey,
      clientId: clientId,
      centroid: centroid,
      regionAddressAutocompleteURL: regionAddressAutocompleteURL,
      recentSearchLocalStorageKey: recentSearchLocalStorageKey,
      fetchSavedHomes: shouldShowSavedHomes,
      fetchSavedSearches: shouldShowSavedSearches,
      disableCurrentLocation: disableCurrentLocation,
      resultTypes: getResultTypes(),
      userSearchContext: userSearchContext
    };
    fetchSuggestions(query, location, autocompleteConfig, reason, undefined, enableNaturalLanguageSearch ? zgGraphClient : undefined).then(function (data) {
      var _data$suggestions;

      var suggestions = (_data$suggestions = data.suggestions) !== null && _data$suggestions !== void 0 ? _data$suggestions : data;
      var filteredSuggestions = filterSuggestions(suggestions, false, enableNaturalLanguageSearch);
      setAutocompleteSource(getIsLocalQuery(filteredSuggestions) ? 'local' : 'remote');
      setAutocompleteSuggestions(filteredSuggestions);

      if (data.requestId) {
        autocompleteRequestIdRef.current = data.requestId;
      }
    }).catch(fetchSuggestionsError);
  };

  var delayedFetch = useDebouncedCallback(fetchSuggestionsWrapper, AUTOCOMPLETE_REQUEST_DELAY); // update search term, update suggestions if search term changes

  useEffect(function () {
    setCurrentSearchTerm(initSearchTerm);

    if (enableNaturalLanguageSearch) {
      setShouldUpdateSuggestions(true);
    }
  }, [initSearchTerm, enableNaturalLanguageSearch]); // update suggestions if userSearchContext changes

  useEffect(function () {
    if (enableNaturalLanguageSearch) {
      setShouldUpdateSuggestions(true);
    }
  }, [userSearchContext, enableNaturalLanguageSearch]); // update suggestions if shouldShowSavedHomes or shouldShowSavedSearches changes

  useEffect(function () {
    setShouldUpdateSuggestions(true);
  }, [shouldShowSavedHomes, shouldShowSavedSearches]);
  useEffect(function () {
    if (shouldUpdateSuggestions) {
      var reason = enableNaturalLanguageSearch && currentSearchTerm.displayName.length > 0 ? FREEFORM : INIT_LOCAL_RESULTS;

      if (!enableNaturalLanguageSearch || isSearchBoxFocused) {
        fetchSuggestionsWrapper(currentSearchTerm.displayName, reason);
        setShouldUpdateSuggestions(false);
      } else {
        // wait to fetch suggestions until focus.
        // clear suggestions for now so old suggestions don't show.
        setAutocompleteSuggestions([]);
      }
    }
  }, [shouldUpdateSuggestions, isSearchBoxFocused, enableNaturalLanguageSearch]);

  var alertUserToNewSuggestions = function alertUserToNewSuggestions() {
    setScreenReaderMessage(autocompleteSuggestions.length + " suggested results are available. Use up and down arrow keys to navigate");
    clearTimeout(screenReaderTimeoutRef.current);
    screenReaderTimeoutRef.current = setTimeout(function () {
      setScreenReaderMessage(null);
    }, SCREEN_READER_MESSAGE_TIMEOUT);
    return function () {
      return clearTimeout(screenReaderTimeoutRef.current);
    };
  };

  var onPopState = function onPopState() {
    var listener = function listener() {
      return searchBoxOnPopState === null || searchBoxOnPopState === void 0 ? void 0 : searchBoxOnPopState(comboboxRef);
    };

    window.addEventListener('popstate', listener);
    return function () {
      return window.removeEventListener('popstate', listener);
    };
  };

  useEffect(onPopState, [searchBoxOnPopState]);
  useEffect(alertUserToNewSuggestions, [autocompleteSuggestions]);
  /**
   * User clicked Current Location or Location icon and granted access
   */

  var onLocationAccessGranted = function onLocationAccessGranted(position) {
    var updatedSearchTerm = {
      displayName: 'Current Location',
      category: SuggestionCategory.currentLocation
    };
    setLocation(position);
    setCurrentSearchTerm(updatedSearchTerm);
    setShowLocationIcon(true);
    yieldHandleSubmit(updatedSearchTerm, {
      accuracy: position.coords.accuracy,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    });
  };

  var onLocationAccessFailed = function onLocationAccessFailed() {
    setShowLocationIcon(false); // eslint-disable-next-line no-alert

    alert('There is no location support on this device or it is disabled. Please check your settings.');
  };

  var locationSubmit = function locationSubmit(accessGranted, accessFailed) {
    var _global$navigator;

    if (accessGranted === void 0) {
      accessGranted = onLocationAccessGranted;
    }

    if (accessFailed === void 0) {
      accessFailed = onLocationAccessFailed;
    }

    if ((_global$navigator = global.navigator) === null || _global$navigator === void 0 ? void 0 : _global$navigator.geolocation) {
      global.navigator.geolocation.getCurrentPosition(accessGranted, accessFailed);
    } else {
      console.warn('No location object available');
    }
  };
  /**
   * User submitted a NLS near me search
   * @param {Object} searchSelection
   * @param {number} viewLatitudeDelta
   */


  var nearMeSubmit = function nearMeSubmit(searchSelection, viewLatitudeDelta) {
    var onAccess = function onAccess(position) {
      setLocation(position);
      setShowLocationIcon(true);
      var _position$coords = position.coords,
          latitude = _position$coords.latitude,
          longitude = _position$coords.longitude;
      var userLocation = {
        latitude: latitude,
        longitude: longitude,
        mapBounds: calculateMapBounds(latitude, longitude, viewLatitudeDelta)
      };
      yieldHandleSubmit(searchSelection, userLocation);
    };

    locationSubmit(onAccess);
  };

  var isLocationSet = function isLocationSet() {
    return location.accuracy && location.latitude && location.longitude;
  }; // We restrict highlighting to the autocomplete suggestions
  // eslint-disable-next-line no-confusing-arrow


  var getHighlightPattern = useCallback(function (inputValue) {
    if (autocompleteSource === 'remote' && inputValue.trim()) {
      return inputValue;
    }

    return null;
  }, [autocompleteSource]);

  function calculateMapBounds(latitude, longitude, latitudeDelta) {
    // Calculate the longitude delta based on the latitude delta and the center point latitude
    var longitudeDelta = latitudeDelta / Math.cos(latitude * Math.PI / 180); // Calculate and return the top, bottom, left, and right bounds of the map

    return {
      north: latitude + latitudeDelta / 2,
      south: latitude - latitudeDelta / 2,
      west: longitude - longitudeDelta / 2,
      east: longitude + longitudeDelta / 2
    };
  }
  /**
   * @param {Suggestion} suggestion
   * @param {*} newFilterState
   */


  var submitSuggestion = function submitSuggestion(suggestion, newFilterState) {
    var displayName = suggestion.displayName,
        data = suggestion.data; // send region info if a region is selected

    if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
      RecentSearchClient.addToRecentSearches(suggestion);
      return yieldHandleSubmit({
        displayName: displayName,
        regions: [{
          regionId: data.regionId
        }],
        category: SuggestionCategory.region
      }, location);
    } // send NLS info if a semantic search is selected


    if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
      var _ref9, _data$regions;

      RecentSearchClient.addToRecentSearches(suggestion);
      var selection = {
        displayName: displayName,
        category: SuggestionCategory.semantic,
        regions: (_ref9 = (_data$regions = data.regions) === null || _data$regions === void 0 ? void 0 : _data$regions.map(function (entry) {
          return {
            regionId: entry.id
          };
        })) !== null && _ref9 !== void 0 ? _ref9 : [],
        filters: newFilterState,
        queryResolutionStatus: data.queryResolutionStatus,
        isSemanticSearchResult: true,
        isSimpleFilterChange: isSimpleFilterChange(suggestion.data.filters)
      };

      if (data.nearMeSearch && data.viewLatitudeDelta) {
        return nearMeSubmit(selection, data.viewLatitudeDelta);
      }

      return yieldHandleSubmit(selection);
    } // handle other suggestions: address, saved search


    return yieldHandleSubmit(suggestion, location);
  };

  var fireClickEvent = function fireClickEvent(suggestion, suggestionIndex) {
    var categoryTrackingConfig = trackingConfigsByCategory[suggestion.category];
    var getSearchBoxClickLabel = categoryTrackingConfig && categoryTrackingConfig.getSearchBoxClickLabel;

    if (typeof getSearchBoxClickLabel !== 'function') {
      console.error("No ga click event configured for search term with category " + suggestion.category);
    } else {
      var trackEventProp = {
        category: gaEventsConfig.category,
        action: gaEventsConfig.getClickActionName(suggestion.category),
        label: getSearchBoxClickLabel({
          chosenSuggestion: suggestion,
          allSuggestions: autocompleteSuggestions,
          chosenIndex: suggestionIndex + 1,
          // analytics expects a 1-indexed index
          searchBoxValue: currentSearchTerm.displayName
        })
      };
      fireTrackEvent({
        trackEventProp: trackEventProp,
        autocompleteResult: suggestion,
        clickstreamTriggerOverrides: clickstreamTriggerOverrides,
        clickstreamDecorators: clickstreamDecorators,
        allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
      });
    }
  };

  var fireFocusEvent = function fireFocusEvent() {
    if (typeof trackSearchBoxInputFocus === 'function') {
      yieldWrapper(function () {
        return trackSearchBoxInputFocus();
      });
    } else {
      fireTrackEvent({
        trackEventProp: {
          category: 'searchbox',
          action: 'focus',
          label: 'searchpage'
        },
        eventName: EventNames.searchBoxFocus,
        clickstreamTriggerOverrides: clickstreamTriggerOverrides,
        clickstreamDecorators: clickstreamDecorators,
        allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
      });
    }
  };
  /**
   * @param {Suggestion} suggestion
   * @param {number} suggestionIndex
   */


  var submitSearch = function submitSearch(suggestion, suggestionIndex) {
    return new Promise(function ($return, $error) {
      var rawValue, _suggestion$displayNa, wasAutocompleteSearch, _suggestion$data, _getUpdatedFilterData, filterState, filterBlock, getSelectionType, metadata, _ref11, _quSuggestion$data, selectionType, rawSearchQuery, _ref10, suggestions, requestId, requestError, quSuggestion, _getUpdatedFilterData2, _filterState, _filterBlock, _metadata, _quSuggestion$data2;

      // do not fire dismiss search event if the user is submitting a search
      shouldFireDismissSearchEventRef.current = false;
      rawValue = currentSearchTerm.displayName;
      searchBoxDidSelectOption === null || searchBoxDidSelectOption === void 0 ? void 0 : searchBoxDidSelectOption(comboboxRef);
      fireClickEvent(suggestion, suggestionIndex);
      setCurrentSearchTerm(suggestion); // Current location row should set location before handleSubmit

      if (isCurrentLocationSuggestion(suggestion) && !isLocationSet()) {
        locationSubmit();
        return $If_1.call(this);
      } else {
        if (enableNaturalLanguageSearch) {
          if ((suggestion === null || suggestion === void 0 ? void 0 : (_suggestion$displayNa = suggestion.displayName) === null || _suggestion$displayNa === void 0 ? void 0 : _suggestion$displayNa.length) === 0) {
            return $return();
          }

          wasAutocompleteSearch = suggestionIndex !== -1;

          /**
           * Submit search using the suggestion selected from autocomplete
           * Applies to all autocomplete selections that aren't recent semantic searches
           * Recent semantic searches require a request to QU as they use the current search context
           */
          if (wasAutocompleteSearch && !isRecentSemanticSuggestion(suggestion)) {
            _getUpdatedFilterData = getUpdatedFilterData(suggestion === null || suggestion === void 0 ? void 0 : (_suggestion$data = suggestion.data) === null || _suggestion$data === void 0 ? void 0 : _suggestion$data.filters), filterState = _getUpdatedFilterData.filterState, filterBlock = _getUpdatedFilterData.filterBlock;

            getSelectionType = function getSelectionType() {
              if (isRecentSearch(suggestion)) {
                return 'RECENT';
              }

              if (isSavedSearchSuggestion(suggestion)) {
                return 'SAVED_SEARCH';
              }

              if (autocompleteSource === 'local' && isSemanticSuggestion(suggestion)) {
                return 'SUGGESTED';
              }

              return 'AUTOCOMPLETE';
            };

            metadata = {
              interactionId: interactionIdRef === null || interactionIdRef === void 0 ? void 0 : interactionIdRef.current,
              requestId: autocompleteRequestIdRef === null || autocompleteRequestIdRef === void 0 ? void 0 : autocompleteRequestIdRef.current,
              selectionType: getSelectionType(),
              selectionIndex: suggestionIndex,
              rawValue: rawValue,
              searchFilterBlock: filterBlock
            };
            fireEvent({
              eventName: EventNames.queryUnderstandingResponse,
              suggestion: Object.assign({}, suggestion, {
                metadata: metadata
              }),
              clickstreamTriggerOverrides: clickstreamTriggerOverrides,
              clickstreamDecorators: clickstreamDecorators,
              allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
            });
            submitSuggestion(suggestion, filterState);

            if ((inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) instanceof HTMLElement) {
              inputRef.current.blur();
            }

            setShouldUpdateSuggestions(true);
            return $If_3.call(this);
          } // Submit search using QU suggestion for any manual or recent search
          else {
              selectionType = isRecentSearch(suggestion) ? 'RECENT' : 'MANUAL';
              rawSearchQuery = suggestion.displayName;
              return Promise.resolve(fetchZgGraphSuggestions(rawSearchQuery, {
                zgGraphClient: zgGraphClient,
                graphqlQuery: getQueryUnderstandingResults,
                autocompleteConfig: {
                  abKey: abKey,
                  centroid: centroid,
                  resultTypes: getResultTypes(),
                  userSearchContext: userSearchContext,
                  fetchLimit: 1
                },
                extraVariables: {
                  querySource: selectionType
                }
              })).then(function ($await_4) {
                try {
                  _ref10 = $await_4, suggestions = _ref10.suggestions, requestId = _ref10.requestId, requestError = _ref10.requestError;
                  quSuggestion = (_ref11 = suggestions === null || suggestions === void 0 ? void 0 : suggestions[0]) !== null && _ref11 !== void 0 ? _ref11 : {
                    category: SuggestionCategory.semantic,
                    displayName: null,
                    data: {
                      queryResolutionStatus: requestError ? 'ERROR' : 'FAILED'
                    }
                  };
                  _getUpdatedFilterData2 = getUpdatedFilterData(quSuggestion === null || quSuggestion === void 0 ? void 0 : (_quSuggestion$data = quSuggestion.data) === null || _quSuggestion$data === void 0 ? void 0 : _quSuggestion$data.filters), _filterState = _getUpdatedFilterData2.filterState, _filterBlock = _getUpdatedFilterData2.filterBlock;
                  _metadata = {
                    interactionId: interactionIdRef === null || interactionIdRef === void 0 ? void 0 : interactionIdRef.current,
                    requestId: requestId,
                    selectionType: selectionType,
                    selectionIndex: suggestionIndex,
                    rawValue: rawValue,
                    searchFilterBlock: _filterBlock
                  };
                  fireEvent({
                    eventName: EventNames.queryUnderstandingResponse,
                    suggestion: Object.assign({}, quSuggestion, {
                      metadata: _metadata
                    }),
                    clickstreamTriggerOverrides: clickstreamTriggerOverrides,
                    clickstreamDecorators: clickstreamDecorators,
                    allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
                  });

                  if (!quSuggestion.displayName) {
                    yieldHandleSubmit({
                      displayName: suggestion.displayName,
                      regions: [],
                      filters: {},
                      queryResolutionStatus: requestError ? 'ERROR' : 'FAILED',
                      category: SuggestionCategory.semantic,
                      isSimpleFilterChange: true
                    });
                  } else {
                    // If the query is partially resolved, we want to maintain the raw search query
                    // rather than replacing it with only the resolved portion
                    if ((quSuggestion === null || quSuggestion === void 0 ? void 0 : (_quSuggestion$data2 = quSuggestion.data) === null || _quSuggestion$data2 === void 0 ? void 0 : _quSuggestion$data2.queryResolutionStatus) === 'PARTIAL_RESOLUTION') {
                      quSuggestion.displayName = rawSearchQuery;
                    }

                    submitSuggestion(quSuggestion, _filterState);
                  }

                  if ((inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) instanceof HTMLElement) {
                    inputRef.current.blur();
                  }

                  if (wasAutocompleteSearch) {
                    setShouldUpdateSuggestions(true);
                  }

                  return $If_3.call(this);
                } catch ($boundEx) {
                  return $error($boundEx);
                }
              }.bind(this), $error);
            }

          function $If_3() {
            return $If_2.call(this);
          }
        } else {
          yieldHandleSubmit(suggestion, location);
          return $If_2.call(this);
        }

        function $If_2() {
          return $If_1.call(this);
        }
      }

      function $If_1() {
        return $return();
      }
    });
  };

  var onComboboxFocus = function onComboboxFocus() {
    yieldWrapper(function () {
      setIsSearchBoxFocused(true);
      searchBoxDidFocusInput === null || searchBoxDidFocusInput === void 0 ? void 0 : searchBoxDidFocusInput();
    });
    fireFocusEvent();
    shouldFireDismissSearchEventRef.current = true;

    if (enableNaturalLanguageSearch) {
      interactionIdRef.current = abKey + "-" + new Date().toISOString();
      setTimeout(function () {
        var length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      });
    }
  };
  /**
   *
   * @param {*} value - input
   * @param {*} meta - additional event information
   * meta.reason - the reason for the change
   */


  var onComboboxChange = function onComboboxChange(value, _ref12) {
    var reason = _ref12.reason;

    if (reason === FREEFORM && !value.match(categoryValuePattern$1) || reason === CLEAR_INPUT) {
      handleChangeCallback();
      setCurrentSearchTerm({
        displayName: value,
        category: 'RAW_SEARCH'
      });
      setShowLocationIcon(false);
      delayedFetch(value, reason);
    }
  }; // For backward compatibility, we have keep platformConfigs and do this check here,
  // To Do, We need to ensure this component is agnostic to host components
  // by removing keys like SearchResultsPageDesktop, etc.
  // This should allow:
  // host pages to change these configs with needing to update/rebuild this package
  // will allow this package to serve more scenarios without the need to have knoweldge of them


  var getPlatformConfiguration = function getPlatformConfiguration() {
    var config = platformConfig || defaultPlatformConfig;
    return platform in config ? config[platform] : {};
  };

  var clearInput = function clearInput() {
    onComboboxChange('', {
      reason: CLEAR_INPUT
    });
    yieldWrapper(function () {
      return onClearButtonClick();
    });
  };

  var renderInputAdornments = function renderInputAdornments() {
    var _getPlatformConfigura = getPlatformConfiguration(),
        supportsLocationIcon = _getPlatformConfigura.supportsLocationIcon;

    var sizeProps = {
      // defines the padding that should be used at each window size breakpoint (sm, md, lg)
      padding: size === 'xl' ? {
        sm: 'sm',
        md: '4.375',
        lg: '4.375'
      } : {},
      size: getSearchBoxSize(size)
    };
    var locationIconAvailable = !disableCurrentLocation && showLocationIcon && (supportsLocationIcon || isCurrentLocationSearch);
    var adornmentToShow = currentSearchTerm.displayName && enableNaturalLanguageSearch ? 'clear' : 'search'; // search, location, or clear

    if (locationIconAvailable && (!enableNaturalLanguageSearch || !isSearchBoxFocused)) {
      adornmentToShow = 'location';
    }

    var adornment =
    /*#__PURE__*/
    React.createElement(StyledSearchBoxButton, _extends({}, sizeProps, {
      title: "Submit Search",
      icon:
      /*#__PURE__*/
      React.createElement(IconSearch, {
        "aria-hidden": "false",
        "aria-describedby": "search-icon-title search-icon-desc",
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 845,
          columnNumber: 21
        }
      },
      /*#__PURE__*/
      React.createElement("title", {
        id: "search-icon-title",
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 849,
          columnNumber: 25
        }
      }, "Search"),
      /*#__PURE__*/
      React.createElement("desc", {
        id: "search-icon-desc",
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 850,
          columnNumber: 25
        }
      }, "A magnifying glass")),
      buttonType: "secondary",
      type: "submit",
      bare: true,
      enableNaturalLanguageSearch: enableNaturalLanguageSearch,
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 841,
        columnNumber: 13
      }
    }));

    if (enableNaturalLanguageSearch) {
      /**
       * Adornments are grouped in a fragment so the suggestions dropdown opens as desired
       * When the adornments are separate, the dropdown closes when the clear button is clicked
       */
      adornment =
      /*#__PURE__*/
      React.createElement(React.Fragment, {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 866,
          columnNumber: 17
        }
      },
      /*#__PURE__*/
      React.createElement(StyledClearButton, _extends({}, sizeProps, {
        bare: true,
        hideButton: adornmentToShow !== 'clear',
        icon:
        /*#__PURE__*/
        React.createElement(IconCloseCircle, {
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 871,
            columnNumber: 31
          }
        }),
        onMouseDown: function onMouseDown(e) {
          return e.preventDefault();
        } // Prevent blur of input
        // Using onClick instead causing the options to disappear on click
        ,
        onMouseUp: function onMouseUp() {
          return clearInput();
        },
        onKeyUp: function onKeyUp(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            clearInput();
            clearButtonRef.current.blur(); // Remove focus from hidden button
          }
        },
        ref: clearButtonRef,
        title: "clear search text button",
        type: "button",
        tabIndex: adornmentToShow !== 'clear' ? '-1' : '0',
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 867,
          columnNumber: 21
        }
      })),
      /*#__PURE__*/
      React.createElement(StyledSearchIconWrapper, _extends({}, sizeProps, {
        bare: true,
        hideButton: adornmentToShow !== 'search',
        onMouseDown: function onMouseDown(e) {
          return e.preventDefault();
        } // Prevent options flickering on click
        ,
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 888,
          columnNumber: 21
        }
      }),
      /*#__PURE__*/
      React.createElement("span", {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 894,
          columnNumber: 25
        }
      },
      /*#__PURE__*/
      React.createElement(IconSearch, {
        title: "search icon",
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 895,
          columnNumber: 29
        }
      }))));
    }

    if (adornmentToShow === 'location') {
      adornment =
      /*#__PURE__*/
      React.createElement(LocationAccessButton$1, _extends({}, sizeProps, {
        onLocationAccessGranted: onLocationAccessGranted,
        onLocationAccessFailed: onLocationAccessFailed,
        isCurrentLocationSearch: isCurrentLocationSearch,
        isUserPositionInMapBounds: isUserPositionInMapBounds,
        onReCenter: onReCenter,
        enableNaturalLanguageSearch: enableNaturalLanguageSearch,
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 904,
          columnNumber: 17
        }
      }));
    }

    return (
      /*#__PURE__*/
      React.createElement(StyledSearchBoxAdornment$1, {
        "aria-hidden": "false",
        enableNaturalLanguageSearch: enableNaturalLanguageSearch,
        __self: _this$2
      }, adornment)
    );
  };

  var renderInput = function renderInput(_ref13) {
    _ref13.onClear;
        var _onBlur = _ref13.onBlur;
        _ref13.onTagClose;
        var inputProps = _objectWithoutProperties(_ref13, ["onClear", "onBlur", "onTagClose"]);

    // defines the padding that should be used at each window size breakpoint (sm, md, lg)
    var paddingProps = {
      paddingY: {
        sm: 'sm',
        md: 'md',
        lg: 'md'
      }
    };
    var value = inputProps.value;
    var newProps = Object.assign({}, inputProps, size === 'xl' && paddingProps, {
      autoComplete: 'off',
      onBlur: function onBlur(e) {
        if (shouldFireDismissSearchEventRef.current) {
          fireEvent({
            eventName: EventNames.dismissSearch,
            clickstreamTriggerOverrides: clickstreamTriggerOverrides,
            clickstreamDecorators: clickstreamDecorators,
            allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
          });
        }

        if (typeof _onBlur === 'function') {
          _onBlur(e);
        }

        setIsSearchBoxFocused(false);
      },
      // Workaround for SXP-12248
      value: getDisplayTextFromOptionValue(value)
    });

    if (showAdornments) {
      return (
        /*#__PURE__*/
        React.createElement(AdornedInput, {
          input:
          /*#__PURE__*/
          React.createElement(Input, _extends({
            ref: inputRef
          }, newProps, {
            __self: _this$2,
            __source: {
              fileName: _jsxFileName$2,
              lineNumber: 955,
              columnNumber: 28
            }
          })),
          rightAdornment: renderInputAdornments(),
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 954,
            columnNumber: 17
          }
        })
      );
    }

    return (
      /*#__PURE__*/
      React.createElement(Input, _extends({
        ref: inputRef
      }, newProps, {
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 960,
          columnNumber: 16
        }
      }))
    );
  };

  var renderOption = useCallback(function (optionProps) {
    var label = optionProps.label,
        notificationCount = optionProps.notificationCount,
        onOptionClick = optionProps.onOptionClick;
    var highlightPattern = getHighlightPattern(getSuggestionValue(currentSearchTerm));
    var gleamNode = notificationCount ?
    /*#__PURE__*/
    React.createElement(StyledListboxOptionLabelGleam$1, {
      count: notificationCount,
      maxSuffix: true,
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 970,
        columnNumber: 17
      }
    }) : null;
    var labelNode =
    /*#__PURE__*/
    React.createElement(StyledListboxOptionLabel$1, {
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 974,
        columnNumber: 17
      }
    },
    /*#__PURE__*/
    React.createElement(StyledListboxOptionLabelText$1, {
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 975,
        columnNumber: 21
      }
    },
    /*#__PURE__*/
    React.createElement(Highlighter, {
      text: label,
      pattern: highlightPattern,
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 976,
        columnNumber: 25
      }
    })), gleamNode);
    return (
      /*#__PURE__*/
      React.createElement(ComboboxOption, _extends({}, optionProps, {
        // TODO SXP-15483: This improves the interaction time of clicking on an option, but a new frame isn't rendered
        // until after onOptionClick runs. Figure out a way to quickly render a new frame when clicking on an option
        onOptionClick: yieldCallback(onOptionClick),
        label: labelNode,
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 982,
          columnNumber: 17
        }
      }))
    );
  }, [currentSearchTerm, getHighlightPattern]);

  var onOptionSelect = function onOptionSelect(_ref14) {
    var index = _ref14.index;
    var selection = autocompleteSuggestions[index];
    submitSearch(selection, index);
  };

  var onFormSubmit = function onFormSubmit(e) {
    e.preventDefault();

    if (currentSearchTerm.category === SuggestionCategory.rawSearch) {
      submitSearch(currentSearchTerm, -1);
    }
  };

  var _getPlatformConfigura2 = getPlatformConfiguration(),
      placeholderText = _getPlatformConfigura2.placeholderText;

  var options = autocompleteSource === 'local' ? convertLocalSuggestionsToOptions(autocompleteSuggestions, enableNaturalLanguageSearch) : convertRemoteSuggestionsToOptions(autocompleteSuggestions, enableNaturalLanguageSearch);
  return (
    /*#__PURE__*/
    React.createElement(StyledForm$1, {
      ref: formRef,
      className: className,
      onSubmit: onFormSubmit,
      autocomplete: "off",
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 1016,
        columnNumber: 9
      }
    },
    /*#__PURE__*/
    React.createElement(StyledCombobox$1, {
      allowSubmitWhileOpen: true,
      fluidDropdown: true,
      freeForm: true,
      appearance: "input",
      autoCompleteBehavior: "manual",
      enableNaturalLanguageSearch: enableNaturalLanguageSearch,
      optionFilter: null,
      renderEmptyState: null,
      focusFirstOption: false,
      size: getSearchBoxSize(size),
      selectCallbackFrequency: CALLBACK_FREQUENCY.ALWAYS,
      ref: comboboxRef,
      renderInput: renderInput,
      renderOption: renderOption,
      onChange: onComboboxChange,
      onFocus: onComboboxFocus,
      onOptionSelect: onOptionSelect,
      placeholder: placeholderText,
      value: getSuggestionValue(currentSearchTerm) // Prevents options from opening on clear button click
      ,
      options: !isSearchBoxFocused && enableNaturalLanguageSearch ? [] : options,
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 1017,
        columnNumber: 13
      }
    }), screenReaderMessage &&
    /*#__PURE__*/
    React.createElement(VisuallyHidden, {
      "aria-live": "polite",
      "aria-atomic": "true",
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 1041,
        columnNumber: 17
      }
    },
    /*#__PURE__*/
    React.createElement("p", {
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 1042,
        columnNumber: 21
      }
    }, screenReaderMessage)))
  );
};

SearchBox$1.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * guid to pass to autocomplete service
   */
  abKey: PropTypes.string,

  /**
   * The location to use as context for autocomplete queries
   */
  centroid: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number
  }),

  /**
   * className to apply to the root element
   */
  className: PropTypes.string,

  /**
   * id to pass to autocomplete service
   */
  clientId: PropTypes.string,

  /**
   * When false the input will not show "Current Location" option
   */
  disableCurrentLocation: PropTypes.bool,

  /**
   * When true the search box will return NLS results via manual searches
   */
  enableNaturalLanguageSearch: PropTypes.bool,

  /**
   * Fetch method taking query string and location data
   * and returning promise with autocomplete suggestions.
   * @param {String} query
   * @param {Object} location
   * @param {Object} autocompleteConfig
   * @param {String} reason
   * @param {Number} [minAutocompleteChars] - minimum number of characters to trigger autocomplete
   * @param {Object} [zgGraphClient] - zgGraphClient instance
   * @return {Promise} fetch suggestions promise
   */
  fetchResults: PropTypes.func,

  /**
   * Fetch error callback.
   */
  fetchResultsError: PropTypes.func,

  /**
   * Function to filter/sort fetched autocomplete suggestions
   */
  filterResults: PropTypes.func,

  /**
   * GA events config
   */
  gaEventsConfig: PropTypes.shape({
    category: PropTypes.string,
    getClickActionName: PropTypes.func,
    getFocusActionName: PropTypes.func
  }),

  /**
   * Callback when the search box change event is triggered
   */
  handleChangeCallback: PropTypes.func,

  /**
   * Submit callback on what to do with submitted query/searchTerm.
   * @param {Object} searchTerm - current searchTerm from state, at least contains displayName attribute
   */
  handleSubmit: PropTypes.func,

  /**
   * Optional starting value for search input.
   */
  initSearchTerm: PropTypes.shape({
    displayName: PropTypes.string,
    category: PropTypes.string
  }),

  /**
   * Option pass a reference object to the input element
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.elementType
  })]),

  /**
   * whether or a current location search is active
   */
  isCurrentLocationSearch: PropTypes.bool,

  /**
   * whether the user position is visible in the map
   */
  isUserPositionInMapBounds: PropTypes.bool,

  /**
   * Callback when the clear button is clicked
   */
  onClearButtonClick: PropTypes.func,

  /**
   * update map bounds to center around user position
   */
  onReCenter: PropTypes.func,

  /**
   * DESKTOP or MOBILE.
   */
  platform: PropTypes.oneOf(Object.keys(defaultPlatformConfig)),

  /**
   * Allow the host component to provide different configs per platform
   */
  platformConfig: PropTypes.shape({
    DESKTOP: PropTypes.shape({
      placeholderText: PropTypes.string,
      supportsLocationIcon: PropTypes.bool
    }),
    MOBILE: PropTypes.shape({
      placeholderText: PropTypes.string,
      supportsLocationIcon: PropTypes.bool
    })
  }),

  /**
   * key to use in local storage for recent searches
   */
  recentSearchLocalStorageKey: PropTypes.string,

  /**
   * The url to send requests for Zillow Autocomplete Service V3
   */
  regionAddressAutocompleteURL: PropTypes.string,

  /**
   * The types of suggestions to be requested from Autocomplete Service
   */
  resultTypes: PropTypes.arrayOf(PropTypes.string),

  /**
   * Common between INPUT_SIZES and BUTTON_SIZES
   */
  size: PropTypes.oneOf(intersection(Object.values(Object.assign({}, BUTTON_SIZES, {
    xl: 'xl'
  })), Object.values(Object.assign({}, INPUT_SIZES, {
    xl: 'xl'
  })))),

  /**
   * Whether or not to include saved homes and claimed homes in autocomplete suggestions
   */
  shouldShowSavedHomes: PropTypes.bool,

  /**
   * Whether or not to include saved searches in autocomplete suggestions
   */
  shouldShowSavedSearches: PropTypes.bool,

  /**
   * whether or not to show adornments(search button and gleam)
   */
  showAdornments: PropTypes.bool,

  /**
   * Fires GA event on search box input focus
   */
  trackSearchBoxInputFocus: PropTypes.func,

  /**
   * Search listing type passed to query understanding ZG Graph query
   */
  userSearchContext: PropTypes.string,

  /**
   * Object that contains values that may override any clickstream trigger values
   */
  clickstreamTriggerOverrides: PropTypes.object,

  /**
   * Object that contains values that will be added to all clickstream events. These are typically Contextual Schema Blocks
   */
  clickstreamDecorators: PropTypes.object,

  /**
   * Boolean value required when not using a datalayer request to fetch new lane props.
   */
  allowEmptyInitialClickstreamBlocks: PropTypes.bool
} : {};
SearchBox$1.defaultProps = {
  disableCurrentLocation: false,
  enableNaturalLanguageSearch: false,
  gaEventsConfig: {
    category: 'searchbox',
    // eslint-disable-next-line no-confusing-arrow
    getClickActionName: function getClickActionName(category) {
      return category === 'RAW_SEARCH' ? 'submit' : 'select-suggestion';
    },
    getFocusActionName: function getFocusActionName() {
      return 'focus';
    }
  },
  fetchResults: defaultFetchSuggestions,
  // eslint-disable-next-line no-console
  fetchResultsError: function fetchResultsError(err) {
    return console.log(err);
  },
  filterResults: defaultFilterSuggestions,
  handleChangeCallback: function handleChangeCallback() {},
  handleSubmit: function handleSubmit() {},
  initSearchTerm: {
    displayName: '',
    category: 'RAW_SEARCH'
  },
  inputRef:
  /*#__PURE__*/
  React.createRef(),
  isCurrentLocationSearch: false,
  isUserPositionInMapBounds: true,
  onClearButtonClick: function onClearButtonClick() {},
  onReCenter: function onReCenter() {},
  platform: 'DESKTOP',
  platformConfig: null,
  showAdornments: true,
  size: INPUT_SIZES.sm,
  trackSearchBoxInputFocus: null,
  userSearchContext: 'FOR_SALE',
  clickstreamTriggerOverrides: null,
  clickstreamDecorators: null,
  allowEmptyInitialClickstreamBlocks: false
};
var SearchBox$2 = SearchBox$1;

var _this$1 = undefined,
    _jsxFileName$1 = "/builds/zillow/searchxp/static-search-box/src/SearchBoxCombobox.tsx";
var SearchBoxSelectionCategory;

(function (SearchBoxSelectionCategory) {
  SearchBoxSelectionCategory["region"] = "REGION";
  SearchBoxSelectionCategory["multiRegion"] = "MULTI_REGION";
})(SearchBoxSelectionCategory || (SearchBoxSelectionCategory = {}));

var isMultiRegionSelection = function isMultiRegionSelection(selection) {
  return Boolean(selection && selection.category === SearchBoxSelectionCategory.multiRegion);
};

var isRawSearch = function isRawSearch(selection) {
  return Boolean(selection && selection.category === SuggestionCategory.rawSearch);
};

var CLAIMED_HOME = RESULT_CATEGORIES.CLAIMED_HOME,
    CURRENT_LOCATION = RESULT_CATEGORIES.CURRENT_LOCATION,
    RECENT_SEARCH = RESULT_CATEGORIES.RECENT_SEARCH,
    SAVED_HOME = RESULT_CATEGORIES.SAVED_HOME,
    SAVED_SEARCH = RESULT_CATEGORIES.SAVED_SEARCH; // Workaround for SXP-12248

var categoryValuePattern = /^\[[A-Z_]+\]/g;
var onLocationFailedMessage = 'There is no location support on this device or it is disabled. Please check your settings.'; // Fix for the default icon padding
// increasng the height of the search box

var StyledSearchBoxAdornment = styled(Adornment).withConfig({
  displayName: "SearchBoxCombobox__StyledSearchBoxAdornment",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-0"
})(["position:absolute;top:calc(50% - ", "px);right:0;padding:0;width:", "px;height:", "px;justify-content:center;", ";button{width:", "px;height:", "px;&:after{margin-top:-", "px;min-width:", "px;height:", "px;}}"], SEARCH_BOX_HEIGHT / 2, SEARCH_BOX_HEIGHT, SEARCH_BOX_HEIGHT, function (_ref) {
  var shouldHideAdornment = _ref.shouldHideAdornment;
  return shouldHideAdornment && 'display: none;';
}, SEARCH_BOX_HEIGHT, SEARCH_BOX_HEIGHT, SEARCH_BOX_HEIGHT / 2, SEARCH_BOX_HEIGHT, SEARCH_BOX_HEIGHT);
var StyledForm = styled(Form).withConfig({
  displayName: "SearchBoxCombobox__StyledForm",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-1"
})(["position:absolute;z-index:2;top:0;left:0;right:0;border:1px solid;border-radius:4px;border-color:", ";background-color:", ";&:active,&:hover,&:focus{border-color:", ";}"], token('colors.gray300'), token('colors.backgroundWhite'), token('colors.blue400')); // The height of the Listbox is not configurable via
// the Combobox interface.
// We need fixed height ListBox across all viewport sizes
// And full width on narrow viewports

var StyledCombobox = styled(Combobox).withConfig({
  displayName: "SearchBoxCombobox__StyledCombobox",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-2"
})(["background-color:", ";border:none;border-radius:4px;&:focus,&:focus-within{box-shadow:0 0 0px 1px #fff,0 0 2px 3px #a6e5ff,0 0 2px 4px #006aff;}label{border:none;background-color:", ";&:focus{box-shadow:none;}}@media screen and (max-width:889px){&&& [role='listbox'],&&& [role='dialog']{width:100vw !important;}}&&& [role='listbox'],&&& [role='dialog']{height:auto;max-height:100vh;z-index:100;}"], token('colors.backgroundWhite'), token('colors.backgroundWhite'));
var StyledComboboxInput = styled(ComboboxInput).withConfig({
  displayName: "SearchBoxCombobox__StyledComboboxInput",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-3"
})(["border:none;padding-left:8px;width:100%;box-shadow:none;background-color:", ";&:focus,&:focus-within{box-shadow:none;}span[class*='StyledTag']{position:relative;display:block;flex:0 auto;min-width:60px;padding-right:32px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;> button{position:absolute;top:calc(50% - 8px);right:8px;height:100%;}&:last-of-type{margin-right:8px;}}input[role='combobox']{background-color:", ";text-overflow:ellipsis;", ";&:focus{box-shadow:none;}}", ""], token('colors.backgroundWhite'), token('colors.backgroundWhite'), function (_ref2) {
  var isRegionSelectionAtLimit = _ref2.isRegionSelectionAtLimit;
  return isRegionSelectionAtLimit && 'display: none;';
}, // Expanded + Collapsed States
// Overrides because the Combobox does not
// support this functionality
// Work to add support is tracked in CDS-1158
function (_ref3) {
  var isContainerFocused = _ref3.isContainerFocused;
  return (// Only applies in collapsed state
    isContainerFocused ? "\n                // Forces input onto new line and full width\n                input[role=\"combobox\"] {\n                    flex-basis: 100%;\n                }\n            " : css(["flex-wrap:nowrap;padding-right:", "px;> button[class*='StyleTagCloseButton']{display:none;}&&& span[class*='StyledTag']:nth-child(1):nth-last-child(4){flex-shrink:0;padding-right:8px;> button{display:none;}}@media screen and (max-width:", "px){span[class*='StyledTag']:nth-child(2) + input[role='combobox']{display:none;}}"], SEARCH_BOX_HEIGHT, DISPLAY_INPUT_MIN_WIDTH)
  );
});
var StyledListboxOptionLabelText = styled.span.withConfig({
  displayName: "SearchBoxCombobox__StyledListboxOptionLabelText",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-4"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
var StyledListboxOptionLabel = styled(ListboxOptionLabel).withConfig({
  displayName: "SearchBoxCombobox__StyledListboxOptionLabel",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-5"
})(["display:flex;"]);
var StyledListboxOptionLabelGleam = styled(Gleam).withConfig({
  displayName: "SearchBoxCombobox__StyledListboxOptionLabelGleam",
  componentId: "sxp-sb-8-100-7__sc-1qvxrzk-6"
})(["align-self:center;margin-left:auto;"]);

var getDisplayTextFromOptionValue = function getDisplayTextFromOptionValue(value) {
  return value.replace(categoryValuePattern, '');
};

var createUniqueOptionValue = function createUniqueOptionValue(displayName, category) {
  return "[" + category + "]" + displayName;
};

var getSuggestionCategoryIcon = function getSuggestionCategoryIcon(category) {
  switch (category) {
    case CURRENT_LOCATION:
      return (
        /*#__PURE__*/
        React.createElement(IconLocation, {
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 476,
            columnNumber: 20
          }
        })
      );

    case RECENT_SEARCH:
      return (
        /*#__PURE__*/
        React.createElement(IconClock, {
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 478,
            columnNumber: 20
          }
        })
      );

    case SAVED_SEARCH:
      return (
        /*#__PURE__*/
        React.createElement(IconSavedSearch, {
          "data-test": "saved-search-icon",
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 480,
            columnNumber: 20
          }
        })
      );

    case SAVED_HOME:
    case CLAIMED_HOME:
      return (
        /*#__PURE__*/
        React.createElement(IconFavoriteHome, {
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 483,
            columnNumber: 20
          }
        })
      );

    default:
      return null;
  }
};

var convertAutocompleteResultsToOptions = function convertAutocompleteResultsToOptions(autocompleteResults) {
  if (autocompleteResults.length) {
    var isLocalAutocompleteResults = autocompleteResults[0].category === CURRENT_LOCATION;

    if (isLocalAutocompleteResults) {
      return autocompleteResults.map(function (_ref4, index) {
        var _ref4$displayName = _ref4.displayName,
            displayName = _ref4$displayName === void 0 ? '' : _ref4$displayName,
            category = _ref4.category,
            notificationCount = _ref4.notificationCount;
        return {
          index: index,
          label: displayName,
          // Workaround for SXP-12248 and CDS-1173
          // CDS-1173: If two or more options share the same value, the Combobox
          // will select the same option regardless of which option the user selects.
          value: createUniqueOptionValue(displayName, category),
          icon: getSuggestionCategoryIcon(category),
          notificationCount: notificationCount
        };
      });
    }

    return autocompleteResults.map(function (_ref5, index) {
      var _ref5$displayName = _ref5.displayName,
          displayName = _ref5$displayName === void 0 ? '' : _ref5$displayName;
      return {
        index: index,
        label: displayName,
        value: displayName
      };
    });
  }

  return [];
};

var isLocationSet = function isLocationSet(location) {
  return location.latitude && location.longitude;
};

var isLocalQuery = function isLocalQuery(autocompleteResults) {
  return Boolean(autocompleteResults.length && autocompleteResults[0].category === CURRENT_LOCATION);
};

var isRegionInMultiRegionSelection = function isRegionInMultiRegionSelection(selection, _ref6) {
  var regionId = _ref6.regionId;
  return Boolean(selection.regions.find(function (region) {
    return region.regionId === regionId;
  }));
};

var isMultiRegionSelectionAtLimit = function isMultiRegionSelectionAtLimit(selection) {
  return selection.regions.length >= MAX_REGION_SELECTION_LENGTH;
};

var isPointRegionSelection = function isPointRegionSelection(selection) {
  return Boolean(selection.regions.find(function (region) {
    return region.isPointRegion === true;
  }));
};

var isSelectionEmpty = function isSelectionEmpty(selection) {
  if (selection === null) {
    return true;
  }

  if (isMultiRegionSelection(selection)) {
    return isPointRegionSelection(selection) || selection.regions.length === 0;
  }

  return !selection.displayName;
};

var shouldTriggerQU = function shouldTriggerQU(selection) {
  return isSelectionEmpty(selection) || isRawSearch(selection);
};
/**
 * SearchBox Component
 */


var SearchBox = function SearchBox(_ref7) {
  var autocompleteConfig = _ref7.autocompleteConfig,
      className = _ref7.className,
      fetchResultsError = _ref7.fetchResultsError,
      _ref7$gaEventsConfig = _ref7.gaEventsConfig,
      gaEventsConfig = _ref7$gaEventsConfig === void 0 ? {
    category: 'searchbox',
    // eslint-disable-next-line no-confusing-arrow
    getClickActionName: function getClickActionName(category) {
      return category === SuggestionCategory.rawSearch ? 'submit' : 'select-suggestion';
    },
    getFocusActionName: function getFocusActionName() {
      return 'focus';
    },
    getAddRegionName: function getAddRegionName() {
      return 'add-region';
    },
    getRemoveRegionName: function getRemoveRegionName() {
      return 'remove-region';
    },
    getRemoveAllRegionsName: function getRemoveAllRegionsName() {
      return 'remove-all-regions';
    },
    getRemoveAllRegionsExternalLocation: function getRemoveAllRegionsExternalLocation() {
      return 'map-clear';
    }
  } : _ref7$gaEventsConfig,
      handleSubmit = _ref7.handleSubmit,
      initialSelection = _ref7.initialSelection,
      isCurrentLocationSearch = _ref7.isCurrentLocationSearch,
      isUserPositionInMapBounds = _ref7.isUserPositionInMapBounds,
      onReCenter = _ref7.onReCenter,
      showAdornments = _ref7.showAdornments,
      supportsLocationIcon = _ref7.supportsLocationIcon,
      trackSearchBoxInputFocus = _ref7.trackSearchBoxInputFocus,
      searchBoxDidFocusInput = _ref7.searchBoxDidFocusInput,
      searchBoxDidClickTag = _ref7.searchBoxDidClickTag,
      searchBoxDidSubmitForm = _ref7.searchBoxDidSubmitForm,
      searchBoxDidSelectOption = _ref7.searchBoxDidSelectOption,
      searchBoxDidClearAllSelections = _ref7.searchBoxDidClearAllSelections,
      searchBoxOnPopState = _ref7.searchBoxOnPopState,
      shouldClearSelection = _ref7.shouldClearSelection;

  var _useState = useState('local'),
      _useState2 = _slicedToArray(_useState, 2),
      autocompleteSource = _useState2[0],
      setAutocompleteSource = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      autocompleteResults = _useState4[0],
      setAutocompleteResults = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      autocompleteResultsLoading = _useState6[0],
      setAutocompleteResultsLoading = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      showNoResultsMessage = _useState8[0],
      setShowNoResultsMessage = _useState8[1];

  var _useState9 = useState({}),
      _useState10 = _slicedToArray(_useState9, 2),
      location = _useState10[0],
      setLocation = _useState10[1];

  var _useState11 = useState(null),
      _useState12 = _slicedToArray(_useState11, 2),
      screenReaderMessage = _useState12[0],
      setScreenReaderMessage = _useState12[1];

  var _useState13 = useState(true),
      _useState14 = _slicedToArray(_useState13, 2),
      showLocationIcon = _useState14[0],
      setShowLocationIcon = _useState14[1];

  var _useState15 = useState(false),
      _useState16 = _slicedToArray(_useState15, 2),
      isContainerFocused = _useState16[0],
      setIsContainerFocused = _useState16[1];

  var _useState17 = useState(false),
      _useState18 = _slicedToArray(_useState17, 2),
      shouldSelectFirstResult = _useState18[0],
      setShouldSelectFirstResult = _useState18[1];

  var _useState19 = useState(initialSelection !== null && initialSelection !== void 0 ? initialSelection : null),
      _useState20 = _slicedToArray(_useState19, 2),
      currentSelection = _useState20[0],
      setCurrentSelection = _useState20[1];

  var _useState21 = useState(false),
      _useState22 = _slicedToArray(_useState21, 2),
      shouldCallDismissSearchEvent = _useState22[0],
      setShouldCallDismissSearchEvent = _useState22[1];

  var _useState23 = useState(false),
      _useState24 = _slicedToArray(_useState23, 2),
      isRegionRemoved = _useState24[0],
      setIsRegionRemoved = _useState24[1];

  var _useState25 = useState(false),
      _useState26 = _slicedToArray(_useState25, 2),
      isLastRegionRemoved = _useState26[0],
      setIsLastRegionRemoved = _useState26[1];

  var yieldWrapper = useYieldCallback(function (callback) {
    callback();
  });
  useDeepCompareEffectNoCheck(function () {
    setCurrentSelection(initialSelection !== null && initialSelection !== void 0 ? initialSelection : null);
  }, [initialSelection]);
  var getPlaceholderText = useCallback(function () {
    if (isSelectionEmpty(currentSelection)) {
      return PLACEHOLDER_TEXT_EMPTY;
    }

    if (isContainerFocused) {
      return PLACEHOLDER_TEXT_EXPANDED;
    }

    return PLACEHOLDER_TEXT_COLLAPSED;
  }, [currentSelection, isContainerFocused]);

  var _useState27 = useState(getPlaceholderText()),
      _useState28 = _slicedToArray(_useState27, 2),
      placeholderText = _useState28[0],
      setPlaceholderText = _useState28[1];

  useEffect(function () {
    setPlaceholderText(getPlaceholderText);
  }, [getPlaceholderText]);
  var formRef = useRef(null);
  var comboboxRef = useRef(null);
  var screenReaderTimeoutRef = useRef();
  var getInputValue = useCallback(function () {
    var _comboboxRef$current;

    var input = (_comboboxRef$current = comboboxRef.current) === null || _comboboxRef$current === void 0 ? void 0 : _comboboxRef$current.querySelector('input');
    return input ? input.value : '';
  }, [comboboxRef]);

  var clearAutocompleteResults = function clearAutocompleteResults() {
    setShouldSelectFirstResult(false);
    setShowNoResultsMessage(false);
    setAutocompleteResults([]);
  };

  var getExceptionText = function getExceptionText(err) {
    if (err instanceof Error) {
      return err.message;
    }

    if (typeof err === 'string') {
      return err;
    }

    return 'Unknown error occured fetching AutoComplete results.';
  };

  var fetchSuggestionsWrapper = useCallback(function (query, reason) {
    return new Promise(function ($return, $error) {
      var $Try_1_Finally = function ($Try_1_Exit) {
        return function ($Try_1_Value) {
          try {
            setAutocompleteResultsLoading(false);
            return $Try_1_Exit && $Try_1_Exit.call(this, $Try_1_Value);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);
      }.bind(this);

      var suggestions, filteredSuggestions;

      var $Try_1_Post = function () {
        try {
          return $return();
        } catch ($boundEx) {
          return $error($boundEx);
        }
      };

      var $Try_1_Catch = function (err) {
        try {
          fetchResultsError(getExceptionText(err));
          setShouldSelectFirstResult(false);
          setShowNoResultsMessage(true);
          return $Try_1_Finally($Try_1_Post)();
        } catch ($boundEx) {
          return $Try_1_Finally($error)($boundEx);
        }
      };

      try {
        setAutocompleteResultsLoading(true); // eslint-disable-next-line zillow/@typescript-eslint/no-unsafe-assignment

        return Promise.resolve(defaultFetchSuggestions(query, location, autocompleteConfig, reason, AUTOCOMPLETE_MIN_CHAR_LIMIT)).then(function ($await_2) {
          try {
            suggestions = $await_2;
            setAutocompleteSource(isLocalQuery(suggestions) ? 'local' : 'remote');
            filteredSuggestions = defaultFilterSuggestions(suggestions, true);
            setAutocompleteResults(filteredSuggestions);

            if (!shouldTriggerQU(currentSelection) && !filteredSuggestions.length) {
              setShowNoResultsMessage(true);
            } else {
              setShowNoResultsMessage(false);
            }

            return $Try_1_Finally($Try_1_Post)();
          } catch ($boundEx) {
            return $Try_1_Catch($boundEx);
          }
        }, $Try_1_Catch);
      } catch (err) {
        $Try_1_Catch(err);
      }
    });
  }, [autocompleteConfig, fetchResultsError, location, currentSelection]);
  var fetchAutocompleteResults = useDebouncedCallback(fetchSuggestionsWrapper, AUTOCOMPLETE_REQUEST_DELAY);

  var readAutocompleteResults = function readAutocompleteResults() {
    if (autocompleteResults.length) {
      var _window;

      setScreenReaderMessage( // prettier-ignore
      autocompleteResults.length + " suggested results are available. Use up and down arrow keys to navigate");
      clearTimeout(screenReaderTimeoutRef.current); // eslint-disable-next-line zillow/@typescript-eslint/no-unnecessary-condition

      if ((_window = window) === null || _window === void 0 ? void 0 : _window.setTimeout) {
        screenReaderTimeoutRef.current = window.setTimeout(function () {
          // eslint-disable-next-line zillow/react/no-did-update-set-state
          setScreenReaderMessage(null);
        }, SCREEN_READER_MESSAGE_TIMEOUT);
      }
    }

    return function () {
      clearTimeout(screenReaderTimeoutRef.current);
    };
  };

  useEffect(readAutocompleteResults, [autocompleteResults]);

  var onPopState = function onPopState() {
    var listener = function listener() {
      return searchBoxOnPopState === null || searchBoxOnPopState === void 0 ? void 0 : searchBoxOnPopState(comboboxRef);
    };

    window.addEventListener('popstate', listener);
    return function () {
      return window.removeEventListener('popstate', listener);
    };
  };

  useEffect(onPopState, [searchBoxOnPopState]); // We restrict highlighting to the autocomplete suggestions

  var getHighlightPattern = function getHighlightPattern(str) {
    if (autocompleteSource === 'remote' && str.trim()) {
      return str;
    }

    return null;
  };

  var trackRegionEvent = function trackRegionEvent(action, _ref8, regionIndex) {
    var regionId = _ref8.regionId;
    var trackEventProp = {
      category: gaEventsConfig.category,
      action: action === 'add' ? gaEventsConfig.getAddRegionName() : gaEventsConfig.getRemoveRegionName(),
      label: regionId + " | " + regionIndex
    };

    if (action === 'remove') {
      fireTrackEvent({
        trackEventProp: trackEventProp,
        regionIndex: regionIndex,
        isMultiregionSearch: true,
        eventName: EventNames.removeMultiRegion
      });
    } else {
      yieldWrapper(function () {
        return track(trackEventProp);
      });
    }
  };

  var trackSearchBoxSubmit = function trackSearchBoxSubmit(autocompleteResult, resultIndex) {
    var categoryTrackingConfig = trackingConfigsByCategory[autocompleteResult.category];
    var getSearchBoxClickLabel = categoryTrackingConfig.getSearchBoxClickLabel;
    var isRegionResult = isRegionSuggestion(autocompleteResult) || isRecentRegionSuggestion(autocompleteResult);
    var selectedRegion;

    if (typeof getSearchBoxClickLabel !== 'function') {
      console.error("No GA click event configured for search term with category " + autocompleteResult.category);
    } else {
      var trackEventProp = {
        category: gaEventsConfig.category,
        action: gaEventsConfig.getClickActionName(autocompleteResult.category),
        label: getSearchBoxClickLabel({
          chosenSuggestion: autocompleteResult,
          allSuggestions: autocompleteResults,
          chosenIndex: resultIndex,
          searchBoxValue: getInputValue()
        })
      };

      if (isRegionResult) {
        selectedRegion = {
          category: SearchBoxSelectionCategory.region,
          displayName: autocompleteResult.displayName,
          regionId: autocompleteResult.data.regionId,
          regionType: autocompleteResult.data.regionType
        };
        var isPointRegionSelected = isMultiRegionSelection(currentSelection) ? isPointRegionSelection(currentSelection) : false;
        var isRegionAlreadyAdded = isMultiRegionSelection(currentSelection) && (isMultiRegionSelectionAtLimit(currentSelection) || isRegionInMultiRegionSelection(currentSelection, selectedRegion));
        fireTrackEvent({
          trackEventProp: trackEventProp,
          autocompleteResult: autocompleteResult,
          isMultiregionSearch: true,
          isRegionInMultiRegionSelection: isRegionAlreadyAdded,
          isPointRegionSelected: isPointRegionSelected
        });
      } else {
        fireTrackEvent({
          trackEventProp: trackEventProp,
          autocompleteResult: autocompleteResult,
          isMultiregionSearch: true
        });
      }
    }
  };

  var yieldSubmitSearch = useYieldCallback(function (newSelection, newLocation) {
    handleSubmit(newSelection, newLocation);
  }, [handleSubmit]);
  var updateCurrentSelection = useCallback(function (newSelection, newLocation) {
    if (newLocation === void 0) {
      newLocation = location;
    }

    setCurrentSelection(newSelection);
    yieldSubmitSearch(newSelection, newLocation);
  }, [setCurrentSelection, yieldSubmitSearch, location]);

  var selectAutocompleteResult = function selectAutocompleteResult(autocompleteResult) {
    var isRegionResult = isRegionSuggestion(autocompleteResult) || isRecentRegionSuggestion(autocompleteResult);

    if (!isRegionResult) {
      updateCurrentSelection(autocompleteResult);
      return;
    }

    var selectedRegion = {
      category: SearchBoxSelectionCategory.region,
      displayName: autocompleteResult.displayName,
      regionId: autocompleteResult.data.regionId,
      regionType: autocompleteResult.data.regionType
    };

    if (isMultiRegionSelection(currentSelection) && (isMultiRegionSelectionAtLimit(currentSelection) || isRegionInMultiRegionSelection(currentSelection, selectedRegion))) {
      return;
    }

    var multiRegionSelection = {
      category: SearchBoxSelectionCategory.multiRegion,
      regions: isMultiRegionSelection(currentSelection) && !isPointRegionSelection(currentSelection) ? [].concat(_toConsumableArray(currentSelection.regions), [selectedRegion]) : [selectedRegion]
    };

    if (isMultiRegionSelection(currentSelection) && currentSelection.regions.length > 0) {
      fireEvent({
        eventName: EventNames.addNthRegion
      });
    }

    trackRegionEvent('add', selectedRegion, multiRegionSelection.regions.length);
    updateCurrentSelection(multiRegionSelection);

    if (isRegionSuggestion(autocompleteResult)) {
      RecentSearchClient.addToRecentSearches(autocompleteResult);
    }
  };

  var clearSelection = function clearSelection(shouldTriggerSubmit) {
    if (shouldTriggerSubmit === void 0) {
      shouldTriggerSubmit = true;
    }

    var newSelection = {
      category: SearchBoxSelectionCategory.multiRegion,
      regions: []
    };

    if (shouldTriggerSubmit) {
      yieldSubmitSearch(newSelection, location);
    }

    setCurrentSelection(null);
    clearAutocompleteResults();
  };

  useEffect(function () {
    if (isMultiRegionSelection(currentSelection) && currentSelection.regions.length && shouldClearSelection) {
      clearSelection(false);
      yieldWrapper(function () {
        track({
          category: gaEventsConfig.category,
          action: gaEventsConfig.getRemoveAllRegionsName(),
          label: currentSelection.regions.length + " | " + gaEventsConfig.getRemoveAllRegionsExternalLocation()
        });
      });
    } // eslint-disable-next-line zillow/react-hooks/exhaustive-deps

  }, [shouldClearSelection]);
  /**
   * Current Location
   */

  var onLocationAccessGranted = useCallback(function (_ref9) {
    var coords = _ref9.coords;
    var newSelection = {
      category: SuggestionCategory.currentLocation,
      displayName: 'Current Location'
    };
    setLocation(coords);
    setShowLocationIcon(true);
    updateCurrentSelection(newSelection, coords);
  }, [updateCurrentSelection]);
  var onLocationAccessFailed = useCallback(function () {
    setShowLocationIcon(false); // eslint-disable-next-line no-alert

    alert(onLocationFailedMessage);
  }, []);
  var requestUserLocation = useCallback(function () {
    var _navigator, _navigator$geolocatio;

    // eslint-disable-next-line zillow/@typescript-eslint/no-unnecessary-condition
    if ((_navigator = navigator) === null || _navigator === void 0 ? void 0 : (_navigator$geolocatio = _navigator.geolocation) === null || _navigator$geolocatio === void 0 ? void 0 : _navigator$geolocatio.getCurrentPosition) {
      navigator.geolocation.getCurrentPosition(onLocationAccessGranted, onLocationAccessFailed);
    } else {
      console.warn('No location object available');
    }
  }, [onLocationAccessGranted, onLocationAccessFailed]);
  var onLocationButtonClick = useCallback(function () {
    if (isCurrentLocationSearch && typeof onReCenter === 'function') {
      onReCenter();
    } else {
      requestUserLocation();
    }
  }, [isCurrentLocationSearch, onReCenter, requestUserLocation]);
  /**
   * Handles Combobox Change Events
   *
   * Depending on which mode the Combobox is in (single or multi) this
   * callback for different events and with different arguments
   *
   * In both modes, this fn will be called the user hits "Enter" to select an autocomplete option,
   * or when the user clicks on an autocomplete option.
   *
   * In single mode, selection will be a single string. reason will be "listbox event"
   *
   * In multi mode (tokens), selection will be an array of strings. reason will be "listbox event"
   *
   * @param {string | string[]} selection - the current selection
   * @param {object} meta:reason - the change reason
   */

  var onComboboxChange = function onComboboxChange(selection, _ref10) {
    var reason = _ref10.reason;

    if (reason === 'clear input') {
      clearSelection(false);
    }
  };
  /**
   * Handles Combobox Clear Events
   */


  var onComboboxClear = function onComboboxClear() {
    clearSelection();
    searchBoxDidClearAllSelections === null || searchBoxDidClearAllSelections === void 0 ? void 0 : searchBoxDidClearAllSelections(comboboxRef);

    if (isMultiRegionSelection(currentSelection) && currentSelection.regions.length) {
      var trackEventProp = {
        category: gaEventsConfig.category,
        action: gaEventsConfig.getRemoveAllRegionsName(),
        label: currentSelection.regions.length + " | searchbox-clear"
      };
      setIsRegionRemoved(true);
      fireTrackEvent({
        trackEventProp: trackEventProp,
        isMultiregionSearch: true,
        eventName: EventNames.removeAllMultiRegion
      });
    }
  };
  /**
   * Handles Combobox KeyUp Events
   * @param {KeyboardEvent} currentTarget
   */


  var onComboboxKeyUp = function onComboboxKeyUp(_ref11) {
    var currentTarget = _ref11.currentTarget,
        key = _ref11.key;
    var keysToIgnore = ['Enter', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'];

    if (!keysToIgnore.includes(key)) {
      void fetchAutocompleteResults(currentTarget.value, 'freeform');
    }
  };
  /**
   * Handles Combobox Container Clicks Events
   */


  var onContainerClick = useCallback(function (target) {
    var isTargetAButton = target.nodeName === 'BUTTON';

    if (!isTargetAButton) {
      setIsContainerFocused(true);
    }
  }, []);
  /**
   * Handles Click Events Outside Combobox Container
   */

  var onContainerClickOutside = useCallback(function () {
    var _comboboxRef$current4;

    setIsLastRegionRemoved(function (currentValue) {
      if (currentValue) {
        var _comboboxRef$current2, _comboboxRef$current3;

        (_comboboxRef$current2 = comboboxRef.current) === null || _comboboxRef$current2 === void 0 ? void 0 : (_comboboxRef$current3 = _comboboxRef$current2.querySelector('input')) === null || _comboboxRef$current3 === void 0 ? void 0 : _comboboxRef$current3.focus();
      }

      return false;
    });

    if (document.activeElement !== ((_comboboxRef$current4 = comboboxRef.current) === null || _comboboxRef$current4 === void 0 ? void 0 : _comboboxRef$current4.querySelector('input'))) {
      setIsContainerFocused(false);
      setShouldCallDismissSearchEvent(false);
      setIsRegionRemoved(false);
      clearAutocompleteResults();
    }
  }, [comboboxRef]);
  useOnClickInsideOutside(formRef, onContainerClick, onContainerClickOutside);
  /**
   * Sets internal focus state false and blurs the field. Will
   * fully collapse the Combobox and hide the dropdown.
   */

  var removeContainerFocus = function removeContainerFocus() {
    setIsContainerFocused(false);

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };
  /**
   * Handles Combobox Input Focus Events
   */


  var onComboboxInputFocus = function onComboboxInputFocus() {
    void fetchAutocompleteResults(getInputValue(), INPUT_FOCUSED);
    yieldWrapper(function () {
      setIsContainerFocused(true);
      searchBoxDidFocusInput === null || searchBoxDidFocusInput === void 0 ? void 0 : searchBoxDidFocusInput(comboboxRef);
    });
    yieldWrapper(function () {
      if (typeof trackSearchBoxInputFocus === 'function') {
        trackSearchBoxInputFocus();
      }
    });
  };

  useEffect(function () {
    if (shouldCallDismissSearchEvent && !isRegionRemoved) {
      fireEvent({
        eventName: EventNames.dismissSearch
      });
      setShouldCallDismissSearchEvent(false);
      setIsRegionRemoved(false);
    }
  }, [shouldCallDismissSearchEvent, isRegionRemoved]);
  /**
   * Handles Combobox Input Blur Events
   */

  var onComboboxInputBlur = function onComboboxInputBlur() {
    setIsContainerFocused(function (currentValue) {
      if (currentValue) {
        setShouldCallDismissSearchEvent(true);
      }

      return false;
    });
  };
  /**
   *
   * @param {*} option - selected option
   */


  var onOptionSelect = function onOptionSelect(_ref12) {
    var index = _ref12.index;

    if (autocompleteResults.length && typeof index === 'number') {
      if (fetchAutocompleteResults.isPending()) {
        fetchAutocompleteResults.cancel();
      }

      searchBoxDidSelectOption === null || searchBoxDidSelectOption === void 0 ? void 0 : searchBoxDidSelectOption(comboboxRef); // eslint-disable-next-line zillow/@typescript-eslint/no-unsafe-member-access

      var autocompleteSelection = autocompleteResults[index];
      trackSearchBoxSubmit(autocompleteSelection, index + 1); // analytics expects a 1-indexed index

      if (autocompleteSelection.category === CURRENT_LOCATION && !isLocationSet(location)) {
        requestUserLocation();
      } else {
        selectAutocompleteResult(autocompleteSelection);
      }

      clearAutocompleteResults();
      removeContainerFocus();
    }
  };

  var selectText = function selectText(str) {
    if (!str) {
      return;
    }

    var newSelection = {
      category: SuggestionCategory.rawSearch,
      displayName: str
    };
    trackSearchBoxSubmit(newSelection, -1);
    updateCurrentSelection(newSelection);
  };

  useEffect(function () {
    if (!autocompleteResultsLoading && shouldSelectFirstResult && !isLocalQuery(autocompleteResults)) {
      if (autocompleteResults.length) {
        onOptionSelect({
          index: 0
        });
      } else if (shouldTriggerQU(currentSelection)) {
        selectText(getInputValue());
      }

      setShouldSelectFirstResult(false);
    } // eslint-disable-next-line zillow/react-hooks/exhaustive-deps

  }, [autocompleteResultsLoading, shouldSelectFirstResult, autocompleteResults, currentSelection]);

  var onOptionRemove = function onOptionRemove(displayName) {
    if (isMultiRegionSelection(currentSelection)) {
      var regions = currentSelection.regions.filter(function (region) {
        return region.displayName !== displayName;
      });

      var _currentSelection$reg = currentSelection.regions.reduce(function (acc, region, index) {
        if (region.displayName === displayName) {
          return [region, index + 1]; // need a one based index
        }

        return acc;
      }, [undefined, -1]),
          _currentSelection$reg2 = _slicedToArray(_currentSelection$reg, 2),
          removedRegion = _currentSelection$reg2[0],
          removedRegionIndex = _currentSelection$reg2[1];

      var newSelection = Object.assign({}, currentSelection, {
        regions: regions
      });

      if (removedRegion) {
        setIsRegionRemoved(true);

        if (regions.length === MAX_REGION_SELECTION_LENGTH - 1 && !isLastRegionRemoved) {
          setIsLastRegionRemoved(true);
        }

        trackRegionEvent('remove', removedRegion, removedRegionIndex);
      }

      if (regions.length === 0) {
        clearSelection();
      } else {
        updateCurrentSelection(newSelection);
      }
    }
  };
  /**
   * Handles Form Submit Events
   *
   * In both modes (single and multi), form submission is triggered when the user hits 'enter'
   * if there are no autocomplete results available. If there is a pending autocomplete request,
   * the pending request is flushed and we will attempt to select the first autocomplete result when
   * results come back
   *
   * @param {FormEvent} e
   */


  var onFormSubmit = function onFormSubmit(e) {
    e.preventDefault();
    searchBoxDidSubmitForm === null || searchBoxDidSubmitForm === void 0 ? void 0 : searchBoxDidSubmitForm(comboboxRef);

    if (getInputValue().length) {
      if (fetchAutocompleteResults.isPending()) {
        fetchAutocompleteResults.flush();
        setShouldSelectFirstResult(true);
      } else if (shouldTriggerQU(currentSelection)) {
        selectText(getInputValue());
      }
    }
  };
  /**
   * Handles clicks on Combobox tags
   */


  var onComboboxTagClick = useCallback(function (e) {
    var node = e.target;

    if (node.dataset.componentName !== 'tag') {
      // prevent bubbling and triggering focus when remove tag button is clicked
      e.stopPropagation();
    } else {
      searchBoxDidClickTag === null || searchBoxDidClickTag === void 0 ? void 0 : searchBoxDidClickTag(comboboxRef);
    }
  }, [comboboxRef, searchBoxDidClickTag]);
  /**
   * Renders custom input tags
   */

  var renderTag = function renderTag(tagProps) {
    return (
      /*#__PURE__*/
      React.createElement(Tag, _extends({}, tagProps, {
        onClick: onComboboxTagClick,
        "data-component-name": "tag",
        textTransform: "none",
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1201,
          columnNumber: 9
        }
      }))
    );
  };
  /**
   * Renders A Combobox Dropdown Option Component
   *
   * @param {*} option - selected option
   */


  var renderOption = function renderOption(_ref13) {
    var _labelNode;

    var label = _ref13.label,
        notificationCount = _ref13.notificationCount;
        _ref13.appearance;
        var onOptionClick = _ref13.onOptionClick,
        optionProps = _objectWithoutProperties(_ref13, ["label", "notificationCount", "appearance", "onOptionClick"]);

    var highlightPattern = getHighlightPattern(getInputValue());
    var gleamNode = notificationCount ?
    /*#__PURE__*/
    React.createElement(StyledListboxOptionLabelGleam, {
      count: notificationCount,
      maxSuffix: true,
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1227,
        columnNumber: 13
      }
    }) : null;
    var labelNode;

    if (typeof label === 'string') {
      labelNode =
      /*#__PURE__*/
      React.createElement(StyledListboxOptionLabel, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1234,
          columnNumber: 17
        }
      },
      /*#__PURE__*/
      React.createElement(StyledListboxOptionLabelText, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1235,
          columnNumber: 21
        }
      },
      /*#__PURE__*/
      React.createElement(Highlighter, {
        text: label,
        pattern: highlightPattern,
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1236,
          columnNumber: 25
        }
      })), gleamNode);
    }

    return (
      /*#__PURE__*/
      React.createElement(ComboboxOption, _extends({}, optionProps, {
        // TODO SXP-15483: This improves the interaction time of clicking on an option, but a new frame isn't rendered
        // until after onOptionClick runs. Figure out a way to quickly render a new frame when clicking on an option
        onOptionClick: onOptionClick ? yieldCallback(onOptionClick) : onOptionClick,
        label: (_labelNode = labelNode) !== null && _labelNode !== void 0 ? _labelNode : label,
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1244,
          columnNumber: 13
        }
      }))
    );
  };
  /**
   * Renders a button within the Combobox's input component
   * Used to render either the search button or current location button
   *
   * @param {*} option - selected option
   */


  var renderInputAdornment = useCallback(function () {
    var locationIconAvailable = showLocationIcon && (supportsLocationIcon || isCurrentLocationSearch);
    var adornment;

    if (locationIconAvailable) {
      adornment =
      /*#__PURE__*/
      React.createElement(IconButton, {
        title: "Use your location",
        onClick: onLocationButtonClick,
        icon: isCurrentLocationSearch && !isUserPositionInMapBounds ?
        /*#__PURE__*/
        React.createElement(IconLocationOutline, {
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 1273,
            columnNumber: 29
          }
        }) :
        /*#__PURE__*/
        React.createElement(IconLocation, {
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 1275,
            columnNumber: 29
          }
        }),
        size: BUTTON_SIZES.sm,
        type: "button",
        bare: true,
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1268,
          columnNumber: 17
        }
      });
    } else {
      adornment =
      /*#__PURE__*/
      React.createElement(IconSearch, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 1284,
          columnNumber: 25
        }
      });
    }

    return (
      /*#__PURE__*/
      React.createElement(StyledSearchBoxAdornment, {
        shouldHideAdornment: isContainerFocused,
        __self: _this$1
      }, adornment)
    );
  }, [isContainerFocused, isCurrentLocationSearch, isUserPositionInMapBounds, onLocationButtonClick, showLocationIcon, supportsLocationIcon]);

  var renderInput = function renderInput(_ref14) {
    var value = _ref14.value,
        _onBlur = _ref14.onBlur,
        inputProps = _objectWithoutProperties(_ref14, ["value", "onBlur"]);

    var newProps = Object.assign({}, inputProps, {
      autoComplete: 'off',
      // Workaround for SXP-12248 and CDS-1173
      // There were cases where value was undefined and we were setting
      // it to an empty string with unintended consequences.
      value: value ? getDisplayTextFromOptionValue(value) : value,
      onBlur: function onBlur(e) {
        if (typeof _onBlur === 'function') {
          _onBlur(e);
        }

        onComboboxInputBlur();
      }
    });
    var inputComponent =
    /*#__PURE__*/
    React.createElement(StyledComboboxInput, _extends({}, newProps, {
      onFocus: onComboboxInputFocus,
      onClear: onComboboxClear,
      onTagClose: onOptionRemove,
      renderTag: renderTag,
      isContainerFocused: isContainerFocused,
      isRegionSelectionAtLimit: isMultiRegionSelection(currentSelection) && isMultiRegionSelectionAtLimit(currentSelection),
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1324,
        columnNumber: 13
      }
    }));

    if (showAdornments) {
      return (
        /*#__PURE__*/
        React.createElement(AdornedInput, {
          input: inputComponent,
          rightAdornment: renderInputAdornment(),
          __self: _this$1,
          __source: {
            fileName: _jsxFileName$1,
            lineNumber: 1338,
            columnNumber: 20
          }
        })
      );
    }

    return inputComponent;
  };

  var renderEmptyState = useCallback( // eslint-disable-next-line no-confusing-arrow
  function () {
    return showNoResultsMessage ?
    /*#__PURE__*/
    React.createElement(ComboboxEmptyState, {
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1347,
        columnNumber: 17
      }
    }, "We couldn't find a location match.") : null;
  }, [showNoResultsMessage]);
  var getDisplayValue = useCallback(function () {
    if (currentSelection) {
      var _currentSelection$dis;

      if (isMultiRegionSelection(currentSelection)) {
        if (currentSelection.regions.find(function (region) {
          return region.isPointRegion;
        })) {
          return [];
        }

        var regionNames = currentSelection.regions.map(function (_ref15) {
          var displayName = _ref15.displayName;
          return displayName;
        });

        if (isContainerFocused || regionNames.length < 2) {
          // Display all chips when the input is focused or if
          // there is only one selection
          return regionNames;
        } // When input not focused and greater than 1 selection,
        // display n more and last selection


        var lastRegionName = regionNames.slice(-1)[0];
        return [regionNames.length - 1 + " more", lastRegionName];
      } // display a single string instead of tokens for non-multi-region selections


      return (_currentSelection$dis = currentSelection.displayName) !== null && _currentSelection$dis !== void 0 ? _currentSelection$dis : [];
    }

    return [];
  }, [currentSelection, isContainerFocused]);
  return (
    /*#__PURE__*/
    React.createElement(StyledForm, {
      ref: formRef,
      onSubmit: onFormSubmit,
      className: className,
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1378,
        columnNumber: 9
      }
    },
    /*#__PURE__*/
    React.createElement(StyledCombobox, {
      allowSubmitWhileOpen: true,
      ref: comboboxRef // The first option is selected by default if the autocomplete results
      // are from a remote search (i.e. suggestions from what was typed)
      ,
      focusFirstOption: autocompleteSource === 'remote',
      appearance: "input",
      autoCompleteBehavior: "manual",
      optionFilter: null,
      renderEmptyState: renderEmptyState,
      size: "md",
      selectCallbackFrequency: "always" // Type of renderInput needs to be updated in @zillow-constellation/types, but the cast works for now
      ,
      renderInput: renderInput,
      renderOption: renderOption,
      onOptionSelect: onOptionSelect,
      options: convertAutocompleteResultsToOptions(autocompleteResults),
      onChange: onComboboxChange,
      onKeyUp: onComboboxKeyUp,
      placeholder: placeholderText,
      value: getDisplayValue(),
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1379,
        columnNumber: 13
      }
    }), isMultiRegionSelection(currentSelection) && isMultiRegionSelectionAtLimit(currentSelection) && isContainerFocused &&
    /*#__PURE__*/
    React.createElement(Paragraph, {
      margin: "xs",
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1404,
        columnNumber: 21
      }
    }, "You can only search up to ",
    /*#__PURE__*/
    React.createElement("strong", {
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1405,
        columnNumber: 51
      }
    }, "five locations"), " at once."), screenReaderMessage &&
    /*#__PURE__*/
    React.createElement(VisuallyHidden, {
      "aria-live": "polite",
      "aria-atomic": "true",
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1409,
        columnNumber: 17
      }
    },
    /*#__PURE__*/
    React.createElement("p", {
      __self: _this$1,
      __source: {
        fileName: _jsxFileName$1,
        lineNumber: 1410,
        columnNumber: 21
      }
    }, screenReaderMessage)))
  );
};

SearchBox.defaultProps = {
  // eslint-disable-next-line no-console
  fetchResultsError: function fetchResultsError(err) {
    return console.log(err);
  },
  isCurrentLocationSearch: false,
  isUserPositionInMapBounds: false,
  onReCenter: function onReCenter() {},
  showAdornments: true,
  supportsLocationIcon: false,
  trackSearchBoxInputFocus: function trackSearchBoxInputFocus() {}
};
var SearchBoxComboBox = SearchBox;

var _this = undefined,
    _jsxFileName = "/builds/zillow/searchxp/static-search-box/src/SearchBoxModal.tsx";

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    html,\n    body {\n        overflow: hidden !important;\n    }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

var ModalGlobalStyle = createGlobalStyle(_templateObject());
var SearchBoxDialog = styled.div.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialog",
  componentId: "sxp-sb-8-100-7__xw1c73-0"
})(["&.fullscreen{position:fixed;top:0px;left:0px;z-index:", ";height:100%;width:100%;box-sizing:border-box;display:flex;justify-content:center;align-items:center;padding:0px;-webkit-overflow-scrolling:touch;}"], token('zIndices.modal'));
var SearchBoxDialogSection = styled.section.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialogSection",
  componentId: "sxp-sb-8-100-7__xw1c73-1"
})([".fullscreen &{z-index:2;max-width:100%;border-radius:0px;min-height:100%;max-height:100%;box-sizing:border-box;background-color:", ";outline:none;position:relative;display:flex;flex-direction:column;height:auto;width:100%;form{margin-left:", "px;margin-right:", "px;}}"], token('colors.backgroundWhite'), token('spacing.xl'), token('spacing.md'));
var SearchBoxDialogBody = styled.div.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialogBody",
  componentId: "sxp-sb-8-100-7__xw1c73-2"
})([".fullscreen &{overflow-y:auto;padding:12px ", "px 0px;flex:1 1 auto;margin-top:0px;section{box-shadow:none;border-top:1px solid ", ";border-color:none;margin-top:", "px !important;border-radius:0;ul{border:none;}}}form{position:relative;z-index:2;}"], token('spacing.sm'), token('colors.borderDark'), token('spacing.xs'));
var SearchBoxDialogClose = styled.div.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialogClose",
  componentId: "sxp-sb-8-100-7__xw1c73-3"
})([".fullscreen &{position:absolute;z-index:", ";top:", "px;left:0px;}"], token('zIndices.dialog'), token('spacing.xs'));
var StyledSearchBox = styled(SearchBox$2).withConfig({
  displayName: "SearchBoxModal__StyledSearchBox",
  componentId: "sxp-sb-8-100-7__xw1c73-4"
})([".fullscreen &{position:absolute;left:0px;right:0px;}.fullscreen &&& [role='dialog']{height:100vh !important;}"]);
var StyledSearchComboBox = styled(SearchBoxComboBox).withConfig({
  displayName: "SearchBoxModal__StyledSearchComboBox",
  componentId: "sxp-sb-8-100-7__xw1c73-5"
})([".fullscreen &{top:", "px;}"], token('spacing.xs'));
var StyledIconButton = styled(IconButton).withConfig({
  displayName: "SearchBoxModal__StyledIconButton",
  componentId: "sxp-sb-8-100-7__xw1c73-6"
})(["display:flex;"]);

var SearchBoxModal = function SearchBoxModal(_ref) {
  var children = _ref.children,
      fullScreen = _ref.fullScreen,
      onClose = _ref.onClose;
  return (
    /*#__PURE__*/
    React.createElement(SearchBoxDialog, {
      className: fullScreen ? 'fullscreen' : '',
      "aria-label": "Search box modal",
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 130,
        columnNumber: 5
      }
    },
    /*#__PURE__*/
    React.createElement(SearchBoxDialogSection, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 131,
        columnNumber: 9
      }
    },
    /*#__PURE__*/
    React.createElement(SearchBoxDialogBody, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 132,
        columnNumber: 13
      }
    }, children), fullScreen &&
    /*#__PURE__*/
    React.createElement(SearchBoxDialogClose, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 134,
        columnNumber: 17
      }
    },
    /*#__PURE__*/
    React.createElement(StyledIconButton, {
      "aria-label": "Close modal",
      title: "Close modal",
      icon:
      /*#__PURE__*/
      React.createElement(IconChevronLeft, {
        __self: _this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 138,
          columnNumber: 31
        }
      }),
      onClick: onClose,
      size: BUTTON_SIZES.md,
      buttonType: "secondary",
      type: "button",
      bare: true,
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 135,
        columnNumber: 21
      }
    }))), fullScreen &&
    /*#__PURE__*/
    React.createElement(ModalGlobalStyle, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 148,
        columnNumber: 24
      }
    }))
  );
};

var SearchBoxModalContainer = function SearchBoxModalContainer(props) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFullScreen = _useState2[0],
      setIsFullScreen = _useState2[1];

  var modalDidUpdate = props.modalDidUpdate;
  var focusTimeoutRef = useRef();
  useEffect(function () {
    return clearTimeout(focusTimeoutRef.current);
  }, []);
  var delegateOpen = useCallback(function () {
    setIsFullScreen(true);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(true);
  }, [modalDidUpdate]);
  var delegateClose = useCallback(function (comboboxRef) {
    var _comboboxRef$current, _comboboxRef$current$;

    setIsFullScreen(false);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(false);
    (_comboboxRef$current = comboboxRef.current) === null || _comboboxRef$current === void 0 ? void 0 : (_comboboxRef$current$ = _comboboxRef$current.querySelector('input')) === null || _comboboxRef$current$ === void 0 ? void 0 : _comboboxRef$current$.blur();
  }, [modalDidUpdate]);
  var delegateTagClick = useCallback(function (comboboxRef) {
    setIsFullScreen(true);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(true); // input is not displayed on smaller devices when there's more than one tag.
    // So, trigger focus on next event cycle after input is displayed

    focusTimeoutRef.current = setTimeout(function () {
      var _comboboxRef$current2, _comboboxRef$current3;

      return (_comboboxRef$current2 = comboboxRef.current) === null || _comboboxRef$current2 === void 0 ? void 0 : (_comboboxRef$current3 = _comboboxRef$current2.querySelector('input')) === null || _comboboxRef$current3 === void 0 ? void 0 : _comboboxRef$current3.focus();
    });
  }, [modalDidUpdate]);
  var onCloseSync = useCallback(function () {
    setIsFullScreen(false);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(false);
  }, [modalDidUpdate]);
  var onClose = useYieldCallback(onCloseSync); // Close modal on unmount

  useEffect(function () {
    return onCloseSync;
  }, [onCloseSync]);
  var searchBoxComboboxProps = Object.assign({}, props, {
    searchBoxDidFocusInput: delegateOpen,
    searchBoxDidClickTag: delegateTagClick,
    searchBoxDidSelectOption: delegateClose,
    searchBoxOnPopState: delegateClose
  });
  var searchBoxProps = Object.assign({}, props, {
    searchBoxDidFocusInput: delegateOpen,
    searchBoxDidSelectOption: delegateClose,
    searchBoxOnPopState: delegateClose
  });
  return (
    /*#__PURE__*/
    React.createElement(SearchBoxModal, {
      fullScreen: isFullScreen,
      onClose: onClose,
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 224,
        columnNumber: 9
      }
    }, props.enableNaturalLanguageSearch ?
    /*#__PURE__*/
    React.createElement(StyledSearchBox, _extends({}, searchBoxProps, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 226,
        columnNumber: 17
      }
    })) :
    /*#__PURE__*/
    React.createElement(StyledSearchComboBox, _extends({}, searchBoxComboboxProps, {
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 228,
        columnNumber: 17
      }
    })))
  );
};

var SearchBoxModalContainer$1 = SearchBoxModalContainer;

export { SRPNavigationSubmitHandler, SearchBox$2 as SearchBox, SearchBoxComboBox as SearchBoxCombobox, SearchBoxModalContainer$1 as SearchBoxModal, buildURL };
